"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  forwardRef,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { InventoryHealthDock } from "@/components/dashboard/DashboardInsightHub";
import { DashStatAnimatedValue } from "@/components/dashboard/DashStatAnimatedValue";
import { BatchScanView } from "@/components/BatchScanView";
import { ConversionDebugPanel } from "@/components/ConversionDebugPanel";
import { DeskBackButton } from "@/components/DeskBackButton";
import { DashboardHowToUse } from "@/components/DashboardHowToUse";
import { DeskLaunchSequence, DESK_LAUNCH_SESSION_KEY } from "@/components/desk/DeskLaunchSequence";
import { ExpiryDeskWordmark } from "@/components/ExpiryDeskWordmark";
import { HourglassLoader } from "@/components/HourglassLoader";
import { PharmacyQueueBackground } from "@/components/PharmacyQueueBackground";
import { PharmacyWarehouseBackdrop } from "@/components/PharmacyWarehouseBackdrop";
import { DASHBOARD_TOUR_STEPS, DashboardTourOverlay } from "@/components/DashboardTourOverlay";
import { AuthBrandHeader } from "@/components/AuthBrandHeader";
import { AuthProviderButtons } from "@/components/AuthProviderButtons";
import { PasswordVisibilityField } from "@/components/PasswordVisibilityField";
import AppErrorBoundary from "@/components/errors/AppErrorBoundary";
import { fetchWithRetry } from "@/lib/anthropicRetry";
import { compressImageBase64ForApi } from "@/lib/compressImageForApi";
import { humanizeNetworkOrUnknown, humanizeSupabaseSaveError } from "@/lib/humanizeClientError";
import { trackOAuthTermsRecorded } from "@/lib/analytics";
import { insertAuditLog, logFieldOverride } from "@/lib/auditLog";
import { useDashboardPreferences } from "@/hooks/dashboard/useDashboardPreferences";
import { useEmailSubscriptions } from "@/hooks/dashboard/useEmailSubscriptions";
import { medicationsToPdfBytes } from "@/lib/exportMedications";
import { getPricingVariantFromDocumentCookie } from "@/lib/landingConversion";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { getAuthRedirectTo, getPasswordResetRedirectTo } from "@/lib/authRedirect";
import { signupPasswordChecks, validatePasswordRules } from "@/lib/authValidators";
import { sanitizeMedStatus, sanitizeMedTextField } from "@/lib/sanitizeMedFields";
import { clearSupabaseAuthStorage, supabase } from "@/lib/supabaseClient";
import {
  batchScanRecoveryHasResumableDraft,
  clearBatchScanRecovery,
  loadBatchScanRecovery,
} from "@/lib/batchScanRecovery";
import {
  POS_REFERENCE_EVENT,
  filterPosReferenceSuggestions,
  loadPosReference,
  mergePosReferenceFromParsed,
} from "@/lib/posReferenceIndex";
import {
  MAX_BATCH_SCAN_PENDING_QUEUE,
  enqueueBatchScanPending,
  flushBatchScanPendingQueue,
  subscribeBatchPendingCount,
} from "@/lib/batchScanOfflineQueue";
import { withViewTransition } from "@/lib/viewTransition";
import { detectStockSheetFileKind } from "@/lib/stockSheet/detectKind";
import { formatExpiryDate, resolveExpiryDate } from "@/lib/resolveExpiryDate";
import { ThemeToggleIos } from "@/components/ThemeToggleIos";
import { applyDocumentThemePreference, readPreferredColorScheme } from "@/lib/documentTheme";
import { usePlanGate } from "@/hooks/usePlanGate";
import { normalizeRuntimePlan, PLAN_LIMITS, PLANS } from "@/lib/plans/planConfig";
import UpgradePrompt from "@/components/upgrade/UpgradePrompt";
import { useExpirySummary } from "@/features/expiry-risk/useExpirySummary";
import { parseInventoryCsv } from "@/features/inventory/bulkImport";
import { AuditLogPanel } from "@/features/audit-log/AuditLogPanel";
import { AUDIT_ACTIONS, logAuditEvent } from "@/lib/audit/auditLogger";
import {
  DashboardSearchCombobox,
  SEARCH_DEBOUNCE_MS,
  SEARCH_PLACEHOLDER,
  useDashboardSearchActions,
  useDashboardSearchEngine,
} from "@/features/dashboard-search";
import { EmptyState, EmptyStateIllustrations } from "@/components/ui/EmptyState";
import { logger } from "@/lib/monitoring/logger";
import { track } from "@/lib/analytics/track";
import { buildDeskNotificationQueue, medHasValidExpiry } from "@/lib/deskNotificationQueue";
import { DeskNotificationsPage } from "@/components/DeskNotificationsPage";

const ED = {
  font: 'var(--font-geist-sans), system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  text: "#0c4a6e",
  textSecondary: "#64748b",
  textMuted: "#94a3b8",
  glass: {
    background: "var(--ed-glass-bg)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: "1px solid var(--ed-glass-border)",
    boxShadow: "var(--ed-shadow)",
  },
  glassHover: {
    boxShadow: "var(--ed-shadow-hover)",
  },
  r16: "16px",
  r14: "14px",
  r12: "12px",
  ease: "cubic-bezier(0.4, 0, 0.2, 1)",
};

const loginInputStyle = {
  width: "100%",
  background: "rgba(255, 255, 255, 0.95)",
  border: "1px solid rgba(14, 165, 233, 0.18)",
  borderRadius: ED.r12,
  color: ED.text,
  padding: "0.85rem 1rem",
  fontFamily: ED.font,
  fontSize: "0.9375rem",
  boxSizing: "border-box",
  transition: `border-color 0.2s ${ED.ease}, box-shadow 0.2s ${ED.ease}`,
};

const DEMO_MEDS = [
  { id: 1, name: "Abilify", dosage: "5mg", quantity: 0.07, unit: "boxes", expiryMonth: 6, expiryYear: 2026, pricePerBox: 850, status: null },
  { id: 2, name: "Acnetane", dosage: "10mg", quantity: 4, unit: "boxes (60s)", expiryMonth: 5, expiryYear: 2026, pricePerBox: 1200, status: null },
  { id: 3, name: "Acnetane", dosage: "20mg", quantity: 2.3, unit: "boxes (60s)", expiryMonth: 5, expiryYear: 2026, pricePerBox: 1400, status: null },
  { id: 4, name: "Adco-Atenolol", dosage: "50mg", quantity: 2, unit: "boxes (30s)", expiryMonth: 6, expiryYear: 2026, pricePerBox: 95, status: null },
  { id: 5, name: "Augmentin BB", dosage: "", quantity: 5.5, unit: "boxes", expiryMonth: 6, expiryYear: 2026, pricePerBox: 320, status: null },
  { id: 6, name: "Nuzak", dosage: "20mg", quantity: 30, unit: "boxes", expiryMonth: 2, expiryYear: 2026, pricePerBox: 180, status: null },
  { id: 7, name: "Nuzak", dosage: "20mg", quantity: 3, unit: "boxes", expiryMonth: 2, expiryYear: 2026, pricePerBox: 180, status: null },
  { id: 8, name: "Budep XR", dosage: "150", quantity: 3, unit: "boxes", expiryMonth: 6, expiryYear: 2026, pricePerBox: 420, status: null },
  { id: 9, name: "Carbrexa", dosage: "1mg", quantity: 1, unit: "box", expiryMonth: 5, expiryYear: 2026, pricePerBox: 210, status: null },
  { id: 10, name: "Ceroxim", dosage: "500mg", quantity: 0.5, unit: "boxes", expiryMonth: 4, expiryYear: 2026, pricePerBox: 290, status: "item_expired" },
];

function mergeDuplicates(meds) {
  const map = new Map();
  for (const med of meds) {
    const key = `${String(med.name || "").toLowerCase().trim()}|${(med.dosage || "").toLowerCase().trim()}|${med.expiryMonth}|${med.expiryYear}`;
    if (map.has(key)) {
      const existing = map.get(key);
      const incA = existing.incompleteCoreFields || [];
      const incB = med.incompleteCoreFields || [];
      const mergedInc = [...new Set([...incA, ...incB])];
      map.set(key, {
        ...existing,
        quantity: Math.round((existing.quantity + med.quantity) * 100) / 100,
        incompleteCoreFields: mergedInc.length ? mergedInc : [],
      });
    } else {
      map.set(key, { ...med });
    }
  }
  return Array.from(map.values());
}

function getDaysUntilExpiry(month, year) {
  const today = new Date();
  const expiry = resolveExpiryDate(month, year, "last");
  return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
}

function getDaysUntilSellBy(month, year) {
  const today = new Date();
  const m = Number(month);
  const y = Number(year);
  if (!Number.isFinite(m) || !Number.isFinite(y)) return Number.NaN;
  // Sell-by is the final day of the month before month-end expiry.
  const sellBy = new Date(y, m - 1, 0);
  return Math.ceil((sellBy - today) / (1000 * 60 * 60 * 24));
}

/** Core ingest rule: name, expiry, quantity must be present; otherwise still save but flag on desk. */
function computeIncompleteCoreFields(med) {
  const missing = [];
  if (!String(med?.name ?? "").trim()) missing.push("name");
  if (!medHasValidExpiry(med)) missing.push("expiry");
  const q = Number(med?.quantity);
  if (!Number.isFinite(q) || q <= 0) missing.push("quantity");
  return missing;
}

function incompleteCoreFieldLabels(fields) {
  if (!fields || fields.length === 0) return "";
  const map = { name: "Name", expiry: "Expiry date", quantity: "Quantity" };
  return fields.map((f) => map[f] || f).join(", ");
}

const CRITICAL_WINDOW_DAYS = 120;
const WARNING_WINDOW_DAYS = 150;
const MONITOR_WINDOW_DAYS = 180;

function getUrgencyLevel(days, status) {
  if (status === "item_expired") return "expired";
  if (status === "item_moved") return "moved";
  if (days < 0) return "overdue";
  if (days <= CRITICAL_WINDOW_DAYS) return "critical";
  if (days <= WARNING_WINDOW_DAYS) return "warning";
  if (days <= MONITOR_WINDOW_DAYS) return "monitor";
  return "safe";
}

/** Active lines only — bucket for dashboard category chips (Fridge / Schedule / OTC). */
function urgencyTierForActiveLine(days) {
  if (days < 0) return "overdue";
  if (days <= CRITICAL_WINDOW_DAYS) return "critical";
  if (days <= WARNING_WINDOW_DAYS) return "warning";
  if (days <= MONITOR_WINDOW_DAYS) return "monitor";
  return "safe";
}

const PRODUCT_CATEGORY_IDS = ["fridge", "schedule6", "schedule35", "otc"];

function normalizeProductCategory(raw) {
  const s = typeof raw === "string" ? raw.trim() : "";
  if (PRODUCT_CATEGORY_IDS.includes(s)) return s;
  return null;
}

/** iOS-style status pills — light, clinical; cardFrame / accentLeft = bolder dashboard chrome */
const URGENCY_CONFIG = {
  overdue: {
    label: "CRITICAL",
    cardTint: "rgba(254, 242, 242, 0.6)",
    border: "rgba(239, 68, 68, 0.25)",
    cardFrame: "rgba(220, 38, 38, 0.62)",
    accentLeft: "#dc2626",
    shadowTint: "rgba(220, 38, 38, 0.22)",
    pillBg: "#fee2e2",
    pillText: "#b91c1c",
    pillBorder: "rgba(239, 68, 68, 0.35)",
  },
  critical: {
    label: "URGENT",
    cardTint: "rgba(255, 237, 213, 0.55)",
    border: "rgba(249, 115, 22, 0.22)",
    cardFrame: "rgba(234, 88, 12, 0.58)",
    accentLeft: "#ea580c",
    shadowTint: "rgba(234, 88, 12, 0.2)",
    pillBg: "#ffedd5",
    pillText: "#c2410c",
    pillBorder: "rgba(249, 115, 22, 0.35)",
  },
  warning: {
    label: "PRIORITY",
    cardTint: "rgba(254, 252, 232, 0.7)",
    border: "rgba(234, 179, 8, 0.22)",
    cardFrame: "rgba(202, 138, 4, 0.55)",
    accentLeft: "#ca8a04",
    shadowTint: "rgba(202, 138, 4, 0.18)",
    pillBg: "#fef9c3",
    pillText: "#a16207",
    pillBorder: "rgba(234, 179, 8, 0.4)",
  },
  monitor: {
    label: "MONITOR",
    cardTint: "rgba(239, 246, 255, 0.72)",
    border: "rgba(59, 130, 246, 0.2)",
    cardFrame: "rgba(37, 99, 235, 0.46)",
    accentLeft: "#2563eb",
    shadowTint: "rgba(37, 99, 235, 0.14)",
    pillBg: "#dbeafe",
    pillText: "#1e40af",
    pillBorder: "rgba(59, 130, 246, 0.34)",
  },
  safe: {
    label: "SAFE",
    cardTint: "rgba(236, 253, 245, 0.65)",
    border: "rgba(16, 185, 129, 0.2)",
    cardFrame: "rgba(5, 150, 105, 0.52)",
    accentLeft: "#059669",
    shadowTint: "rgba(16, 185, 129, 0.16)",
    pillBg: "#d1fae5",
    pillText: "#047857",
    pillBorder: "rgba(16, 185, 129, 0.35)",
  },
  expired: {
    label: "EXPIRED",
    cardTint: "rgba(241, 245, 249, 0.85)",
    border: "rgba(100, 116, 139, 0.2)",
    cardFrame: "rgba(71, 85, 105, 0.48)",
    accentLeft: "#64748b",
    shadowTint: "rgba(71, 85, 105, 0.14)",
    pillBg: "#e2e8f0",
    pillText: "#475569",
    pillBorder: "rgba(100, 116, 139, 0.3)",
  },
  moved: {
    label: "MOVED",
    cardTint: "rgba(239, 246, 255, 0.75)",
    border: "rgba(59, 130, 246, 0.22)",
    cardFrame: "rgba(37, 99, 235, 0.52)",
    accentLeft: "#2563eb",
    shadowTint: "rgba(37, 99, 235, 0.18)",
    pillBg: "#dbeafe",
    pillText: "#1d4ed8",
    pillBorder: "rgba(59, 130, 246, 0.35)",
  },
};

function formatCurrency(val) {
  return "R " + Number(val).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function calcWaste(meds) { return meds.filter(m => m.status === "item_expired").reduce((s, m) => s + m.quantity * (m.pricePerBox || 0), 0); }
function calcAtRisk(meds) { return meds.filter(m => !m.status && getDaysUntilSellBy(m.expiryMonth, m.expiryYear) <= CRITICAL_WINDOW_DAYS).reduce((s, m) => s + m.quantity * (m.pricePerBox || 0), 0); }
function calcSaved(meds) { return meds.filter(m => m.status === "item_moved").reduce((s, m) => s + m.quantity * (m.pricePerBox || 0), 0); }

function sumLineQuantities(items) {
  return items.reduce((s, m) => s + (Number(m.quantity) || 0), 0);
}

/** Readable qty for dashboard copy (avoids float noise). */
function formatQtyShort(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0";
  const r = Math.round(x * 100) / 100;
  return Number.isInteger(r) ? String(r) : r.toFixed(2).replace(/\.?0+$/, "");
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/** Dashboard + modal unit picker — keep in sync with history display. */
const MED_UNIT_OPTIONS = [
  { value: "boxes", label: "Boxes" },
  { value: "tablets", label: "Tablets" },
  { value: "ml", label: "mL" },
];

function normalizeMedUnit(u) {
  const s = String(u ?? "").toLowerCase();
  if (s.includes("tablet")) return "tablets";
  if (s.includes("ml") || /\bm\s*l\b/.test(s)) return "ml";
  if (s.includes("box")) return "boxes";
  return "boxes";
}

/** Avoid failed updates when id is UUID string in DB but number in client (or vice versa). */
function sameMedId(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function isMissingProductCategoryColumnError(error) {
  const msg = String(error?.message || "");
  return /product_category/i.test(msg) && /schema cache|could not find/i.test(msg);
}

function isMissingIncompleteCoreFieldsColumnError(error) {
  const msg = String(error?.message || "");
  return /incomplete_core_fields/i.test(msg) && /schema cache|could not find/i.test(msg);
}

/** Strips columns PostgREST's schema cache may lack; returns `row` unchanged when nothing matches. */
function stripMedicationDbRowForKnownSchemaCacheError(row, error) {
  if (!row || !error) return row;
  if (isMissingIncompleteCoreFieldsColumnError(error)) {
    const { incomplete_core_fields: _i, ...rest } = row;
    return rest;
  }
  if (isMissingProductCategoryColumnError(error)) {
    const { product_category: _p, ...rest } = row;
    return rest;
  }
  return row;
}

async function updateMedicationPatchWithSchemaFallback(supabase, rowId, userId, patch) {
  let working = { ...patch };
  for (let i = 0; i < 6; i += 1) {
    const result = await supabase.from("medications").update(working).eq("id", rowId).eq("user_id", userId).select("*").single();
    if (!result.error) return result;
    const next = stripMedicationDbRowForKnownSchemaCacheError(working, result.error);
    if (Object.keys(next).length === Object.keys(working).length) return result;
    working = next;
  }
  return supabase.from("medications").update(working).eq("id", rowId).eq("user_id", userId).select("*").single();
}

async function insertMedicationRowsWithSchemaFallback(supabase, rows) {
  let working = rows.map((r) => ({ ...r }));
  for (let i = 0; i < 6; i += 1) {
    const result = await supabase.from("medications").insert(working).select("*");
    if (!result.error) return result;
    const sample = working[0];
    if (!sample) return result;
    const strippedSample = stripMedicationDbRowForKnownSchemaCacheError(sample, result.error);
    if (Object.keys(strippedSample).length === Object.keys(sample).length) return result;
    working = working.map((r) => stripMedicationDbRowForKnownSchemaCacheError(r, result.error));
  }
  return supabase.from("medications").insert(working).select("*");
}

const MICROCOPY_RECENT_WINDOW_MS = 12000;
const microcopyRecentByType = new Map();

/** Stable index for hover hints — same card + type always gets the same phrase (no flicker on re-hover). */
function microcopyHashSeed(seed) {
  const s = String(seed);
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function pickStableMicrocopy(type, options, seed) {
  if (!Array.isArray(options) || options.length === 0) return "";
  if (options.length === 1) return options[0];
  const idx = microcopyHashSeed(`${type}:${seed}`) % options.length;
  return options[idx];
}

function pickMicrocopy(type, options, previous = null) {
  if (!Array.isArray(options) || options.length === 0) return "";
  if (options.length === 1) return options[0];
  const now = Date.now();
  const recent = microcopyRecentByType.get(type);
  const recentPhrase = recent && now - recent.ts < MICROCOPY_RECENT_WINDOW_MS ? recent.phrase : null;
  const filtered = options.filter((opt) => opt && opt !== previous && opt !== recentPhrase);
  const pool = filtered.length > 0 ? filtered : options.filter((opt) => opt && opt !== previous);
  const finalPool = pool.length > 0 ? pool : options;
  const phrase = finalPool[Math.floor(Math.random() * finalPool.length)];
  microcopyRecentByType.set(type, { phrase, ts: now });
  return phrase;
}

function sanitizeMedForSave(med) {
  const sn = sanitizeMedTextField(med.supplierName ?? "");
  return {
    ...med,
    name: sanitizeMedTextField(med.name),
    dosage: sanitizeMedTextField(med.dosage ?? ""),
    unit: sanitizeMedTextField(med.unit ?? ""),
    status: sanitizeMedStatus(med.status),
    productCategory: normalizeProductCategory(med.productCategory),
    supplierName: sn ? sn.slice(0, 120) : "",
  };
}

/** Empty string → null; preserves 0. */
function parsePriceFromForm(raw) {
  const v = String(raw ?? "").trim().replace(",", ".");
  if (v === "") return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function medToModalForm(med, currentCalendarYear) {
  const m = med ?? {};
  const initialLineStatus =
    m.status === "item_moved" || m.status === "item_expired" ? m.status : "";
  const pp = m.pricePerBox;
  const priceStr =
    pp != null && pp !== "" && Number.isFinite(Number(pp)) ? String(pp) : "";
  return {
    name: m.name || "",
    dosage: m.dosage || "",
    quantity: m.quantity ?? "",
    unit: normalizeMedUnit(m.unit),
    expiryMonth: Number.isFinite(Number(m.expiryMonth)) && Number(m.expiryMonth) >= 1 && Number(m.expiryMonth) <= 12
      ? Number(m.expiryMonth)
      : new Date().getMonth() + 1,
    expiryYear: Number.isFinite(Number(m.expiryYear)) ? Number(m.expiryYear) : currentCalendarYear,
    pricePerBox: priceStr,
    lineStatus: initialLineStatus,
    productCategory: normalizeProductCategory(m.productCategory) || "",
    supplierName: String(m.supplierName || "").trim().slice(0, 120),
  };
}

function mapMedicationRow(r) {
  const em = Number(r.expiry_month);
  const ey = Number(r.expiry_year);
  const y = new Date().getFullYear();
  return {
    id: r.id,
    name: r.name,
    dosage: r.dosage,
    quantity: Number(r.quantity),
    unit: r.unit,
    expiryMonth: Number.isFinite(em) && em >= 1 && em <= 12 ? em : new Date().getMonth() + 1,
    expiryYear: Number.isFinite(ey) ? ey : y,
    pricePerBox: r.price_per_box === null ? null : Number(r.price_per_box),
    status: r.status,
    originalName: null,
    suggestedName: null,
    matchScore: null,
    matchConfidence: null,
    matchStatus: "not_checked",
    scanDate: r.scan_date == null ? null : String(r.scan_date),
    deletedAt: r.deleted_at == null ? null : String(r.deleted_at),
    productCategory: normalizeProductCategory(r.product_category),
    expiryDayConvention: r.expiry_day_convention === "first" ? "first" : "last",
    aiConfidenceScore: r.ai_confidence_score == null ? null : Number(r.ai_confidence_score),
    aiRawPayload: r.ai_raw_payload ?? null,
    imageFingerprint: r.image_fingerprint ?? null,
    supplierName: r.supplier_name != null && String(r.supplier_name).trim() ? String(r.supplier_name).trim().slice(0, 120) : "",
    incompleteCoreFields: Array.isArray(r.incomplete_core_fields) ? r.incomplete_core_fields.filter(Boolean) : [],
  };
}

function MedModal({ med: rawMed, onSave, onClose, userId = null }) {
  const med = rawMed ?? {};
  const isNew = !med.id;
  const suggestListId = useId().replace(/:/g, "");
  const currentCalendarYear = new Date().getFullYear();
  const [form, setForm] = useState(() => medToModalForm(med, currentCalendarYear));
  const [posReferenceEntries, setPosReferenceEntries] = useState([]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    const next = medToModalForm(med, new Date().getFullYear());
    queueMicrotask(() => {
      setForm(next);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable med fields; object identity churns too often
  }, [
    med.id,
    med.name,
    med.dosage,
    med.quantity,
    med.unit,
    med.expiryMonth,
    med.expiryYear,
    med.pricePerBox,
    med.status,
    med.productCategory,
    med.supplierName,
  ]);

  useEffect(() => {
    if (!userId) {
      setPosReferenceEntries([]);
      return;
    }
    let cancelled = false;
    void loadPosReference(userId).then((rows) => {
      if (!cancelled) setPosReferenceEntries(rows);
    });
    const onRef = () => {
      void loadPosReference(userId).then((rows) => {
        if (!cancelled) setPosReferenceEntries(rows);
      });
    };
    window.addEventListener(POS_REFERENCE_EVENT, onRef);
    return () => {
      cancelled = true;
      window.removeEventListener(POS_REFERENCE_EVENT, onRef);
    };
  }, [userId]);

  const posNameSuggestions = useMemo(
    () => filterPosReferenceSuggestions(posReferenceEntries, form.name, 12),
    [posReferenceEntries, form.name],
  );

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  const yearOptions = useMemo(() => {
    const y = Number(form.expiryYear);
    const anchor = Number.isFinite(y) ? y : currentCalendarYear;
    const start = Math.min(currentCalendarYear - 8, anchor - 4);
    const end = Math.max(currentCalendarYear + 15, anchor + 8);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [form.expiryYear, currentCalendarYear]);

  function handleSave() {
    if (!form.name.trim()) return;
    const expMo = parseInt(String(form.expiryMonth), 10);
    const expYr = parseInt(String(form.expiryYear), 10);
    onSave({
      ...med,
      id: med.id || Date.now(),
      name: form.name.trim(),
      dosage: form.dosage.trim(),
      quantity: parseFloat(String(form.quantity)) || 0,
      unit: MED_UNIT_OPTIONS.some((o) => o.value === form.unit) ? form.unit : "boxes",
      expiryMonth: Number.isFinite(expMo) ? Math.min(12, Math.max(1, expMo)) : new Date().getMonth() + 1,
      expiryYear: Number.isFinite(expYr) ? expYr : currentCalendarYear,
      pricePerBox: parsePriceFromForm(form.pricePerBox),
      status: form.lineStatus === "" ? null : form.lineStatus,
      productCategory: normalizeProductCategory(form.productCategory),
      supplierName: String(form.supplierName || "").trim().slice(0, 120),
    });
    onClose();
  }

  const inputStyle = {
    width: "100%",
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(14, 165, 233, 0.15)",
    borderRadius: ED.r12,
    color: ED.text,
    padding: "0.65rem 0.85rem",
    fontFamily: ED.font,
    fontSize: "0.875rem",
    boxSizing: "border-box",
  };
  const labelStyle = {
    display: "block",
    color: ED.textMuted,
    fontSize: "0.6875rem",
    fontFamily: ED.font,
    fontWeight: 600,
    letterSpacing: "0.04em",
    marginBottom: "0.35rem",
    textTransform: "uppercase",
  };
  return (
    <div
      className="ed-modal-backdrop ed-med-modal-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(12, 74, 110, 0.25)",
        backdropFilter: "blur(4px)",
        display: "flex",
        justifyContent: "center",
        zIndex: 10040,
        padding: "1rem",
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
      data-haptic
    >
      <div
        className="ed-card-glass ed-med-modal-panel"
        style={{
          width: "100%",
          maxWidth: "440px",
          maxHeight: "min(calc(100dvh - var(--ed-med-modal-chrome-gap) - 1.25rem), 90vh)",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          animation: "ed-fade-in 0.25s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <div>
            <div style={{ fontFamily: ED.font, fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.06em", color: ED.textMuted, marginBottom: "0.25rem" }}>
              {isNew ? "New line" : "Edit line"}
            </div>
            <div style={{ fontFamily: ED.font, fontSize: "1.25rem", fontWeight: 600, color: ED.text, letterSpacing: "-0.02em" }}>
              {isNew ? "Add a product line" : form.name || "Edit"}
            </div>
          </div>
          <button type="button" onClick={onClose} style={{
            background: "rgba(248, 250, 252, 0.9)", border: "1px solid rgba(148, 163, 184, 0.25)", borderRadius: ED.r12,
            color: ED.textSecondary, cursor: "pointer", padding: "0.45rem 0.65rem", fontSize: "1.1rem", lineHeight: 1,
            transition: `background 0.2s ${ED.ease}`,
          }}>×</button>
        </div>

        <div className="ed-med-modal-form-grid">
          <div style={{ gridColumn: "1 / -1", position: "relative" }}>
            <label style={labelStyle}>Medicine Name *</label>
            <input
              style={inputStyle}
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder={
                posReferenceEntries.length
                  ? "Type to match POS / sheet list — e.g. Nuzak"
                  : "e.g. Nuzak"
              }
              autoComplete="off"
              aria-autocomplete="list"
              aria-controls={suggestListId}
              autoFocus
            />
            {posNameSuggestions.length > 0 ? (
              <ul
                id={suggestListId}
                role="listbox"
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: "100%",
                  margin: "4px 0 0",
                  padding: "4px 0",
                  maxHeight: "min(36vh, 200px)",
                  overflowY: "auto",
                  listStyle: "none",
                  zIndex: 10,
                  borderRadius: ED.r12,
                  background: "rgba(255,255,255,0.98)",
                  border: "1px solid rgba(14, 165, 233, 0.22)",
                  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.12)",
                }}
              >
                {posNameSuggestions.map((s) => (
                  <li key={`${s.name}-${s.dosage}-${s.unit}`} role="option">
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setForm((f) => ({
                          ...f,
                          name: s.name,
                          dosage: s.dosage || f.dosage,
                          unit: normalizeMedUnit(s.unit || f.unit),
                        }));
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "0.55rem 0.85rem",
                        border: "none",
                        background: "transparent",
                        color: ED.text,
                        fontSize: "0.875rem",
                        cursor: "pointer",
                        fontFamily: ED.font,
                      }}
                    >
                      <span style={{ fontWeight: 650 }}>{s.name}</span>
                      {s.dosage ? (
                        <span style={{ color: ED.textSecondary, fontWeight: 500 }}> · {s.dosage}</span>
                      ) : null}
                      {s.qty != null ? (
                        <span
                          style={{
                            display: "block",
                            fontSize: "0.72rem",
                            color: ED.textMuted,
                            marginTop: 2,
                          }}
                        >
                          Stock sheet: {s.qty} {s.unit || ""}
                        </span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <div>
            <label style={labelStyle}>Strength</label>
            <input style={inputStyle} value={form.dosage} onChange={e => set("dosage", e.target.value)} placeholder="e.g. 20 mg" />
          </div>
          <div>
            <label style={labelStyle}>Unit</label>
            <select
              style={{ ...inputStyle, cursor: "pointer" }}
              value={form.unit}
              onChange={(e) => set("unit", e.target.value)}
            >
              {MED_UNIT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Quantity</label>
            <input style={inputStyle} type="number" min="0" step="0.01" value={form.quantity} onChange={e => set("quantity", e.target.value)} placeholder="e.g. 5" />
          </div>
          <div>
            <label style={labelStyle}>Price per Box (R)</label>
            <input style={inputStyle} type="number" min="0" step="0.01" value={form.pricePerBox} onChange={e => set("pricePerBox", e.target.value)} placeholder="e.g. 250" />
          </div>
          <div>
            <label style={labelStyle}>Expiry Month</label>
            <select
              style={{ ...inputStyle, cursor: "pointer" }}
              value={form.expiryMonth}
              onChange={e => set("expiryMonth", parseInt(e.target.value, 10))}
            >
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Expiry Year</label>
            <select
              style={{ ...inputStyle, cursor: "pointer" }}
              value={form.expiryYear}
              onChange={e => set("expiryYear", parseInt(e.target.value, 10))}
            >
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1", marginTop: "-0.15rem" }}>
            {String(med.expiryDayConvention || "last") === "last" ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  borderRadius: 999,
                  padding: "0.24rem 0.58rem",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  background: "rgba(34, 197, 94, 0.14)",
                  border: "1px solid rgba(34, 197, 94, 0.35)",
                  color: "#166534",
                }}
              >
                End of month expiry (standard)
              </span>
            ) : (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  borderRadius: 999,
                  padding: "0.24rem 0.58rem",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  background: "rgba(245, 158, 11, 0.14)",
                  border: "1px solid rgba(245, 158, 11, 0.35)",
                  color: "#92400e",
                }}
              >
                First day of month expiry
              </span>
            )}
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Shelf / schedule group</label>
            <select
              style={{ ...inputStyle, cursor: "pointer" }}
              value={form.productCategory || ""}
              onChange={(e) => set("productCategory", e.target.value)}
            >
              <option value="">Not set — pick when you know the line type</option>
              <option value="fridge">Fridge line (cold chain)</option>
              <option value="schedule6">Schedule 6 (controlled)</option>
              <option value="schedule35">Schedule 3–5 (Rx / dispensary)</option>
              <option value="otc">OTC 1–2</option>
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Supplier (optional)</label>
            <input
              style={inputStyle}
              value={form.supplierName || ""}
              onChange={(e) => set("supplierName", e.target.value)}
              placeholder="e.g. Wholesaler name — powers supplier insights"
              maxLength={120}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Stock status</label>
            <select
              style={{ ...inputStyle, cursor: "pointer" }}
              value={form.lineStatus}
              onChange={e => set("lineStatus", e.target.value)}
            >
              <option value="">Active · tracking for expiry</option>
              <option value="item_moved">Moved / dispensed</option>
              <option value="item_expired">Marked expired (write-off)</option>
            </select>
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem" }}>
          <button type="button" onClick={onClose} className="ed-btn-ghost" style={{
            flex: 1, padding: "0.75rem", fontFamily: ED.font,
          }}>Cancel</button>
          <button type="button" onClick={handleSave} className="ed-btn-primary" style={{
            flex: 2, padding: "0.75rem", fontFamily: ED.font,
          }}>{isNew ? "Add" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function WriteOffsDetailModal({ items, totalWaste, onClose, onEditLine, viewMode = "finance" }) {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      if (viewMode === "stock") {
        const dq = (Number(b.quantity) || 0) - (Number(a.quantity) || 0);
        if (dq !== 0) return dq;
        return String(a.name).localeCompare(String(b.name));
      }
      const va = a.quantity * (a.pricePerBox || 0);
      const vb = b.quantity * (b.pricePerBox || 0);
      if (vb !== va) return vb - va;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [items, viewMode]);

  const totalQty = useMemo(() => sumLineQuantities(items), [items]);

  return (
    <div
      className="ed-modal-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(12, 74, 110, 0.28)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1001,
        padding: "1rem",
        fontFamily: ED.font,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-haptic
    >
      <div
        className="ed-card-glass ed-detail-modal-panel"
        style={{
          width: "100%",
          maxWidth: "520px",
          maxHeight: "min(85vh, 640px)",
          display: "flex",
          flexDirection: "column",
          borderRadius: ED.r16,
          animation: "ed-fade-in 0.25s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem", marginBottom: "1rem" }}>
          <div>
            <div style={{ fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.08em", color: ED.textMuted, marginBottom: "0.35rem", textTransform: "uppercase" }}>
              Write-offs
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#e11d48", letterSpacing: "-0.02em" }}>
              {viewMode === "stock"
                ? `${formatQtyShort(totalQty)} qty`
                : formatCurrency(totalWaste)}
            </div>
            <div style={{ fontSize: "0.8125rem", color: ED.textSecondary, marginTop: "0.35rem", lineHeight: 1.4 }}>
              {sorted.length} line{sorted.length === 1 ? "" : "s"} marked expired
              {viewMode === "stock" && sorted.length > 0 ? ` · ${formatQtyShort(totalQty)} units total` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(248, 250, 252, 0.9)",
              border: "1px solid rgba(148, 163, 184, 0.25)",
              borderRadius: ED.r12,
              color: ED.textSecondary,
              cursor: "pointer",
              padding: "0.45rem 0.65rem",
              fontSize: "1.1rem",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1, minHeight: 0, borderTop: "1px solid rgba(14, 165, 233, 0.12)", margin: "0 -0.25rem", padding: "0.75rem 0.25rem 0" }}>
          {sorted.length === 0 ? (
            <p style={{ margin: "0.5rem 0", fontSize: "0.875rem", color: ED.textSecondary, lineHeight: 1.5 }}>
              Nothing marked expired yet. Use <strong style={{ color: ED.text }}>Marked expired</strong> on a card or set status in Edit line.
            </p>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {sorted.map((m) => {
                const lineVal = m.quantity * (m.pricePerBox || 0);
                const hasPrice = m.pricePerBox != null && Number.isFinite(Number(m.pricePerBox));
                return (
                  <li key={String(m.id)}>
                    <button
                      type="button"
                      onClick={() => {
                        onEditLine(m);
                        onClose();
                      }}
                      className="ed-card-glass"
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "0.75rem 0.85rem",
                        borderRadius: ED.r12,
                        border: "1px solid rgba(148, 163, 184, 0.22)",
                        background: "rgba(255,255,255,0.72)",
                        cursor: "pointer",
                        fontFamily: ED.font,
                        display: "grid",
                        gap: "0.25rem",
                      }}
                    >
                      <div style={{ fontWeight: 600, color: ED.text, fontSize: "0.9rem" }}>
                        {m.name}
                        {m.dosage ? <span style={{ fontWeight: 500, color: ED.textSecondary }}> · {m.dosage}</span> : null}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: ED.textSecondary }}>
                        {m.quantity} {m.unit}
                        <span style={{ color: ED.textMuted, margin: "0 0.35rem" }}>·</span>
                        Exp {formatExpiryDate(m.expiryMonth, m.expiryYear, m.expiryDayConvention || "last")}
                      </div>
                      <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: hasPrice ? "#b91c1c" : ED.textMuted }}>
                        {viewMode === "stock" ? (
                          <span style={{ color: ED.text }}>{m.quantity} {m.unit} on hand</span>
                        ) : hasPrice ? (
                          formatCurrency(lineVal) + " write-off"
                        ) : (
                          "No unit price — tap to edit"
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function RecoveredDetailModal({ items, totalRecovered, onClose, onEditLine, viewMode = "finance" }) {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      if (viewMode === "stock") {
        const dq = (Number(b.quantity) || 0) - (Number(a.quantity) || 0);
        if (dq !== 0) return dq;
        return String(a.name).localeCompare(String(b.name));
      }
      const va = a.quantity * (a.pricePerBox || 0);
      const vb = b.quantity * (b.pricePerBox || 0);
      if (vb !== va) return vb - va;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [items, viewMode]);

  const totalQty = useMemo(() => sumLineQuantities(items), [items]);

  return (
    <div
      className="ed-modal-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(12, 74, 110, 0.28)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1001,
        padding: "1rem",
        fontFamily: ED.font,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-haptic
    >
      <div
        className="ed-card-glass ed-detail-modal-panel"
        style={{
          width: "100%",
          maxWidth: "520px",
          maxHeight: "min(85vh, 640px)",
          display: "flex",
          flexDirection: "column",
          borderRadius: ED.r16,
          animation: "ed-fade-in 0.25s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem", marginBottom: "1rem" }}>
          <div>
            <div style={{ fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.08em", color: ED.textMuted, marginBottom: "0.35rem", textTransform: "uppercase" }}>
              Recovered
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#059669", letterSpacing: "-0.02em" }}>
              {viewMode === "stock"
                ? `${formatQtyShort(totalQty)} qty`
                : formatCurrency(totalRecovered)}
            </div>
            <div style={{ fontSize: "0.8125rem", color: ED.textSecondary, marginTop: "0.35rem", lineHeight: 1.4 }}>
              {sorted.length} line{sorted.length === 1 ? "" : "s"} moved or dispensed
              {viewMode === "stock" && sorted.length > 0 ? ` · ${formatQtyShort(totalQty)} units total` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(248, 250, 252, 0.9)",
              border: "1px solid rgba(148, 163, 184, 0.25)",
              borderRadius: ED.r12,
              color: ED.textSecondary,
              cursor: "pointer",
              padding: "0.45rem 0.65rem",
              fontSize: "1.1rem",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1, minHeight: 0, borderTop: "1px solid rgba(14, 165, 233, 0.12)", margin: "0 -0.25rem", padding: "0.75rem 0.25rem 0" }}>
          {sorted.length === 0 ? (
            <p style={{ margin: "0.5rem 0", fontSize: "0.875rem", color: ED.textSecondary, lineHeight: 1.5 }}>
              Nothing moved yet. Use <strong style={{ color: ED.text }}>Moved / dispensed</strong> on a card or set status in Edit line.
            </p>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {sorted.map((m) => {
                const lineVal = m.quantity * (m.pricePerBox || 0);
                const hasPrice = m.pricePerBox != null && Number.isFinite(Number(m.pricePerBox));
                return (
                  <li key={String(m.id)}>
                    <button
                      type="button"
                      onClick={() => {
                        onEditLine(m);
                        onClose();
                      }}
                      className="ed-card-glass"
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "0.75rem 0.85rem",
                        borderRadius: ED.r12,
                        border: "1px solid rgba(148, 163, 184, 0.22)",
                        background: "rgba(255,255,255,0.72)",
                        cursor: "pointer",
                        fontFamily: ED.font,
                        display: "grid",
                        gap: "0.25rem",
                      }}
                    >
                      <div style={{ fontWeight: 600, color: ED.text, fontSize: "0.9rem" }}>
                        {m.name}
                        {m.dosage ? <span style={{ fontWeight: 500, color: ED.textSecondary }}> · {m.dosage}</span> : null}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: ED.textSecondary }}>
                        {m.quantity} {m.unit}
                        <span style={{ color: ED.textMuted, margin: "0 0.35rem" }}>·</span>
                        Exp {formatExpiryDate(m.expiryMonth, m.expiryYear, m.expiryDayConvention || "last")}
                      </div>
                      <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: hasPrice ? "#047857" : ED.textMuted }}>
                        {viewMode === "stock" ? (
                          <span style={{ color: ED.text }}>{m.quantity} {m.unit} recorded</span>
                        ) : hasPrice ? (
                          formatCurrency(lineVal) + " recovered"
                        ) : (
                          "No unit price — tap to edit"
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function AtRiskDetailModal({ items, totalAtRiskValue, onClose, onEditLine, viewMode = "finance" }) {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      if (viewMode === "stock") {
        const dq = (Number(b.quantity) || 0) - (Number(a.quantity) || 0);
        if (dq !== 0) return dq;
        return String(a.name).localeCompare(String(b.name));
      }
      const va = a.quantity * (a.pricePerBox || 0);
      const vb = b.quantity * (b.pricePerBox || 0);
      if (vb !== va) return vb - va;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [items, viewMode]);

  const totalQty = useMemo(() => sumLineQuantities(items), [items]);

  return (
    <div
      className="ed-modal-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(12, 74, 110, 0.28)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1001,
        padding: "1rem",
        fontFamily: ED.font,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-haptic
    >
      <div
        className="ed-card-glass ed-detail-modal-panel"
        style={{
          width: "100%",
          maxWidth: "520px",
          maxHeight: "min(85vh, 640px)",
          display: "flex",
          flexDirection: "column",
          borderRadius: ED.r16,
          animation: "ed-fade-in 0.25s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem", marginBottom: "1rem" }}>
          <div>
            <div style={{ fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.08em", color: ED.textMuted, marginBottom: "0.35rem", textTransform: "uppercase" }}>
              At risk
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#ea580c", letterSpacing: "-0.02em" }}>
              {viewMode === "stock" ? `${formatQtyShort(totalQty)} qty` : formatCurrency(totalAtRiskValue)}
            </div>
            <div style={{ fontSize: "0.8125rem", color: ED.textSecondary, marginTop: "0.35rem", lineHeight: 1.4 }}>
              {sorted.length} active line{sorted.length === 1 ? "" : "s"} · ≤{CRITICAL_WINDOW_DAYS} days to sell-by
              {viewMode === "stock" && sorted.length > 0 ? ` · ${formatQtyShort(totalQty)} units on hand` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(248, 250, 252, 0.9)",
              border: "1px solid rgba(148, 163, 184, 0.25)",
              borderRadius: ED.r12,
              color: ED.textSecondary,
              cursor: "pointer",
              padding: "0.45rem 0.65rem",
              fontSize: "1.1rem",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1, minHeight: 0, borderTop: "1px solid rgba(14, 165, 233, 0.12)", margin: "0 -0.25rem", padding: "0.75rem 0.25rem 0" }}>
          {sorted.length === 0 ? (
            <p style={{ margin: "0.5rem 0", fontSize: "0.875rem", color: ED.textSecondary, lineHeight: 1.5 }}>
              No lines in this window. Active inventory outside ≤{CRITICAL_WINDOW_DAYS} days appears on the main list.
            </p>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {sorted.map((m) => {
                const lineVal = m.quantity * (m.pricePerBox || 0);
                const hasPrice = m.pricePerBox != null && Number.isFinite(Number(m.pricePerBox));
                return (
                  <li key={String(m.id)}>
                    <button
                      type="button"
                      onClick={() => {
                        onEditLine(m);
                        onClose();
                      }}
                      className="ed-card-glass"
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "0.75rem 0.85rem",
                        borderRadius: ED.r12,
                        border: "1px solid rgba(148, 163, 184, 0.22)",
                        background: "rgba(255,255,255,0.72)",
                        cursor: "pointer",
                        fontFamily: ED.font,
                        display: "grid",
                        gap: "0.25rem",
                      }}
                    >
                      <div style={{ fontWeight: 600, color: ED.text, fontSize: "0.9rem" }}>
                        {m.name}
                        {m.dosage ? <span style={{ fontWeight: 500, color: ED.textSecondary }}> · {m.dosage}</span> : null}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: ED.textSecondary }}>
                        {m.quantity} {m.unit}
                        <span style={{ color: ED.textMuted, margin: "0 0.35rem" }}>·</span>
                        Exp {formatExpiryDate(m.expiryMonth, m.expiryYear, m.expiryDayConvention || "last")}
                      </div>
                      <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: hasPrice ? "#c2410c" : ED.textMuted }}>
                        {viewMode === "stock" ? (
                          <span style={{ color: ED.text }}>{m.quantity} {m.unit} on hand</span>
                        ) : hasPrice ? (
                          formatCurrency(lineVal) + " in window"
                        ) : (
                          "No unit price — tap to edit"
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function MedCard({ med, onUpdate, onDelete, onEdit, onInteractionHint, viewMode = "finance", isDarkMode = false, isMobile = false }) {
  const days = getDaysUntilExpiry(med.expiryMonth, med.expiryYear);
  const daysToSellBy = getDaysUntilSellBy(med.expiryMonth, med.expiryYear);
  const urgency = getUrgencyLevel(daysToSellBy, med.status);
  const cfg = URGENCY_CONFIG[urgency];
  const totalValue = med.quantity * (med.pricePerBox || 0);
  const expiryLabel = formatExpiryDate(med.expiryMonth, med.expiryYear, "last");
  const quantityLabel = useMemo(() => {
    const raw = String(med.unit || "").trim().toLowerCase();
    if (!raw) return "";
    if (Number(med.quantity) === 1) {
      if (raw.endsWith("ies")) return raw.replace(/ies$/, "y");
      if (raw.endsWith("es") && (raw.endsWith("xes") || raw.endsWith("ches") || raw.endsWith("shes"))) return raw.slice(0, -2);
      if (raw.endsWith("s")) return raw.slice(0, -1);
      return raw;
    }
    if (raw.endsWith("s")) return raw;
    if (raw.endsWith("x") || raw.endsWith("ch") || raw.endsWith("sh")) return `${raw}es`;
    if (raw.endsWith("y")) return `${raw.slice(0, -1)}ies`;
    return `${raw}s`;
  }, [med.unit, med.quantity]);
  const urgencyTone =
    med.status === "item_moved"
      ? {
          badge: "MOVED",
          tooltip: "Marked as moved/dispensed.",
          primary: "Line marked moved / dispensed.",
          action: "No immediate expiry action needed.",
        }
      : med.status === "item_expired" || daysToSellBy < 0
        ? {
            badge: "CRITICAL",
            tooltip: "Sell-by window already passed. Act immediately.",
            primary: "Past sell-by. Remove now.",
            action: "Prioritize same-day movement.",
          }
        : daysToSellBy <= CRITICAL_WINDOW_DAYS
          ? {
              badge: "CRITICAL",
              tooltip: "Under 120 days to sell-by.",
              primary: `${daysToSellBy} days to sell-by`,
              action: "Prioritize sale today.",
            }
          : daysToSellBy <= WARNING_WINDOW_DAYS
            ? {
                badge: "PRIORITY",
                tooltip: "Middle-ground runway to sell-by.",
                primary: `${daysToSellBy} days to sell-by`,
                action: "Keep this batch moving.",
              }
            : daysToSellBy <= MONITOR_WINDOW_DAYS
              ? {
                  badge: "MONITOR",
                  tooltip: "Good runway, still monitor weekly.",
                  primary: `${daysToSellBy} days to sell-by`,
                  action: "Check periodically.",
                }
              : {
                  badge: "SAFE",
                  tooltip: "No immediate expiry risk.",
                  primary: "Within safe range.",
                  action: "No immediate action needed.",
                };
  const sellByCopy = useMemo(() => {
    if (med.status) return null;
    const sellByDate = new Date(Number(med.expiryYear), Number(med.expiryMonth) - 1, 0);
    const sellByLabel = sellByDate.toLocaleDateString("en-ZA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return `Sell by ${sellByLabel}`;
  }, [med.status, med.expiryMonth, med.expiryYear]);
  const cornerCountdownCopy = useMemo(() => {
    if (!(urgency === "critical" || urgency === "overdue" || urgency === "expired")) return null;
    if (!Number.isFinite(daysToSellBy)) return null;
    if (daysToSellBy < 0) return `${Math.abs(daysToSellBy)}d past sell-by`;
    return `${daysToSellBy}d to sell-by`;
  }, [urgency, daysToSellBy]);
  // Insight rail: one stable line per card from block hover (PRIORITY/MONITOR/CRITICAL). Status buttons do not swap the rail.
  const [cardHoverHint, setCardHoverHint] = useState("");
  const [badgeHoverHint, setBadgeHoverHint] = useState("");

  function handleStatus(s) { onUpdate({ ...med, status: med.status === s ? null : s }); }
  function acceptSuggestedName() {
    if (!med.suggestedName) return;
    onUpdate({
      ...med,
      name: med.suggestedName,
      matchStatus: "auto_corrected",
      originalName: med.originalName || med.name,
    });
  }
  function rejectSuggestedName() {
    onUpdate({
      ...med,
      matchStatus: "reviewed_no_match",
      suggestedName: med.suggestedName || null,
      originalName: med.originalName || med.name,
    });
  }

  const pillStyle = {
    display: "inline-flex",
    alignItems: "center",
    padding: "0.28rem 0.65rem",
    borderRadius: "9999px",
    fontSize: "0.6875rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    fontFamily: ED.font,
    background: cfg.pillBg,
    color: cfg.pillText,
    border: `1px solid ${cfg.pillBorder}`,
  };

  const cardShadow =
    `inset 5px 0 0 0 ${cfg.accentLeft}, inset 0 2px 0 0 ${cfg.cardFrame}, inset 0 1px 0 rgba(255,255,255,0.82), 0 1px 0 rgba(255,255,255,0.55), 0 14px 38px ${cfg.shadowTint}, 0 0 0 1px ${cfg.cardFrame}40, 0 0 22px ${cfg.shadowTint}, 0 6px 18px rgba(15, 23, 42, 0.07)`;
  const cardHoverShadow =
    `inset 5px 0 0 0 ${cfg.accentLeft}, inset 0 2px 0 0 ${cfg.cardFrame}, inset 0 1px 0 rgba(255,255,255,0.88), 0 20px 52px ${cfg.shadowTint}, 0 0 0 1px ${cfg.cardFrame}52, 0 0 28px ${cfg.shadowTint}, 0 12px 28px rgba(15, 23, 42, 0.14)`;

  return (
    <div
      className="ed-card-glass ed-med-card ed-med-card-interactive"
      data-ed-med-id={med.id != null ? String(med.id) : undefined}
      data-urgency={urgency}
      onClick={(e) => {
        if (e.target instanceof Element && e.target.closest("button")) return;
        if (!onInteractionHint) return;
        if (urgencyTone.badge === "SAFE" || urgencyTone.badge === "MONITOR") return;
        onInteractionHint(
          urgencyTone.badge === "CRITICAL"
            ? "These should be handled first"
            : urgencyTone.badge === "PRIORITY"
              ? "Keep this batch moving"
              : "Stable for now",
          e,
        );
      }}
      onFocusCapture={(e) => {
        if (!onInteractionHint) return;
        if (urgencyTone.badge !== "CRITICAL") return;
        onInteractionHint("High-risk item in focus", e);
      }}
      onMouseEnter={() => {
        const b = urgencyTone.badge;
        if (b === "CRITICAL") {
          setCardHoverHint(
            pickStableMicrocopy("card-critical", ["Remove immediately", "Action required now", "Take off shelf"], med.id),
          );
        } else if (b === "PRIORITY") {
          setCardHoverHint(
            pickStableMicrocopy(
              "card-priority",
              ["Keep this batch moving", "Plan rotation before sell-by", "Watch the sell-by window"],
              med.id,
            ),
          );
        } else if (b === "MONITOR") {
          setCardHoverHint(
            pickStableMicrocopy("card-monitor", ["Check periodically", "On your weekly radar", "No rush yet"], med.id),
          );
        } else {
          setCardHoverHint("");
        }
      }}
      onMouseLeave={() => {
        setCardHoverHint("");
        setBadgeHoverHint("");
      }}
      style={{
        padding: "1.35rem 1.4rem",
        paddingLeft: "1.45rem",
        opacity: med.status ? 0.88 : 1,
        borderRadius: ED.r16,
        transition: `box-shadow 0.28s ${ED.ease}, transform 0.28s ${ED.ease}, opacity 0.2s ${ED.ease}, border-color 0.28s ${ED.ease}`,
        /* Tint anchors bottom-right (quiet zone); light + dark shells override with !important where needed. */
        background: `linear-gradient(180deg, ${cfg.cardFrame}44 0%, ${cfg.cardFrame}1f 2.4%, rgba(255,255,255,0.98) 6%), linear-gradient(210deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.93) 52%, rgba(255,255,255,0.88) 78%, ${cfg.cardTint} 100%)`,
        border: `2px solid ${cfg.cardFrame}`,
        boxShadow: cardShadow,
        outline: "1px solid rgba(255,255,255,0.65)",
        outlineOffset: "-1px",
        "--ed-med-hover-shadow": cardHoverShadow,
        "--ed-med-hover-border": "rgba(100, 116, 139, 0.3)",
        position: "relative",
      }}
    >
      {cornerCountdownCopy ? (
        <div
          className="ed-med-card__tickcount"
          title={
            daysToSellBy < 0
              ? `${Math.abs(daysToSellBy)} day${Math.abs(daysToSellBy) === 1 ? "" : "s"} past sell-by`
              : `${daysToSellBy} day${daysToSellBy === 1 ? "" : "s"} until sell-by`
          }
        >
          {cornerCountdownCopy}
        </div>
      ) : null}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem", gap: "0.75rem" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="ed-med-card__headline"
            style={{
              fontFamily: ED.font,
              fontSize: "1.0625rem",
              letterSpacing: "-0.02em",
              lineHeight: 1.35,
              fontWeight: 700,
            }}
          >
            <span className="ed-med-card__name">{med.name}</span>
            {med.dosage ? (
              <span className="ed-med-card__dose">
                {" "}
                · {med.dosage}
              </span>
            ) : null}
          </div>
          {sellByCopy ? (
            <div
              className="ed-med-card__time-hint"
              style={{
                fontSize: "0.68rem",
                fontWeight: 600,
                lineHeight: 1.3,
                marginTop: "0.14rem",
                letterSpacing: "0.01em",
              }}
            >
              {sellByCopy}
            </div>
          ) : null}
          <div
            className="ed-med-card__meta"
            style={{
              fontSize: "0.78rem",
              marginTop: "0.35rem",
              fontFamily: ED.font,
            }}
          >
            <span
              className={viewMode === "stock" ? "ed-med-card__meta-stock" : undefined}
              style={{
                whiteSpace: "nowrap",
                display: "inline-block",
                fontWeight: 700,
                letterSpacing: "-0.005em",
                lineHeight: 1.25,
              }}
            >
              {`${med.quantity} ${quantityLabel} expire ${expiryLabel}`}
            </span>
          </div>
          {Array.isArray(med.incompleteCoreFields) && med.incompleteCoreFields.length > 0 ? (
            <div
              style={{
                marginTop: "0.65rem",
                padding: "0.65rem 0.75rem",
                borderRadius: ED.r12,
                background: "rgba(254, 243, 199, 0.5)",
                border: "1px solid rgba(234, 179, 8, 0.35)",
              }}
            >
              <div style={{ color: "#a16207", fontSize: "0.8125rem", fontFamily: ED.font, lineHeight: 1.45, fontWeight: 600 }}>
                Incomplete: {incompleteCoreFieldLabels(med.incompleteCoreFields)} — open Edit to finish this line.
              </div>
            </div>
          ) : null}
          {med.matchStatus === "needs_review" && (
            <div style={{ marginTop: "0.65rem", padding: "0.65rem 0.75rem", borderRadius: ED.r12, background: "rgba(254, 243, 199, 0.45)", border: "1px solid rgba(234, 179, 8, 0.25)" }}>
              <div style={{ color: "#a16207", fontSize: "0.8125rem", fontFamily: ED.font, lineHeight: 1.45 }}>
                Did you mean <strong>{med.suggestedName || "a catalog match"}</strong>?
              </div>
              {med.suggestedName && (
                <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
                  <button type="button" onClick={acceptSuggestedName} className="ed-btn-secondary" style={{ padding: "0.35rem 0.75rem", fontSize: "0.75rem" }}>Use suggestion</button>
                  <button type="button" onClick={rejectSuggestedName} className="ed-btn-ghost" style={{ padding: "0.35rem 0.75rem", fontSize: "0.75rem" }}>Keep original</button>
                </div>
              )}
            </div>
          )}
          {(med.matchStatus === "auto_corrected" || med.matchStatus === "learned_correction") &&
            med.originalName &&
            med.originalName !== med.name && (
            <div style={{ color: ED.textSecondary, fontSize: "0.75rem", marginTop: "0.45rem", fontFamily: ED.font }}>
              {med.matchStatus === "learned_correction" ? "Desk-learned name" : "Updated"} from {'“'}{med.originalName}{'”'}
            </div>
          )}
          <div className="ed-med-card__badge--mobile">
            <span style={pillStyle} title={urgencyTone.tooltip}>{urgencyTone.badge}</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", flexShrink: 0 }}>
          <div className="ed-med-card__badge--desktop" style={{ position: "relative" }}>
            <span
              style={pillStyle}
              title={urgencyTone.tooltip}
              onMouseEnter={() =>
                setBadgeHoverHint(
                  (() => {
                    const visibleTimePhrase = sellByCopy || null;
                    const rawOptions =
                      urgencyTone.badge === "CRITICAL"
                        ? ["Past expiry", "Expired stock", "Remove immediately"]
                        : urgencyTone.badge === "PRIORITY"
                          ? [sellByCopy || "Sell soon", "Sell soon", "Keep this moving"]
                          : urgencyTone.badge === "MONITOR"
                            ? ["Check occasionally", "Stable for now", "No rush yet"]
                            : [];
                    const options = visibleTimePhrase ? rawOptions.filter((o) => o !== visibleTimePhrase) : rawOptions;
                    if (options.length === 0) return "";
                    return pickStableMicrocopy(`badge-${urgencyTone.badge.toLowerCase()}`, options, med.id);
                  })(),
                )
              }
              onMouseLeave={() => setBadgeHoverHint("")}
            >
              {urgencyTone.badge}
            </span>
            {badgeHoverHint ? (
              <span
                style={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  top: "calc(100% + 0.3rem)",
                  whiteSpace: "nowrap",
                  background: "rgba(15, 23, 42, 0.88)",
                  color: "#e2e8f0",
                  borderRadius: 999,
                  padding: "0.18rem 0.5rem",
                  fontSize: "0.64rem",
                  fontWeight: 600,
                  pointerEvents: "none",
                  transition: `opacity 0.18s ${ED.ease}`,
                }}
              >
                {badgeHoverHint}
              </span>
            ) : null}
          </div>
          <button type="button" onClick={() => onEdit(med)} title="Edit" className="ed-btn-ghost" style={{ padding: "0.35rem 0.5rem", fontSize: "0.875rem" }}>✎</button>
          <button type="button" onClick={() => onDelete(med.id)} className="ed-btn-ghost" style={{ padding: "0.35rem 0.5rem", fontSize: "1rem", color: ED.textMuted }}>×</button>
        </div>
      </div>

      {viewMode === "finance" && (() => {
        const hasPrice = med.pricePerBox != null && med.pricePerBox !== "" && Number.isFinite(Number(med.pricePerBox));
        if (hasPrice) {
          return (
            <div className="ed-med-card__finance" style={{ marginBottom: "0.85rem", fontFamily: ED.font, fontSize: "0.8125rem", lineHeight: 1.3 }}>
              <div>
                <span className="ed-med-card__finance-value" style={{ fontWeight: 700 }}>{formatCurrency(totalValue)}</span>
              </div>
              <div className="ed-med-card__finance-muted" style={{ fontSize: "0.76rem", marginTop: "0.14rem" }}>
                ({formatCurrency(Number(med.pricePerBox))} per unit)
              </div>
            </div>
          );
        }
        if (med.status === "item_expired") {
          return (
            <div className="ed-med-card__finance" style={{ marginBottom: "0.85rem", fontFamily: ED.font, fontSize: "0.8125rem", lineHeight: 1.45 }}>
              No unit price — <button type="button" className="ed-btn-ghost" style={{ padding: 0, margin: 0, fontSize: "inherit", fontFamily: ED.font, fontWeight: 600, color: "#0284c7", verticalAlign: "baseline" }} onClick={() => onEdit(med)}>Edit line</button> to count this toward write-offs
            </div>
          );
        }
        if (med.status === "item_moved") {
          return (
            <div className="ed-med-card__finance" style={{ marginBottom: "0.85rem", fontFamily: ED.font, fontSize: "0.8125rem", lineHeight: 1.45 }}>
              No unit price — <button type="button" className="ed-btn-ghost" style={{ padding: 0, margin: 0, fontSize: "inherit", fontFamily: ED.font, fontWeight: 600, color: "#0284c7", verticalAlign: "baseline" }} onClick={() => onEdit(med)}>Edit line</button> to count this toward recovered
            </div>
          );
        }
        return null;
      })()}

      {cardHoverHint ? (
        <div
          aria-live="polite"
          role="status"
          className="ed-med-card__insight-rail"
          style={{
            marginBottom: "0.65rem",
            padding: "0.55rem 0.75rem",
            borderRadius: ED.r12,
            fontSize: "0.75rem",
            fontWeight: 600,
            lineHeight: 1.45,
            pointerEvents: "none",
            transition: `opacity 0.18s ${ED.ease}`,
            backdropFilter: "blur(6px)",
          }}
        >
          {cardHoverHint}
        </div>
      ) : null}

      <div className="ed-med-card__action-btns" style={{ display: "flex", gap: "0.5rem" }}>
        {["item_moved", "item_expired"].map(s => {
          const active = med.status === s;
          const isExpired = s === "item_expired";
          return (
            <button type="button" key={s} onClick={() => handleStatus(s)} style={{
              flex: 1, padding: "0.55rem 0.5rem",
              background: active
                ? (isExpired
                    ? (isDarkMode ? "rgba(255, 77, 77, 0.16)" : "rgba(254, 226, 226, 0.85)")
                    : (isDarkMode ? "rgba(20, 184, 196, 0.16)" : "rgba(219, 234, 254, 0.9)"))
                : (isDarkMode ? "rgba(18, 25, 41, 0.92)" : "rgba(252, 252, 251, 0.94)"),
              border: `1px solid ${active
                ? (isExpired
                    ? (isDarkMode ? "rgba(255, 77, 77, 0.42)" : "rgba(239, 68, 68, 0.35)")
                    : (isDarkMode ? "rgba(20, 184, 196, 0.4)" : "rgba(59, 130, 246, 0.35)"))
                : (isDarkMode ? "rgba(255, 255, 255, 0.22)" : "rgba(148, 163, 184, 0.25)")}`,
              borderRadius: ED.r12, cursor: "pointer",
              color: active
                ? (isExpired ? (isDarkMode ? "#ff8a8a" : "#b91c1c") : (isDarkMode ? "#7ee7ee" : "#1d4ed8"))
                : (isDarkMode ? "#c8d7ea" : ED.textSecondary),
              fontSize: "0.75rem", fontFamily: ED.font,
              fontWeight: 600,
              transition: `all 0.2s ${ED.ease}`,
            }}
            >
              {s === "item_moved" ? (isMobile ? "Dispensed" : "Moved / dispensed") : (isMobile ? "Expired" : "Marked expired")}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const MAX_STOCK_SHEET_UPLOAD_BYTES = 15 * 1024 * 1024;
/** Photos (handwritten lists) plus PDF / spreadsheet / Word — single upload control. */
const STOCK_SHEET_UPLOAD_ACCEPT =
  "image/*,.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,.csv,.xls,.xlsx,.doc,.docx,application/pdf,text/csv,application/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword";

function UploadView({
  onParsed,
  onUseDemoData,
  isMobile,
  onCancel,
  parseAccessToken,
  commitError,
  onClearCommitError,
}) {
  const pickUid = useId().replace(/:/g, "");
  const stockSheetInputId = `ed-upload-stock-sheet-${pickUid}`;
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pickedNonImageName, setPickedNonImageName] = useState(null);
  const [readPhaseLabel, setReadPhaseLabel] = useState("Reading your sheet…");

  const hiddenFileInputStyle = {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0,0,0,0)",
    whiteSpace: "nowrap",
    border: 0,
    opacity: 0,
  };

  async function processStockFile(file) {
    if (file.size > MAX_STOCK_SHEET_UPLOAD_BYTES) {
      setError("That file is too large (max 15 MB). Try exporting a smaller range or compressing the PDF.");
      return;
    }
    const kind = detectStockSheetFileKind(file.name || "", file.type || "");
    const phase =
      kind === "excel" || kind === "csv"
        ? "Reading spreadsheet…"
        : kind === "pdf" || kind === "word"
          ? "Reading document…"
          : "Reading your sheet…";
    setReadPhaseLabel(phase);
    setLoading(true);
    setError(null);
    onClearCommitError?.();
    if (kind === "image") {
      setPickedNonImageName(null);
    } else {
      setPickedNonImageName(file.name || "Selected file");
      setPreview(null);
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = typeof e.target?.result === "string" ? e.target.result : "";
      const rawBase64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : "";
      if (!rawBase64) {
        setError("Could not read that file. Try again or pick a different export.");
        setLoading(false);
        return;
      }
      const rawMediaType =
        file.type ||
        (kind === "pdf"
          ? "application/pdf"
          : kind === "word"
            ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            : kind === "excel" || kind === "csv"
              ? "application/octet-stream"
              : "image/jpeg");
      if (kind === "image") {
        setPreview(dataUrl);
      }
      let base64 = rawBase64;
      let mediaType = rawMediaType;
      if (kind === "image") {
        try {
          const compressed = await compressImageBase64ForApi(rawBase64, rawMediaType);
          base64 = compressed.base64;
          mediaType = compressed.mediaType;
        } catch (err) {
          console.warn("compressImageBase64ForApi skipped:", err);
        }
      }
      try {
        const headers = { "Content-Type": "application/json" };
        if (parseAccessToken) headers.Authorization = `Bearer ${parseAccessToken}`;
        const response = await fetchWithRetry(
          "/api/parse-stock-sheet",
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              base64,
              mediaType,
              fileName: file.name || undefined,
            }),
          },
          { retries: 2, delayMs: 2000, label: "parse-stock-sheet" },
        );
        let data = {};
        try {
          data = await response.json();
        } catch {
          throw new Error("Something didn't work as expected. Try again.");
        }
        if (!response.ok) {
          const msg = data.message || data.error || "We couldn't read that file. Please try again.";
          throw new Error(typeof msg === "string" ? msg : "We couldn't read that file. Please try again.");
        }
        const withIds = (data.meds || []).map((m, i) => ({
          ...m,
          id: Date.now() + i,
          pricePerBox: null,
          status: null,
        }));

        const merged = mergeDuplicates(withIds);
        const ok = await onParsed(merged);
        if (ok === false) return;
      } catch (err) {
        setError(humanizeNetworkOrUnknown(err));
      } finally {
        setLoading(false);
      }
    };
    reader.readAsDataURL(file);
  }

  useEffect(() => {
    if (typeof onCancel !== "function") return;
    function handleEscape(ev) {
      if (ev.key !== "Escape" || loading) return;
      onCancel();
    }
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onCancel, loading]);

  function handleStockSheetPick(e) {
    const f = e.currentTarget.files?.[0];
    e.currentTarget.value = "";
    if (!f) return;
    void processStockFile(f);
  }

  return (
    <>
      <PharmacyWarehouseBackdrop />
      <PharmacyQueueBackground position="fixed" />
      <div className="ed-upload-page-root" style={{ fontFamily: ED.font, viewTransitionName: "ed-app-surface" }}>
      <section className="ed-upload-shell" aria-busy={loading} aria-labelledby="ed-upload-heading">
      {commitError ? (
        <div
          role="alert"
          style={{
            width: "100%",
            maxWidth: "100%",
            marginBottom: "1rem",
            padding: "0.85rem 1rem",
            borderRadius: ED.r16,
            background: "rgba(254, 242, 242, 0.95)",
            border: "1px solid rgba(239, 68, 68, 0.25)",
            color: "#991b1b",
            fontSize: "0.875rem",
            lineHeight: 1.5,
            fontFamily: ED.font,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", alignItems: "flex-start" }}>
            <span>{commitError}</span>
            {typeof onClearCommitError === "function" ? (
              <button
                type="button"
                className="ed-btn-ghost"
                style={{ flexShrink: 0, padding: "0.25rem 0.5rem", fontSize: "0.8125rem", fontFamily: ED.font }}
                onClick={() => onClearCommitError()}
              >
                Dismiss
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {typeof onCancel === "function" ? (
        <nav className="ed-upload-back-sticky" aria-label="Leave upload">
          <div className="ed-upload-back-sticky__inner">
            <button type="button" className="ed-upload-back-cta ed-upload-back-cta--compact" disabled={loading} onClick={onCancel}>
              ← Back to dashboard
            </button>
            <span className="ed-upload-back-sticky__hint" title="Press Escape to leave this screen">
              Esc
            </span>
          </div>
        </nav>
      ) : null}
      <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", clipPath: "inset(50%)" }} aria-hidden>
        {/* Main picker — NO capture attr so iOS shows Photo Library + Files sheet */}
        <input
          ref={fileInputRef}
          id={stockSheetInputId}
          type="file"
          accept={STOCK_SHEET_UPLOAD_ACCEPT}
          disabled={loading}
          style={hiddenFileInputStyle}
          onChange={handleStockSheetPick}
        />
        {/* Camera-only input used by the "Take a photo" button on mobile */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          disabled={loading}
          style={hiddenFileInputStyle}
          onChange={handleStockSheetPick}
        />
      </div>

      <div
        className="ed-upload-hero-card"
        style={{
          textAlign: "center",
          marginBottom: "1.15rem",
          padding: "1.1rem 1.2rem",
          borderRadius: ED.r16,
          background: "rgba(255, 255, 255, 0.96)",
          border: "1px solid rgba(15, 23, 42, 0.1)",
          boxShadow: "0 12px 40px rgba(15, 23, 42, 0.14), 0 0 0 1px rgba(255,255,255,0.8) inset",
          maxWidth: "100%",
          width: "100%",
        }}
      >
        <h1 id="ed-upload-heading" style={{ margin: 0, font: "inherit", fontSize: "1.35rem", fontWeight: 800, color: ED.text, letterSpacing: "-0.02em" }}>
          Add stock
        </h1>
        <p style={{ margin: "0.55rem 0 0", color: "#475569", fontSize: "0.875rem", lineHeight: 1.5, fontWeight: 500 }}>
          Every line needs a <strong>name</strong>, <strong>expiry date</strong>, and <strong>quantity</strong>. If something is missing, you can still add it — we flag it on your desk.
        </p>
      </div>

      <label
        htmlFor={stockSheetInputId}
        onDragOver={(e) => {
          e.preventDefault();
          if (!loading) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (loading) return;
          const f = e.dataTransfer.files[0];
          if (f) void processStockFile(f);
        }}
        data-haptic={!loading ? "" : undefined}
        className={`ed-card-glass ed-upload-dropzone ed-upload-dropzone-premium${dragging ? " ed-upload-dropzone--dragging" : ""}`}
        style={{
          width: "100%",
          maxWidth: "100%",
          borderRadius: ED.r16,
          textAlign: "center",
          cursor: loading ? "default" : "pointer",
          display: "block",
          position: "relative",
          pointerEvents: loading ? "none" : "auto",
        }}
      >
        {loading ? (
          <div>
            <HourglassLoader
              size={44}
              label={readPhaseLabel.replace(/\u2026/g, "").trim() || "Reading stock"}
              style={{ margin: "0 auto 1rem" }}
            />
            <div style={{ color: ED.textSecondary, fontSize: "0.875rem", fontWeight: 500 }}>{readPhaseLabel}</div>
          </div>
        ) : preview ? (
          <Image
            src={preview}
            alt="Preview of your stock sheet"
            width={480}
            height={320}
            unoptimized
            style={{ maxHeight: "200px", width: "auto", height: "auto", borderRadius: ED.r12, opacity: 0.85, margin: "0 auto" }}
          />
        ) : pickedNonImageName ? (
          <>
            <div style={{ fontSize: "2.25rem", marginBottom: "0.85rem", opacity: 0.85 }} aria-hidden>
              📄
            </div>
            <div style={{ color: ED.text, marginBottom: "0.35rem", fontSize: "1.05rem", fontWeight: 600 }}>Selected file</div>
            <div style={{ color: "#475569", fontSize: "0.8125rem", fontWeight: 500, wordBreak: "break-word" }}>{pickedNonImageName}</div>
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", fontWeight: 500, marginTop: "0.5rem" }}>Tap here to choose a different file</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: "2.25rem", marginBottom: "0.85rem", opacity: 0.85 }} aria-hidden>
              📋
            </div>
            <div style={{ color: ED.text, marginBottom: "0.35rem", fontSize: "1.05rem", fontWeight: 600 }}>
              Upload stock sheet
            </div>
            {isMobile ? (
              <>
                <div style={{ color: "#475569", fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.45, marginBottom: "1.1rem" }}>
                  Handwritten photo, PDF, Excel, CSV, or Word (max 15 MB)
                </div>
                <div style={{ display: "flex", gap: "0.65rem", justifyContent: "center", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); cameraInputRef.current?.click(); }}
                    disabled={loading}
                    style={{
                      padding: "0.55rem 1.1rem",
                      borderRadius: ED.r12,
                      border: "1.5px solid rgba(15,23,42,0.18)",
                      background: "rgba(255,255,255,0.92)",
                      color: ED.text,
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      fontFamily: ED.font,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                    }}
                  >
                    <span aria-hidden>📷</span> Take a photo
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); fileInputRef.current?.click(); }}
                    disabled={loading}
                    style={{
                      padding: "0.55rem 1.1rem",
                      borderRadius: ED.r12,
                      border: "1.5px solid rgba(15,23,42,0.18)",
                      background: "rgba(255,255,255,0.92)",
                      color: ED.text,
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      fontFamily: ED.font,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                    }}
                  >
                    <span aria-hidden>📁</span> Choose file or photo
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ color: "#475569", fontSize: "0.875rem", fontWeight: 500, lineHeight: 1.5, marginBottom: "1.25rem" }}>
                  Drag a file here, or click to browse — up to 15 MB.
                </div>
                {/* File type chips */}
                <div style={{ display: "flex", gap: "0.45rem", justifyContent: "center", flexWrap: "wrap", marginBottom: "1.5rem" }}>
                  {[
                    { label: "PDF", emoji: "📄" },
                    { label: "Excel", emoji: "📊" },
                    { label: "CSV", emoji: "🗂" },
                    { label: "Word", emoji: "📝" },
                    { label: "Photo", emoji: "🖼" },
                  ].map(({ label, emoji }) => (
                    <span
                      key={label}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.3rem",
                        padding: "0.28rem 0.65rem",
                        borderRadius: 999,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.01em",
                        background: "rgba(14, 165, 233, 0.07)",
                        color: "#0369a1",
                        border: "1px solid rgba(14, 165, 233, 0.18)",
                      }}
                    >
                      <span aria-hidden>{emoji}</span>{label}
                    </span>
                  ))}
                </div>
                {/* Explicit browse CTA — makes the click action obvious */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); fileInputRef.current?.click(); }}
                  disabled={loading}
                  style={{
                    padding: "0.6rem 1.5rem",
                    borderRadius: ED.r12,
                    border: "1.5px solid rgba(14,165,233,0.35)",
                    background: "rgba(240,249,255,0.9)",
                    color: "#0369a1",
                    fontSize: "0.875rem",
                    fontWeight: 700,
                    fontFamily: ED.font,
                    cursor: "pointer",
                    letterSpacing: "-0.01em",
                  }}
                >
                  Browse files
                </button>
              </>
            )}
          </>
        )}
      </label>

      {error ? (
        <div role="alert" style={{ marginTop: "1rem", color: "#dc2626", fontSize: "0.875rem", fontWeight: 500 }}>
          {error}
        </div>
      ) : null}

      <div
        style={{
          marginTop: "1.75rem",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: "0.85rem",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {typeof onCancel === "function" ? (
          <>
            <button
              type="button"
              onClick={onCancel}
              className="ed-upload-back-cta ed-upload-back-cta--large"
              disabled={loading}
            >
              ← Back to dashboard
            </button>
            <p className="ed-upload-back-caption">
              Tip: your browser&apos;s <strong>back</strong> button does the same. Press <kbd className="ed-upload-kbd">Esc</kbd> for a
              quick exit.
            </p>
          </>
        ) : null}
        {process.env.NODE_ENV === "development" ? (
          <button type="button" onClick={onUseDemoData} className="ed-btn-ghost" style={{ padding: "0.65rem 1.5rem", fontFamily: ED.font }}>
            Load demo data
          </button>
        ) : null}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", justifyContent: "center" }}>
          <Link
            href="/history"
            className="ed-btn-secondary ed-upload-footer-link"
            aria-label="History — past scans and uploads"
            style={{ textAlign: "center", textDecoration: "none", display: "inline-block", padding: "0.65rem 1.5rem", fontFamily: ED.font }}
          >
            History
          </Link>
        </div>
      </div>
      </section>
      </div>
    </>
  );
}

const TOUR_META_KEY = "dashboard_tour_v1_done";

function reportNonFatal(scope, error) {
  const message =
    error && typeof error === "object" && "message" in error
      ? String(error.message)
      : String(error ?? "unknown");
  console.warn(`[ExpiryDesk][${scope}] ${message}`);
}

const DASHBOARD_SUMMARY_ANCHOR_ID = "ed-dash-summary-anchor";
const DASHBOARD_ATTENTION_FALLBACK_ID = "ed-dash-attention-fallback";

function scrollStockSummarySectionIntoView() {
  if (typeof document === "undefined") return;
  const summaryEl = document.getElementById(DASHBOARD_SUMMARY_ANCHOR_ID);
  if (summaryEl) {
    summaryEl.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }
  document.getElementById(DASHBOARD_ATTENTION_FALLBACK_ID)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function DashboardAccountMenuPanel({
  userEmail,
  hasSession,
  showManageSubscription,
  onClose,
  onLogout,
}) {
  const [toolsComingSoonExpanded, setToolsComingSoonExpanded] = useState(false);

  return (
    <div
      role="menu"
      className={`ed-dash-top-header__panel ed-dash-top-header__panel--wide ed-dash-top-header__panel--right${hasSession ? " ed-dash-top-header__panel--account-compact" : ""}`}
    >
      {!hasSession ? (
        <>
          <div className="ed-dash-top-header__panel-heading">Get started</div>
          <Link href="/dashboard?tab=help" role="menuitem" className="ed-dash-top-header__panel-item" onClick={() => onClose()}>
            How to Use
          </Link>
          <Link href="/signup" role="menuitem" className="ed-dash-top-header__panel-item" onClick={() => onClose()}>
            Sign in
          </Link>
        </>
      ) : (
        <>
          <div className="ed-dash-top-header__profile-email">{userEmail || "Signed in"}</div>
          <div className="ed-dash-account-menu-scroll">
            <Link
              href="/dashboard?tab=help"
              role="menuitem"
              className="ed-dash-top-header__panel-item"
              onClick={() => onClose()}
            >
              How to Use
            </Link>
            <Link href="/tools/expiry-calculator" role="menuitem" className="ed-dash-top-header__panel-item" onClick={() => onClose()}>
              Calculator
            </Link>

            <div className="ed-dash-top-header__panel-divider ed-dash-top-header__panel-divider--tight" aria-hidden />

            <Link href="/dashboard?tab=settings" role="menuitem" className="ed-dash-top-header__panel-item" onClick={() => onClose()}>
              Settings &amp; Account
            </Link>
            {showManageSubscription ? (
              <Link href="/manage-subscription" role="menuitem" className="ed-dash-top-header__panel-item" onClick={() => onClose()}>
                Billing &amp; subscription
              </Link>
            ) : null}
            <Link href="/history" role="menuitem" className="ed-dash-top-header__panel-item" onClick={() => onClose()}>
              History &amp; scans
            </Link>

            <div className="ed-dash-top-header__panel-divider ed-dash-top-header__panel-divider--tight" aria-hidden />

            <Link
              href="/dashboard?tab=settings#ed-settings-trash-bin"
              role="menuitem"
              className="ed-dash-top-header__panel-item"
              onClick={() => onClose()}
            >
              Trash bin
            </Link>

            <div className="ed-dash-top-header__panel-divider ed-dash-top-header__panel-divider--tight" aria-hidden />

            <button
              type="button"
              role="menuitem"
              className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--more-disclosure"
              aria-expanded={toolsComingSoonExpanded}
              onClick={() => setToolsComingSoonExpanded((v) => !v)}
            >
              <span className="ed-dash-top-header__panel-item-title">
                Tools <span aria-hidden>{toolsComingSoonExpanded ? "▴" : "▾"}</span>
              </span>
              <span className="ed-dash-top-header__panel-item-sub">
                {toolsComingSoonExpanded
                  ? "Hide coming soon items"
                  : "Pill counter, POS file, workspace — tap to show"}
              </span>
            </button>
            {toolsComingSoonExpanded ? (
              <>
                <div
                  className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--coming-soon"
                  aria-disabled="true"
                >
                  <span className="ed-dash-top-header__panel-item-title">Pill counter</span>
                  <span className="ed-dash-top-header__panel-item-sub">Coming soon — stay tuned</span>
                </div>
                <div
                  className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--coming-soon"
                  aria-disabled="true"
                >
                  <span className="ed-dash-top-header__panel-item-title">POS / programme stock file</span>
                  <span className="ed-dash-top-header__panel-item-sub">Coming soon — stay tuned</span>
                </div>
                <div
                  className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--coming-soon"
                  aria-disabled="true"
                >
                  <span className="ed-dash-top-header__panel-item-title">Workspace tools</span>
                  <span className="ed-dash-top-header__panel-item-sub">Coming soon — stay tuned</span>
                </div>
              </>
            ) : null}
          </div>

          <div className="ed-dash-account-menu-footer">
            <div className="ed-dash-account-menu-legal" role="none">
              <Link href="/privacy" className="ed-dash-account-menu-legal-link" onClick={() => onClose()}>
                Privacy
              </Link>
              <span className="ed-dash-account-menu-legal-sep" aria-hidden>
                ·
              </span>
              <Link href="/terms" className="ed-dash-account-menu-legal-link" onClick={() => onClose()}>
                Terms
              </Link>
            </div>
            <div className="ed-dash-top-header__panel-divider ed-dash-top-header__panel-divider--tight" aria-hidden />
            <button
              type="button"
              role="menuitem"
              className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--signout"
              onClick={() => {
                onClose();
                onLogout();
              }}
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * `useSearchParams()` can suspend during query-string updates. If it lives in `Dashboard` without a nearer
 * `Suspense` boundary, React bails out to `(desk)/layout`'s `Suspense` and the whole `ExpiryDeskApp` is replaced
 * by `null` — resetting desk state and breaking Add line / batch scan. Keep search-param sync in this leaf.
 */
function DashboardSearchParamsBridge({ setAddingNew, setMainTab, router, mainTab }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  /** `/dashboard?newLine=1` opens the new-line modal; `?tab=help|settings` syncs desk tabs from the URL. */
  useEffect(() => {
    if (pathname !== "/dashboard") return;
    if (searchParams.get("newLine") === "1") {
      setAddingNew(true);
      setMainTab("dashboard");
      router.replace("/dashboard", { scroll: false });
      return;
    }
    const tab = searchParams.get("tab");
    if (tab === "help") setMainTab("help");
    else if (tab === "settings") setMainTab("settings");
    else setMainTab("dashboard");
  }, [pathname, searchParams, router, setAddingNew, setMainTab]);

  /** Strip legacy `?focusMed=` (no longer used) so bookmarks stay on a clean dashboard URL. */
  useLayoutEffect(() => {
    if (pathname !== "/dashboard" || mainTab !== "dashboard") return;
    if (!searchParams.get("focusMed")) return;
    const u = new URL(window.location.href);
    u.searchParams.delete("focusMed");
    router.replace(`${u.pathname}${u.search}${u.hash}`, { scroll: false });
  }, [pathname, searchParams, router, mainTab]);

  useLayoutEffect(() => {
    if (pathname !== "/dashboard" || mainTab !== "settings") return;
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    if (!hash || hash.length < 2) return;
    const id = hash.slice(1);
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [pathname, mainTab, searchParams]);

  return null;
}

/**
 * `/upload?batchScan=1` opens the floor batch scanner from the dashboard. URL + `replace` avoids a race where
 * `router.push("/upload")` can commit the new route before React applies `setBatchScanOpen(true)`, so users only
 * see the sheet upload screen.
 */
function UploadBatchScanParamsBridge({ setBatchScanOpen, router }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useLayoutEffect(() => {
    if (pathname !== "/upload") return;
    if (searchParams.get("batchScan") !== "1") return;
    setBatchScanOpen(true);
    const id = window.requestAnimationFrame(() => {
      router.replace("/upload", { scroll: false });
    });
    return () => window.cancelAnimationFrame(id);
  }, [pathname, searchParams, router, setBatchScanOpen]);

  return null;
}

const Dashboard = forwardRef(function Dashboard(
  {
    meds,
    setMeds,
    deletedMeds,
    setDeletedMeds,
    onAddSheet,
    onAddBatchScan,
    onClearAllInventory,
    isMobile,
    isLandscapeCompact,
    supabaseReady,
    supabaseClient,
    userId,
    userEmail,
    userMetadata,
    onLogout,
    paidPlan,
    billingStatus,
    userCreatedAt,
    parseAccessToken,
    inventoryLoaded,
    inventoryRefreshing,
  },
  ref,
) {
  const router = useRouter();
  const pathname = usePathname();
  const navLaunchAnchorRef = useRef(null);
  const deskNavLogoMountRef = useRef(null);
  /** false = show cinematic launch on this mount (each full page load on /dashboard). */
  const [deskLaunchDone, setDeskLaunchDone] = useState(false);
  const [deskLaunchReplay, setDeskLaunchReplay] = useState(0);
  /** After data + launch overlay clear, brief pause so summary numbers feel "landed" not spreadsheet-snapped. */
  const [statsReveal, setStatsReveal] = useState(false);
  const [mainTab, setMainTab] = useState("dashboard");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [emailPreview, setEmailPreview] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [query, setQuery] = useState("");
  const debouncedNameSearch = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const [saveError, setSaveError] = useState("");
  const [exportBusy, setExportBusy] = useState(false);
  const [shareNotice, setShareNotice] = useState("");
  const [visibleCount, setVisibleCount] = useState(20);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreSentinelRef = useRef(null);
  const sortedFilteredRef = useRef([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [dashMenu, setDashMenu] = useState(/** @type {null | "notifications" | "profile" | "add"} */ (null));
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);
  /** Bell menu: "More" hides Priority Pulse + dashboard insights until expanded. */
  const [notifMenuMoreExpanded, setNotifMenuMoreExpanded] = useState(false);
  const [bulkImportPreviewRows, setBulkImportPreviewRows] = useState([]);
  const [bulkImportErrors, setBulkImportErrors] = useState([]);
  const [bulkImportBusy, setBulkImportBusy] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [cardBandVisibility, setCardBandVisibility] = useState({ red: true, orange: true, green: true });
  const [statHoverHint, setStatHoverHint] = useState({ key: "", text: "" });
  const [interactionHint, setInteractionHint] = useState(/** @type {{ text: string; x: number; y: number } | null} */ (null));
  const interactionHintTimerRef = useRef(null);
  const headerBarRef = useRef(null);
  const dashMenuCloseTimerRef = useRef(null);

  useEffect(() => {
    if (mainTab !== "dashboard" || typeof window === "undefined") return;
    const el = headerBarRef.current;
    if (!el) return;
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop;
      el.setAttribute("data-scrolled", y > 8 ? "true" : "false");
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [mainTab]);
  const [sortBy, setSortBy] = useState("urgency");
  const [editingMed, setEditingMed] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [writeOffsOpen, setWriteOffsOpen] = useState(false);
  const [recoveredOpen, setRecoveredOpen] = useState(false);
  const [atRiskOpen, setAtRiskOpen] = useState(false);
  const [statusUndo, setStatusUndo] = useState(null);
  const statusUndoTimerRef = useRef(null);
  const { dashboardView, setDashboardView, viewModeTipOpen, closeViewModeTip } = useDashboardPreferences({
    userId,
    userMetadata,
    supabaseClient,
    onSyncError: setSaveError,
  });
  const {
    emailAddr,
    setEmailAddr,
    emailSubLoading,
    emailSubError,
    emailSubscribed,
    subscribeEmailReminders,
  } = useEmailSubscriptions({
    supabaseReady,
    userId,
    userEmail,
  });
  const normalizedPlan = normalizeRuntimePlan(paidPlan, Boolean(userId));
  const { canUse, getLimit, isWithinLimit, isFree, isTeam } = usePlanGate(normalizedPlan);
  const expirySummary = useExpirySummary(meds || []);
  const planLabel = paidPlan === "premium" ? "PREMIUM" : paidPlan === "pro" ? "PRO" : "FREE";
  const planSubtitle =
    paidPlan === "premium"
      ? "Unlimited scans · one shared expiry list."
      : paidPlan === "pro"
        ? "Every sheet lands in one list. Pro includes 20 scans."
        : "Trial limits apply — upgrade when you are ready.";
  const renewalDate =
    billingStatus?.currentPeriodEnd && (paidPlan === "premium" || paidPlan === "pro")
      ? new Date(billingStatus.currentPeriodEnd).toLocaleDateString("en-ZA", { dateStyle: "medium" })
      : null;
  const statusLabel =
    billingStatus?.status || (paidPlan === "premium" || paidPlan === "pro" ? "active" : "free");

  const totalWaste = calcWaste(meds);
  const atRisk = calcAtRisk(meds);
  const saved = calcSaved(meds);
  const hasInventory = meds.length > 0;
  const medicationLimit = getLimit("maxMedications");
  const medicationsUsed = meds.length;
  const isMedicationUnlimited = medicationLimit === -1;
  const usagePercent = isMedicationUnlimited || medicationLimit <= 0 ? 0 : (medicationsUsed / medicationLimit) * 100;
  const isNearMedicationLimit = !isMedicationUnlimited && usagePercent >= 80 && usagePercent < 100;
  const isAtMedicationLimit = !isMedicationUnlimited && !isWithinLimit("maxMedications", medicationsUsed);
  /** Always show summary cards in Finance when there is inventory — Rand totals can be zero until prices exist. */
  const showStatTiles =
    hasInventory && (dashboardView === "stock" || dashboardView === "finance");
  const stockOverview = useMemo(() => {
    const groups = {
      overdue: { count: 0, value: 0, items: [] },
      critical: { count: 0, value: 0, items: [] },
      warning: { count: 0, value: 0, items: [] },
      monitor: { count: 0, value: 0, items: [] },
      safe: { count: 0, value: 0, items: [] },
    };
    for (const med of meds) {
      if (med.status) continue;
      const daysToSellBy = getDaysUntilSellBy(med.expiryMonth, med.expiryYear);
      const key = getUrgencyLevel(daysToSellBy, med.status);
      if (!(key in groups)) continue;
      groups[key].count += 1;
      groups[key].value += Number(med.quantity || 0) * Number(med.pricePerBox || 0);
      groups[key].items.push(med);
    }
    for (const key of ["overdue", "critical", "warning", "monitor", "safe"]) {
      groups[key].items.sort(
        (a, b) => getDaysUntilSellBy(a.expiryMonth, a.expiryYear) - getDaysUntilSellBy(b.expiryMonth, b.expiryYear),
      );
    }
    return groups;
  }, [meds]);
  const urgentAttentionCount = stockOverview.overdue.count + stockOverview.critical.count;
  /** Active lines expiring within 90 days (includes overdue) — Priority Pulse header bell + risk delta. */
  const priorityPulseCount = useMemo(
    () =>
      meds.filter((m) => {
        if (m.status) return false;
        const d = getDaysUntilSellBy(m.expiryMonth, m.expiryYear);
        return d <= CRITICAL_WINDOW_DAYS;
      }).length,
    [meds],
  );
  /** Active lines that still need desk action (same idea as cards: clear, move, or fix missing expiry / key fields). */
  const deskNotificationQueue = useMemo(() => buildDeskNotificationQueue(meds), [meds]);
  const deskNotificationCount = deskNotificationQueue.length;
  const activeLineCount = meds.filter((m) => !m.status).length;
  const writeOffMeds = useMemo(() => meds.filter((m) => m.status === "item_expired"), [meds]);
  const recoveredMeds = useMemo(() => meds.filter((m) => m.status === "item_moved"), [meds]);
  const atRiskMeds = useMemo(
    () =>
      meds.filter(
        (m) => !m.status && getDaysUntilSellBy(m.expiryMonth, m.expiryYear) <= CRITICAL_WINDOW_DAYS,
      ),
    [meds],
  );
  const expiringThisWeek = useMemo(() => {
    if (!hasInventory) return [];
    return meds.filter((m) => {
      if (m.status) return false;
      const d = getDaysUntilExpiry(m.expiryMonth, m.expiryYear);
      return d >= 0 && d <= 7;
    });
  }, [hasInventory, meds]);

  const showInteractionHint = useCallback((text, event) => {
    if (!text) return;
    const x = event?.clientX ?? (typeof window !== "undefined" ? window.innerWidth - 240 : 220);
    const y = event?.clientY ?? 136;
    setInteractionHint({ text, x, y });
    if (interactionHintTimerRef.current != null) {
      window.clearTimeout(interactionHintTimerRef.current);
    }
    interactionHintTimerRef.current = window.setTimeout(() => {
      interactionHintTimerRef.current = null;
      setInteractionHint(null);
    }, 2300);
  }, []);
  useEffect(
    () => () => {
      if (interactionHintTimerRef.current != null) {
        window.clearTimeout(interactionHintTimerRef.current);
        interactionHintTimerRef.current = null;
      }
    },
    [],
  );

  const sorted = useMemo(
    () =>
      [...meds]
        .filter((m) => {
          if (filterStatus === "active") return !m.status;
          if (filterStatus === "moved") return m.status === "item_moved";
          if (filterStatus === "expired") return m.status === "item_expired";
          if (filterStatus === "all") return m.status !== "item_expired" && m.status !== "item_moved";
          return true;
        })
        .sort((a, b) => {
          if (sortBy === "urgency") return getDaysUntilExpiry(a.expiryMonth, a.expiryYear) - getDaysUntilExpiry(b.expiryMonth, b.expiryYear);
          if (sortBy === "value") return (b.quantity * (b.pricePerBox || 0)) - (a.quantity * (a.pricePerBox || 0));
          if (sortBy === "quantity") return (Number(b.quantity) || 0) - (Number(a.quantity) || 0);
          return a.name.localeCompare(b.name);
        }),
    [meds, filterStatus, sortBy],
  );

  const sortedFiltered = useMemo(() => {
    const q = debouncedNameSearch.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(
      (m) =>
        (m.name || "").toLowerCase().includes(q) ||
        (m.dosage || "").toLowerCase().includes(q),
    );
  }, [sorted, debouncedNameSearch]);

  sortedFilteredRef.current = sortedFiltered;

  const dashboardQuickActionItems = useMemo(
    () => [
      {
        id: "cmd-add-line",
        type: "command",
        title: "Add inventory line",
        subtitle: "Create a manual stock line now",
        keywords: "add inventory line new stock entry manual create",
        action: () => router.push("/dashboard?newLine=1"),
      },
      {
        id: "cmd-import-pos-file",
        type: "command",
        title: "Import POS or programme file",
        subtitle: "Excel, CSV, PDF, Word, or photo on the upload screen",
        keywords: "pos programme stock file excel xlsx pdf docx csv upload import sheet export",
        action: () => onAddSheet(),
      },
      {
        id: "cmd-batch-scan",
        type: "command",
        title: "Batch scan",
        subtitle: "Open camera-first scanner",
        keywords: "batch scan camera capture upload shelf stock sheet",
        action: () => onAddBatchScan(),
      },
      {
        id: "cmd-how-to-use",
        type: "command",
        title: "How to use",
        subtitle: "Open guided dashboard instructions",
        keywords: "help tutorial instructions guide onboarding how to",
        action: () => router.push("/dashboard?tab=help"),
      },
      {
        id: "cmd-settings",
        type: "command",
        title: "Settings and audit log",
        subtitle: "Open account settings",
        keywords: "settings audit log preferences app setup",
        action: () => router.push("/dashboard?tab=settings"),
      },
      {
        id: "cmd-notifications",
        type: "command",
        title: "Notifications",
        subtitle: "Full action queue from the bell",
        keywords: "notifications bell action queue alerts desk lines expiry",
        action: () => router.push("/notifications"),
      },
      {
        id: "cmd-history",
        type: "command",
        title: "History and scans",
        subtitle: "Open sheet and add-line history",
        keywords: "history scans past uploads records",
        action: () => router.push("/history"),
      },
      {
        id: "cmd-manage-subscription",
        type: "command",
        title: "Manage subscription",
        subtitle: "Billing and plan controls",
        keywords: "billing subscription plan premium owner manager upgrade",
        action: () => router.push("/manage-subscription"),
      },
      {
        id: "cmd-attention",
        type: "command",
        title: "Jump to attention queue",
        subtitle: "Scroll to urgent lines",
        keywords: "attention urgent overdue critical queue",
        action: () => scrollStockSummarySectionIntoView(),
      },
    ],
    [onAddBatchScan, onAddSheet, router],
  );

  const { results: dashboardSearchCandidates, isSearching } = useDashboardSearchEngine({
    query,
    medications: meds,
    actions: dashboardQuickActionItems,
  });

  const closeDashboardSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSelectedIndex(-1);
  }, []);

  const { handleSelectResult: executeDashboardSearchCandidate, handleKeyDown: handleDashboardSearchInputKeyDown } =
    useDashboardSearchActions({
      results: dashboardSearchCandidates,
      selectedIndex,
      setSelectedIndex,
      onClose: closeDashboardSearch,
      navigate: router.push,
      onFilterChange: setFilterStatus,
      onTabChange: (nextTab) =>
        withViewTransition(() => {
          setMainTab(nextTab);
          if (nextTab === "help") router.replace("/dashboard?tab=help", { scroll: false });
          else if (nextTab === "settings") router.replace("/dashboard?tab=settings", { scroll: false });
          else router.replace("/dashboard", { scroll: false });
        }),
      onQueryChange: setQuery,
    });

  useEffect(() => {
    const el = loadMoreSentinelRef.current;
    if (!el || mainTab !== "dashboard") return;
    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        if (!hit) return;
        setVisibleCount((c) => {
          const cap = sortedFilteredRef.current.length;
          if (c >= cap) return c;
          setLoadingMore(true);
          window.setTimeout(() => {
            setLoadingMore(false);
            setVisibleCount((v) => Math.min(v + 20, sortedFilteredRef.current.length));
          }, 280);
          return c;
        });
      },
      { root: null, rootMargin: "120px", threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [mainTab, sortedFiltered.length, visibleCount, meds.length]);

  useEffect(() => {
    if (dashboardView === "stock" && sortBy === "value") setSortBy("urgency");
  }, [dashboardView, sortBy]);

  useEffect(() => {
    closeDashboardSearch();
  }, [mainTab, closeDashboardSearch]);

  useEffect(() => {
    setSelectedIndex(dashboardSearchCandidates.length > 0 ? 0 : -1);
  }, [dashboardSearchCandidates.length, query, isSearchOpen]);

  useEffect(() => {
    if (!saveError) return;
    const t = window.setTimeout(() => setSaveError(""), 5000);
    return () => window.clearTimeout(t);
  }, [saveError]);

  useEffect(() => {
    setVisibleCount(20);
  }, [filterStatus, sortBy, debouncedNameSearch]);

  useEffect(() => {
    if (hasInventory && inventoryLoaded) {
      track('dashboard.viewed', { 
        medicationCount: meds.length,
        hasExpiringItems: expiringThisWeek.length > 0,
        hasExpiredItems: writeOffMeds.length > 0
      });
    }
  }, [hasInventory, inventoryLoaded, meds.length]);

  useEffect(() => {
    if (mainTab !== "settings" || !supabaseReady || !userId) return;
    if (!canUse("auditLogEnabled")) {
      setAuditRows([]);
      setAuditLoading(false);
      return;
    }
    let cancelled = false;
    setAuditLoading(true);
    void (async () => {
      const { data } = await supabase
        .from("audit_logs")
        .select("id,action,entity_type,entity_id,meta,created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(100);
      if (cancelled) return;
      setAuditRows(data || []);
      setAuditLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [mainTab, supabaseReady, userId, canUse]);

  useEffect(() => {
    if (dashMenu !== "notifications") setNotifMenuMoreExpanded(false);
  }, [dashMenu]);

  const cancelDashMenuPointerClose = useCallback(() => {
    if (dashMenuCloseTimerRef.current != null) {
      window.clearTimeout(dashMenuCloseTimerRef.current);
      dashMenuCloseTimerRef.current = null;
    }
  }, []);

  const scheduleDashMenuPointerClose = useCallback(() => {
    if (isMobile || !dashMenu) return;
    cancelDashMenuPointerClose();
    dashMenuCloseTimerRef.current = window.setTimeout(() => {
      setDashMenu(null);
      dashMenuCloseTimerRef.current = null;
    }, 120);
  }, [isMobile, dashMenu, cancelDashMenuPointerClose]);

  const dashDropdownHoverProps = isMobile
    ? {}
    : { onMouseEnter: cancelDashMenuPointerClose, onMouseLeave: scheduleDashMenuPointerClose };

  const dashDropdownClass = (active) =>
    `ed-dash-top-header__dropdown${!isMobile && active ? " ed-dash-top-header__dropdown--open" : ""}`;

  useEffect(() => {
    if (!dashMenu) return;
    cancelDashMenuPointerClose();
    function handlePointerDown(ev) {
      if (headerBarRef.current && !headerBarRef.current.contains(ev.target)) {
        cancelDashMenuPointerClose();
        dashMenuCloseTimerRef.current = window.setTimeout(() => {
          setDashMenu(null);
          dashMenuCloseTimerRef.current = null;
        }, 120);
      }
    }
    function handleKey(ev) {
      if (ev.key === "Escape") setDashMenu(null);
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      cancelDashMenuPointerClose();
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [dashMenu, cancelDashMenuPointerClose]);

  useEffect(() => {
    if (!dashMenu || isMobile) return;
    function handleWheel() {
      setDashMenu(null);
    }
    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [dashMenu, isMobile]);

  useEffect(() => {
    setDashMenu(null);
  }, [mainTab]);

  useEffect(() => {
    if (!dashMenu || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [dashMenu, isMobile]);

  useLayoutEffect(() => {
    setIsDarkMode(readPreferredColorScheme() === "dark");
  }, []);

  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    const launchChrome =
      !deskLaunchDone && pathname === "/dashboard" && mainTab === "dashboard";
    document.documentElement.classList.toggle("ed-desk-launch-active", launchChrome);
    return () => {
      document.documentElement.classList.remove("ed-desk-launch-active");
    };
  }, [deskLaunchDone, pathname, mainTab]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    if (pathname !== "/dashboard" || mainTab !== "dashboard") return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduce.matches) {
      try {
        sessionStorage.setItem(DESK_LAUNCH_SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
      setDeskLaunchDone(true);
    }
  }, [pathname, mainTab]);

  useEffect(() => {
    const w = window;
    w.__ED_REPLAY_DESK_LAUNCH__ = () => {
      try {
        sessionStorage.removeItem(DESK_LAUNCH_SESSION_KEY);
      } catch {
        /* ignore */
      }
      setDeskLaunchReplay((n) => n + 1);
      setDeskLaunchDone(false);
    };
    return () => {
      delete w.__ED_REPLAY_DESK_LAUNCH__;
    };
  }, []);

  useEffect(() => {
    if (!inventoryLoaded || !deskLaunchDone) {
      setStatsReveal(false);
      return undefined;
    }
    const id = window.setTimeout(() => setStatsReveal(true), 300);
    return () => window.clearTimeout(id);
  }, [inventoryLoaded, deskLaunchDone, deskLaunchReplay]);

  useEffect(() => {
    applyDocumentThemePreference(isDarkMode);
  }, [isDarkMode]);

  const statTiles = useMemo(() => {
    const arQ = sumLineQuantities(atRiskMeds);
    const atRiskLineCount = atRiskMeds.length;
    if (dashboardView === "stock") {
      const woQ = sumLineQuantities(writeOffMeds);
      const recQ = sumLineQuantities(recoveredMeds);
      return [
        {
          label: "Write-offs",
          statNumber: writeOffMeds.length,
          statFormat: "integer",
          color: isDarkMode ? "var(--ed-dark-critical)" : "#e11d48",
          sub: `${formatQtyShort(woQ)} qty · expired lines`,
          statAction: "writeoffs",
          dashVariant: "writeoff",
          historyLink: true,
        },
        {
          label: "At Risk",
          statNumber: atRiskLineCount,
          statFormat: "integer",
          color: isDarkMode ? "var(--ed-dark-warning)" : "#ea580c",
          sub: `≤${CRITICAL_WINDOW_DAYS} days to sell-by · active · ${formatQtyShort(arQ)} qty on hand`,
          statAction: "atrisk",
          dashVariant: "atrisk",
          historyLink: true,
        },
        {
          label: "Recovered",
          statNumber: recoveredMeds.length,
          statFormat: "integer",
          color: isDarkMode ? "var(--ed-dark-safe)" : "#059669",
          sub: `${formatQtyShort(recQ)} qty`,
          statAction: "recovered",
          dashVariant: "recovered",
          historyLink: true,
        },
      ];
    }
    return [
      {
        label: "Write-offs",
        statNumber: totalWaste,
        statFormat: "currency",
        color: isDarkMode ? "var(--ed-dark-critical)" : "#e11d48",
        sub: `${writeOffMeds.length} ${writeOffMeds.length === 1 ? "item" : "items"} written off`,
        statAction: "writeoffs",
        dashVariant: "writeoff",
        historyLink: true,
      },
      {
        label: "At Risk",
        statNumber: atRisk,
        statFormat: "currency",
        color: isDarkMode ? "var(--ed-dark-warning)" : "#ea580c",
        sub: `${atRiskLineCount} ${atRiskLineCount === 1 ? "item" : "items"} ≤ ${CRITICAL_WINDOW_DAYS} days to sell-by`,
        statAction: "atrisk",
        dashVariant: "atrisk",
        historyLink: true,
      },
      {
        label: "Recovered",
        statNumber: saved,
        statFormat: "currency",
        color: isDarkMode ? "var(--ed-dark-safe)" : "#059669",
        sub: `${recoveredMeds.length} ${recoveredMeds.length === 1 ? "item" : "items"} recovered`,
        statAction: "recovered",
        dashVariant: "recovered",
        historyLink: true,
      },
    ];
  }, [dashboardView, writeOffMeds, recoveredMeds, atRiskMeds, totalWaste, atRisk, saved, isDarkMode]);

  useEffect(() => {
    return () => {
      if (statusUndoTimerRef.current != null) {
        window.clearTimeout(statusUndoTimerRef.current);
        statusUndoTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !userId) return;
    const doneMeta = userMetadata?.[TOUR_META_KEY] === true;
    const doneLs = window.localStorage.getItem(`ed_tour_v1_${userId}`);
    if (doneMeta || doneLs) {
      setTourOpen(false);
      return;
    }
    // Tour targets (stats, filters, …) only exist once there is inventory — open when first lines land.
    setTourOpen(hasInventory);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only tour flag in metadata; avoid broad userMetadata dep
  }, [userId, userMetadata?.[TOUR_META_KEY], hasInventory]);

  async function dismissTour() {
    if (typeof window !== "undefined" && userId) {
      window.localStorage.setItem(`ed_tour_v1_${userId}`, "1");
    }
    if (supabaseClient && userId) {
      try {
        await supabaseClient.auth.updateUser({ data: { [TOUR_META_KEY]: true } });
        await supabaseClient.auth.refreshSession();
      } catch (err) {
        reportNonFatal("dismiss_tour_remote_sync", err);
      }
    }
    setTourOpen(false);
  }

  async function showTourAgain() {
    if (typeof window !== "undefined" && userId) {
      window.localStorage.removeItem(`ed_tour_v1_${userId}`);
    }
    if (supabaseClient && userId) {
      try {
        await supabaseClient.auth.updateUser({ data: { [TOUR_META_KEY]: false } });
        await supabaseClient.auth.refreshSession();
      } catch (err) {
        reportNonFatal("show_tour_again_remote_sync", err);
      }
    }
    setMainTab("dashboard");
    if (pathname === "/dashboard") router.replace("/dashboard", { scroll: false });
    setTourOpen(true);
  }

  async function updateMed(updated, options = {}) {
    const skipStatusUndo = options.skipStatusUndo === true;
    const prevRow = meds.find((m) => sameMedId(m.id, updated.id));
    const becameExpired =
      !skipStatusUndo &&
      prevRow &&
      prevRow.status !== "item_expired" &&
      updated.status === "item_expired";
    const becameMoved =
      !skipStatusUndo &&
      prevRow &&
      prevRow.status !== "item_moved" &&
      updated.status === "item_moved";

    if (becameExpired || becameMoved) {
      if (statusUndoTimerRef.current != null) {
        window.clearTimeout(statusUndoTimerRef.current);
        statusUndoTimerRef.current = null;
      }
      setStatusUndo({
        id: updated.id,
        kind: becameExpired ? "expired" : "moved",
        label: (prevRow.name || "Line").trim() || "Line",
        snapshot: { ...prevRow },
      });
      statusUndoTimerRef.current = window.setTimeout(() => {
        statusUndoTimerRef.current = null;
        setStatusUndo(null);
      }, 12000);
    }

    const cleaned = sanitizeMedForSave(updated);
    const incompleteNow = computeIncompleteCoreFields({
      ...cleaned,
      expiryMonth: updated.expiryMonth,
      expiryYear: updated.expiryYear,
      quantity: updated.quantity,
    });
    const cleanedWithInc = { ...cleaned, incompleteCoreFields: incompleteNow };
    setMeds((prev) => prev.map((m) => (sameMedId(m.id, updated.id) ? cleanedWithInc : m)));
    if (!supabaseReady || !userId) return;
    const rowId = updated.id == null ? updated.id : String(updated.id);

    const patch = {
      name: cleaned.name,
      dosage: cleaned.dosage || "",
      quantity: updated.quantity,
      unit: cleaned.unit,
      expiry_month: updated.expiryMonth,
      expiry_year: updated.expiryYear,
      expiry_day_convention: updated.expiryDayConvention || "last",
      price_per_box: updated.pricePerBox,
      status: cleaned.status,
      product_category: cleaned.productCategory,
      supplier_name: cleaned.supplierName ? String(cleaned.supplierName).trim().slice(0, 120) : null,
    };
    if (updated.scanDate == null || updated.scanDate === "") {
      patch.scan_date = new Date().toISOString();
    }

    const result = await updateMedicationPatchWithSchemaFallback(supabase, rowId, userId, patch);
    const { data, error } = result;

    if (error) {
      logger.error('medication.update.failed', error, { medicationId: updated.id });
      if (prevRow) {
        setMeds((prev) => prev.map((m) => (sameMedId(m.id, updated.id) ? prevRow : m)));
      }
      setStatusUndo(null);
      if (statusUndoTimerRef.current != null) {
        window.clearTimeout(statusUndoTimerRef.current);
        statusUndoTimerRef.current = null;
      }
      setSaveError(error.message || "Could not save changes.");
      return;
    }
    if (data) {
      setMeds((prev) => prev.map((m) => (sameMedId(m.id, data.id) ? mapMedicationRow(data) : m)));
      if (prevRow && prevRow.status !== cleaned.status) {
        void insertAuditLog(supabase, userId, {
          action: "item_status_changed",
          entityId: String(data.id),
          meta: { from: prevRow.status ?? null, to: cleaned.status ?? null },
        });
      }
      if (
        prevRow &&
        (prevRow.name !== cleaned.name ||
          (prevRow.dosage || "") !== (cleaned.dosage || "") ||
          Number(prevRow.quantity) !== Number(cleaned.quantity) ||
          (prevRow.unit || "") !== (cleaned.unit || "") ||
          prevRow.expiryMonth !== cleaned.expiryMonth ||
          prevRow.expiryYear !== cleaned.expiryYear ||
          prevRow.pricePerBox !== cleaned.pricePerBox ||
          normalizeProductCategory(prevRow.productCategory) !== normalizeProductCategory(cleaned.productCategory) ||
          String(prevRow.supplierName || "") !== String(cleaned.supplierName || ""))
      ) {
        void insertAuditLog(supabase, userId, { action: "item_edited", entityId: String(data.id) });
      }
      if (prevRow && String(prevRow.name ?? "") !== String(cleaned.name ?? "")) {
        void logFieldOverride({
          supabase,
          medicationId: String(data.id),
          fieldName: "name",
          aiValue: prevRow.name ?? null,
          userValue: cleaned.name ?? "",
          aiConfidence: prevRow.aiConfidenceScore ?? undefined,
          userId,
        });
      }
      if (prevRow && Number(prevRow.expiryMonth ?? 0) !== Number(cleaned.expiryMonth ?? 0)) {
        void logFieldOverride({
          supabase,
          medicationId: String(data.id),
          fieldName: "expiryMonth",
          aiValue: prevRow.expiryMonth == null ? null : String(prevRow.expiryMonth),
          userValue: String(cleaned.expiryMonth ?? ""),
          aiConfidence: prevRow.aiConfidenceScore ?? undefined,
          userId,
        });
      }
      if (prevRow && Number(prevRow.expiryYear ?? 0) !== Number(cleaned.expiryYear ?? 0)) {
        void logFieldOverride({
          supabase,
          medicationId: String(data.id),
          fieldName: "expiryYear",
          aiValue: prevRow.expiryYear == null ? null : String(prevRow.expiryYear),
          userValue: String(cleaned.expiryYear ?? ""),
          aiConfidence: prevRow.aiConfidenceScore ?? undefined,
          userId,
        });
      }
      if (prevRow && Number(prevRow.quantity ?? 0) !== Number(cleaned.quantity ?? 0)) {
        void logFieldOverride({
          supabase,
          medicationId: String(data.id),
          fieldName: "quantity",
          aiValue: prevRow.quantity == null ? null : String(prevRow.quantity),
          userValue: String(cleaned.quantity ?? ""),
          aiConfidence: prevRow.aiConfidenceScore ?? undefined,
          userId,
        });
      }
    }
  }

  function undoStatusBucket() {
    const u = statusUndo;
    if (!u?.snapshot) return;
    if (statusUndoTimerRef.current != null) {
      window.clearTimeout(statusUndoTimerRef.current);
      statusUndoTimerRef.current = null;
    }
    setStatusUndo(null);
    void updateMed(u.snapshot, { skipStatusUndo: true });
  }

  async function deleteMed(id) {
    const ok = window.confirm(
      "Remove this line?\n\nIt goes to Recently deleted for up to 30 days — you can restore it from Settings → Trash.",
    );
    if (!ok) return;
    const prev = meds.find((m) => sameMedId(m.id, id));
    const removed = prev ? { ...prev } : null;
    const ts = new Date().toISOString();
    setMeds((prevList) => prevList.filter((m) => !sameMedId(m.id, id)));
    if (removed) setDeletedMeds((d) => [{ ...removed, deletedAt: ts }, ...d]);
    if (!supabaseReady || !userId) return;
    const { error } = await supabase
      .from("medications")
      .update({ deleted_at: ts })
      .eq("id", id == null ? id : String(id))
      .eq("user_id", userId);
    if (error) {
      if (removed) {
        setMeds((prevList) => [...prevList, removed]);
        setDeletedMeds((d) => d.filter((x) => !sameMedId(x.id, id)));
      }
      setSaveError(error.message || "Could not remove line.");
      return;
    }
    void insertAuditLog(supabase, userId, { action: "item_deleted", entityId: String(id) });
  }

  async function restoreDeletedMed(med) {
    if (!supabaseReady || !userId) return;
    const { data, error } = await supabase
      .from("medications")
      .update({ deleted_at: null })
      .eq("id", med.id == null ? med.id : String(med.id))
      .eq("user_id", userId)
      .select("*")
      .single();
    if (error || !data) {
      setSaveError(error?.message || "Could not restore line.");
      return;
    }
    const row = mapMedicationRow(data);
    setDeletedMeds((d) => d.filter((x) => !sameMedId(x.id, med.id)));
    setMeds((prev) => mergeDuplicates([...prev, row]));
    void insertAuditLog(supabase, userId, { action: "item_restored", entityId: String(med.id) });
  }

  function medRowsForExport() {
    return sortedFiltered.map((m) => ({
      name: m.name || "",
      dosage: m.dosage || "",
      quantity: Number(m.quantity) || 0,
      unit: m.unit || "",
      expiryMonth: m.expiryMonth,
      expiryYear: m.expiryYear,
      status: m.status,
      pricePerBox: m.pricePerBox,
    }));
  }

  function generateExpiryCSV(medications) {
    const now = new Date();
    const rows = medications
      .map((med) => {
        const expiry = new Date(Number(med.expiryYear), Number(med.expiryMonth), 0);
        const daysUntil = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return {
          medication_name: med.name || "",
          batch_number: med.dosage || "",
          expiry_date: `${String(med.expiryMonth).padStart(2, "0")}/${med.expiryYear}`,
          days_until_expiry: daysUntil,
          quantity: med.quantity,
          category: med.productCategory || "",
          risk_level: getUrgencyLevel(getDaysUntilSellBy(med.expiryMonth, med.expiryYear), med.status).toUpperCase(),
        };
      })
      .sort((a, b) => a.days_until_expiry - b.days_until_expiry);
    if (!rows.length) {
      return "medication_name,batch_number,expiry_date,days_until_expiry,quantity,category,risk_level\n";
    }
    const headers = Object.keys(rows[0]).join(",");
    const csvRows = rows.map((r) => Object.values(r).join(","));
    return [headers, ...csvRows].join("\n");
  }

  function exportFilterDescription() {
    const fs =
      filterStatus === "all"
        ? "All active-style"
        : filterStatus === "active"
          ? "Active"
          : filterStatus === "moved"
            ? "Recovered"
            : filterStatus === "expired"
              ? "Write-offs"
              : filterStatus;
    const q = debouncedNameSearch.trim();
    return [fs, q ? `search "${q}"` : ""].filter(Boolean).join(" · ");
  }

  function reportShareUrl() {
    const ref = userId ? String(userId).slice(0, 8) : "public";
    const q = new URLSearchParams({
      utm_source: "report",
      utm_medium: "shared-export",
      utm_campaign: "viral-loop",
      ref,
      report: exportFilterDescription(),
    });
    return `https://expirydesk.co.za/signup?${q.toString()}`;
  }

  async function handleExportCsv() {
    setExportBusy(true);
    try {
      const csv = generateExpiryCSV(sortedFiltered);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `expirydesk-risk-report-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      void logAuditEvent({
        action: AUDIT_ACTIONS.REPORT_EXPORTED,
        resourceType: "inventory_report",
        resourceId: "csv",
        metadata: { format: "csv", filter: exportFilterDescription() },
      });
    } finally {
      setExportBusy(false);
    }
  }

  async function handleExportPdf() {
    setExportBusy(true);
    try {
      const bytes = await medicationsToPdfBytes(medRowsForExport(), exportFilterDescription(), { shareUrl: reportShareUrl() });
      const blob = new Blob([bytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `expirydesk-export-${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      void logAuditEvent({
        action: AUDIT_ACTIONS.REPORT_EXPORTED,
        resourceType: "inventory_report",
        resourceId: "pdf",
        metadata: { format: "pdf", filter: exportFilterDescription() },
      });
    } finally {
      setExportBusy(false);
    }
  }

  async function handleShareRiskReport() {
    const url = reportShareUrl();
    const text = "Expiry risk report generated in ExpiryDesk. Review and coordinate replenishment here:";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Expiry risk report", text, url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${text} ${url}`);
      }
      setShareNotice("Share link ready.");
      window.setTimeout(() => setShareNotice(""), 2600);
    } catch {
      setShareNotice("Share canceled.");
      window.setTimeout(() => setShareNotice(""), 2200);
    }
  }

  async function handleBulkImportFile(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const kind = detectStockSheetFileKind(file.name || "", file.type || "");
    try {
      if (kind === "csv") {
        const text = await file.text();
        const parsed = parseInventoryCsv(text);
        if ((parsed.rows || []).length > 0) {
          setBulkImportPreviewRows(parsed.rows || []);
          setBulkImportErrors(parsed.errors || []);
          setBulkImportOpen(true);
          showInteractionHint("Bulk actions ready", event);
          return;
        }
      }

      if (kind !== "csv" && kind !== "excel") {
        setBulkImportPreviewRows([]);
        setBulkImportErrors([{ row: 0, message: "Only CSV or Excel files are supported in Bulk import." }]);
        setBulkImportOpen(true);
        showInteractionHint("Bulk actions opened", event);
        return;
      }

      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(typeof e.target?.result === "string" ? e.target.result : "");
        reader.onerror = () => reject(new Error("Could not read selected file"));
        reader.readAsDataURL(file);
      });
      const base64 = String(dataUrl || "").includes(",") ? String(dataUrl).split(",")[1] : "";
      if (!base64) throw new Error("Could not read selected file");

      const headers = { "Content-Type": "application/json" };
      if (parseAccessToken) headers.Authorization = `Bearer ${parseAccessToken}`;
      const response = await fetchWithRetry(
        "/api/parse-stock-sheet",
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            base64,
            mediaType: "application/octet-stream",
            fileName: file.name || undefined,
          }),
        },
        { retries: 1, delayMs: 1200, label: "bulk-import-parse-sheet" },
      );
      const data = await response.json();
      if (!response.ok) {
        const msg = data?.message ?? data?.error;
        throw new Error(typeof msg === "string" ? msg : "We couldn't import those items. Please try again.");
      }
      const rows = (data?.meds || []).map((m) => ({
        name: m.name || "",
        batchNumber: m.dosage || "",
        expiryDate: `${String(m.expiryYear || new Date().getFullYear() + 1).padStart(4, "0")}-${String(m.expiryMonth || 12).padStart(2, "0")}-01`,
        quantity: Number(m.quantity) || 1,
        category: m.productCategory || undefined,
        pricePerBox: Number.isFinite(Number(m.pricePerBox)) ? Number(m.pricePerBox) : null,
        dosage: m.dosage || "",
        unit: m.unit || "boxes",
      }));
      setBulkImportPreviewRows(rows);
      setBulkImportErrors([]);
      setBulkImportOpen(true);
      showInteractionHint(rows.length > 1 ? "High risk items selected" : "Bulk actions ready", event);
    } catch (err) {
      setBulkImportPreviewRows([]);
      setBulkImportErrors([{ row: 0, message: humanizeNetworkOrUnknown(err) }]);
      setBulkImportOpen(true);
      showInteractionHint("Bulk actions opened", event);
    }
  }

  async function handleConfirmBulkImport() {
    if (!bulkImportPreviewRows.length) return;
    setBulkImportBusy(true);
    try {
      const cap = isTeam ? Number.POSITIVE_INFINITY : 200;
      const slice = bulkImportPreviewRows.slice(0, cap);
      const toRows = slice.map((row) => {
        const dt = new Date(row.expiryDate);
        const expiryMonth = Number.isNaN(dt.getTime()) ? null : dt.getMonth() + 1;
        const expiryYear = Number.isNaN(dt.getTime()) ? null : dt.getFullYear();
        const medShape = { name: row.name, quantity: row.quantity, expiryMonth, expiryYear };
        const inc = computeIncompleteCoreFields(medShape);
        return {
          user_id: userId || "",
          name: row.name,
          dosage: row.batchNumber || row.dosage || "",
          quantity: row.quantity,
          unit: row.unit || "boxes",
          expiry_month: expiryMonth,
          expiry_year: expiryYear,
          price_per_box: Number.isFinite(Number(row.pricePerBox)) ? Number(row.pricePerBox) : null,
          status: null,
          product_category: row.category || null,
          scan_date: new Date().toISOString(),
        };
      });

      if (supabaseReady && userId) {
        const { data, error } = await insertMedicationRowsWithSchemaFallback(supabase, toRows);
        if (error) {
          setSaveError(error.message || "We couldn't import those items. Please try again.");
          return;
        }
        const mapped = (data || []).map(mapMedicationRow);
        setMeds((prev) => [...(prev || []), ...mapped]);
      } else {
        const mapped = toRows.map((row, idx) => ({
          id: `bulk-local-${Date.now()}-${idx}`,
          name: row.name,
          dosage: row.dosage || "",
          quantity: row.quantity,
          unit: row.unit,
          expiryMonth: row.expiry_month,
          expiryYear: row.expiry_year,
          pricePerBox: row.price_per_box,
          status: row.status,
          productCategory: row.product_category,
          incompleteCoreFields: row.incomplete_core_fields || [],
        }));
        setMeds((prev) => [...(prev || []), ...mapped]);
      }

      void logAuditEvent({
        action: AUDIT_ACTIONS.BULK_IMPORT_COMPLETED,
        resourceType: "medications",
        resourceId: userId || "anonymous",
        metadata: { importedRows: toRows.length },
      });
      setBulkImportOpen(false);
      setBulkImportPreviewRows([]);
      setBulkImportErrors([]);
    } finally {
      setBulkImportBusy(false);
    }
  }

  function saveMed(med) {
    const cleaned = sanitizeMedForSave(med);
    if (meds.find((m) => sameMedId(m.id, cleaned.id))) {
      void updateMed(cleaned);
      return;
    }

    // Local-only fallback (when Supabase isn’t configured/authenticated).
    if (!supabaseReady || !userId) {
      const inc = computeIncompleteCoreFields(cleaned);
      setMeds((prev) => mergeDuplicates([...prev, { ...cleaned, incompleteCoreFields: inc }]));
      return;
    }

    // Persist new manual item as a DB row.
    (async () => {
      const inc = computeIncompleteCoreFields(cleaned);
      const insertRow = {
        user_id: userId,
        name: cleaned.name,
        dosage: cleaned.dosage || "",
        quantity: cleaned.quantity,
        unit: cleaned.unit,
        expiry_month: cleaned.expiryMonth,
        expiry_year: cleaned.expiryYear,
        price_per_box: cleaned.pricePerBox,
        status: cleaned.status,
        product_category: cleaned.productCategory,
        supplier_name: cleaned.supplierName ? String(cleaned.supplierName).trim().slice(0, 120) : null,
        scan_date: new Date().toISOString(),
        incomplete_core_fields: inc.length ? inc : [],
      };
      const { data: inserted, error } = await insertMedicationRowsWithSchemaFallback(supabase, [insertRow]);
      const data = inserted?.[0];

      if (!error && data) {
        const row = mapMedicationRow(data);
        setMeds((prev) => [...prev, row]);
        void insertAuditLog(supabase, userId, { action: "item_edited", entityId: String(data.id), meta: { created: true } });
      } else {
        setSaveError(error?.message || "Could not add item. Please retry.");
      }
    })();
  }

  const bandForCard = useCallback((med) => {
    if (med.status === "item_expired") return "red";
    if (med.status === "item_moved") return "green";
    const tier = urgencyTierForActiveLine(getDaysUntilSellBy(med.expiryMonth, med.expiryYear));
    if (tier === "overdue" || tier === "critical") return "red";
    if (tier === "warning") return "orange";
    if (tier === "monitor") return "green";
    return "green";
  }, []);

  const colorFiltered = useMemo(
    () =>
      sortedFiltered.filter((med) => {
        const band = bandForCard(med);
        return cardBandVisibility[band];
      }),
    [sortedFiltered, cardBandVisibility, bandForCard],
  );
  const colorBandStats = useMemo(() => {
    const stats = { red: 0, orange: 0, green: 0 };
    sortedFiltered.forEach((med) => {
      const band = bandForCard(med);
      stats[band] += 1;
    });
    return stats;
  }, [sortedFiltered, bandForCard]);
  const hiddenBands = useMemo(
    () =>
      [
        !cardBandVisibility.red ? "Critical" : null,
        !cardBandVisibility.orange ? "At Risk" : null,
        !cardBandVisibility.green ? "Safe" : null,
      ].filter(Boolean),
    [cardBandVisibility],
  );

  const visibleSlice = colorFiltered.slice(0, visibleCount);
  const hasMoreLines = visibleCount < colorFiltered.length;

  const criticalItems = meds
    .filter((m) => !m.status)
    .sort((a, b) => getDaysUntilSellBy(a.expiryMonth, a.expiryYear) - getDaysUntilSellBy(b.expiryMonth, b.expiryYear))
    .slice(0, 5);

  const allInactiveOnAllView =
    filterStatus === "all" &&
    sortedFiltered.length === 0 &&
    meds.length > 0 &&
    meds.every((m) => m.status === "item_expired" || m.status === "item_moved");

  const shellPadding =
    isMobile && isLandscapeCompact
      ? "max(0.7rem, env(safe-area-inset-top)) max(0.85rem, env(safe-area-inset-right)) max(0.7rem, env(safe-area-inset-bottom)) max(0.85rem, env(safe-area-inset-left))"
      : isMobile
        ? "max(1.1rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) max(1.1rem, env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))"
        : "max(1.5rem, env(safe-area-inset-top)) max(2.5rem, env(safe-area-inset-right)) max(1.5rem, env(safe-area-inset-bottom)) max(2.5rem, env(safe-area-inset-left))";
  const filterOptions = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "moved", label: "Recovered" },
    { value: "expired", label: "Write-offs" },
  ];
  const updateMedRef = useRef(updateMed);
  updateMedRef.current = updateMed;
  const setEditingMedRef = useRef(setEditingMed);
  setEditingMedRef.current = setEditingMed;
  const setFilterStatusRef = useRef(setFilterStatus);
  setFilterStatusRef.current = setFilterStatus;

  useImperativeHandle(
    ref,
    () => ({
      updateMed: (...args) => updateMedRef.current(...args),
      setEditingMed: (...args) => setEditingMedRef.current(...args),
      setFilterStatus: (...args) => setFilterStatusRef.current(...args),
    }),
    [],
  );

  return (
    <>
      <Suspense fallback={null}>
        <DashboardSearchParamsBridge
          setAddingNew={setAddingNew}
          setMainTab={setMainTab}
          router={router}
          mainTab={mainTab}
        />
      </Suspense>
      <div
        id="main"
        tabIndex={-1}
        style={{
          minHeight: "100vh",
          width: "100%",
          maxWidth: isMobile ? "920px" : "min(1600px, 100%)",
          margin: "0 auto",
          padding: shellPadding,
          fontFamily: ED.font,
          position: "relative",
          viewTransitionName: "ed-app-surface",
          boxSizing: "border-box",
          visibility: deskLaunchDone ? "visible" : "hidden",
        }}
      >
      {tourOpen && mainTab === "dashboard" && hasInventory && (
        <DashboardTourOverlay steps={DASHBOARD_TOUR_STEPS} onDismiss={() => void dismissTour()} />
      )}
      {editingMed && (
        <MedModal
          key={editingMed.id != null ? String(editingMed.id) : "edit"}
          med={editingMed}
          userId={userId}
          onSave={saveMed}
          onClose={() => setEditingMed(null)}
        />
      )}
      {addingNew && (
        <MedModal
          key="new"
          med={{}}
          userId={userId}
          onSave={saveMed}
          onClose={() => setAddingNew(false)}
        />
      )}

      {writeOffsOpen && (
        <WriteOffsDetailModal
          items={writeOffMeds}
          totalWaste={totalWaste}
          onClose={() => setWriteOffsOpen(false)}
          onEditLine={(m) => setEditingMed(m)}
          viewMode={dashboardView}
        />
      )}

      {recoveredOpen && (
        <RecoveredDetailModal
          items={recoveredMeds}
          totalRecovered={saved}
          onClose={() => setRecoveredOpen(false)}
          onEditLine={(m) => setEditingMed(m)}
          viewMode={dashboardView}
        />
      )}

      {atRiskOpen && (
        <AtRiskDetailModal
          items={atRiskMeds}
          totalAtRiskValue={atRisk}
          onClose={() => setAtRiskOpen(false)}
          onEditLine={(m) => setEditingMed(m)}
          viewMode={dashboardView}
        />
      )}

      {statusUndo && (
        <div
          className="ed-card-glass"
          style={{
            position: "fixed",
            left: "50%",
            bottom: isMobile ? "max(1rem, env(safe-area-inset-bottom))" : "1.5rem",
            transform: "translateX(-50%)",
            zIndex: 10040,
            maxWidth: "min(420px, calc(100vw - 2rem - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)))",
            width: "min(420px, calc(100vw - 2rem - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)))",
            padding: "0.65rem 0.85rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.75rem",
            borderRadius: ED.r16,
            border:
              statusUndo.kind === "expired"
                ? "1px solid rgba(239, 68, 68, 0.25)"
                : "1px solid rgba(16, 185, 129, 0.35)",
            background: statusUndo.kind === "expired" ? "rgba(255, 251, 251, 0.96)" : "rgba(236, 253, 245, 0.96)",
            boxShadow: "0 12px 40px rgba(15, 23, 42, 0.12)",
            fontFamily: ED.font,
          }}
          role="status"
        >
          <span style={{ fontSize: "0.8125rem", color: ED.text, lineHeight: 1.45 }}>
            <strong>{statusUndo.label}</strong>
            <span style={{ color: ED.textSecondary }}>
              {statusUndo.kind === "expired" ? " · Added to write-offs" : " · Added to recovered"}
            </span>
          </span>
          <button
            type="button"
            className="ed-btn-secondary"
            onClick={() => undoStatusBucket()}
            style={{
              flexShrink: 0,
              padding: "0.4rem 0.85rem",
              fontFamily: ED.font,
              fontSize: "0.8125rem",
              fontWeight: 600,
            }}
          >
            Undo
          </button>
        </div>
      )}

      {interactionHint ? (
        <div
          className="ed-card-glass"
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            left: `${Math.max(12, Math.min(interactionHint.x + 14, (typeof window !== "undefined" ? window.innerWidth : 900) - 230))}px`,
            top: `${Math.max(72, interactionHint.y + 10)}px`,
            zIndex: 10038,
            padding: "0.32rem 0.56rem",
            borderRadius: 999,
            border: "1px solid rgba(14, 165, 233, 0.28)",
            background: "rgba(248,250,252,0.94)",
            color: ED.textSecondary,
            fontSize: "0.68rem",
            fontWeight: 600,
            lineHeight: 1.25,
            pointerEvents: "none",
            boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
            transition: `opacity 0.16s ${ED.ease}`,
            backdropFilter: "blur(6px)",
          }}
        >
          {interactionHint.text}
        </div>
      ) : null}

      {saveError && (
        <div
          role="alert"
          className="ed-card-glass"
          style={{
            position: "fixed",
            left: "50%",
            top: isMobile ? "max(0.75rem, env(safe-area-inset-top))" : "1rem",
            transform: "translateX(-50%)",
            zIndex: 10045,
            maxWidth: "min(420px, calc(100vw - 2rem - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)))",
            padding: "0.55rem 0.85rem",
            borderRadius: ED.r12,
            border: isDarkMode ? "1px solid rgba(255, 77, 77, 0.28)" : "1px solid rgba(220, 38, 38, 0.28)",
            background: isDarkMode ? "rgba(255, 77, 77, 0.1)" : "rgba(255, 251, 251, 0.98)",
            color: isDarkMode ? "var(--ed-dark-critical)" : "#991b1b",
            fontSize: "0.8125rem",
            fontWeight: 500,
            fontFamily: ED.font,
            boxShadow: "0 8px 28px rgba(15, 23, 42, 0.1)",
          }}
        >
          {saveError}
        </div>
      )}

      <div ref={headerBarRef} className="ed-dashboard-sticky-shell">
      <header className="ed-dash-top-header">
        <div className="ed-dash-top-header__row">
          {mainTab === "help" || mainTab === "settings" ? (
            <button
              type="button"
              className="ed-desk-back-btn"
              aria-label="Go back"
              onClick={() => {
                withViewTransition(() => {
                  setMainTab("dashboard");
                  router.replace("/dashboard", { scroll: false });
                });
              }}
            >
              ← Back
            </button>
          ) : null}
          <div data-tour="welcome" className="ed-dash-top-header__brand" style={{ display: "flex", alignItems: "center", gap: "0.35rem", marginRight: "0.35rem", maxWidth: "none" }}>
            <h1 ref={deskNavLogoMountRef} className="ed-dash-top-header__logo">
              <ExpiryDeskWordmark
                variant="nav"
                showTagline={false}
                hourglassWithSand
                hourglassAnchorRef={navLaunchAnchorRef}
                href="/dashboard"
                onLinkClick={(e) => {
                  e.preventDefault();
                  if (mainTab !== "dashboard") {
                    withViewTransition(() => {
                      setMainTab("dashboard");
                      router.replace("/dashboard", { scroll: false });
                    });
                    return;
                  }
                  if (typeof window !== "undefined") window.location.reload();
                }}
              />
            </h1>
            <Link
              href={userId ? "/manage-subscription" : "/pricing"}
              className="ed-pro-badge"
              style={{
                marginLeft: "0.08rem",
                padding: "0.18rem 0.42rem",
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,0.35)",
                background: "rgba(248,250,252,0.82)",
                color: "#64748b",
                fontSize: "0.64rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                lineHeight: 1.1,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
              title="View plans and upgrade"
              aria-label="View plans and upgrade"
            >
              PRO
            </Link>
          </div>

          <nav className="ed-dash-top-header__nav" aria-label="Dashboard">
            <div className={dashDropdownClass(dashMenu === "add")} {...dashDropdownHoverProps}>
              <button
                type="button"
                className="ed-dash-top-header__search-icon-btn"
                aria-expanded={dashMenu === "add"}
                aria-haspopup="menu"
                aria-label="Add stock — line, handwritten sheet, or batch scan"
                title="Add stock"
                onClick={() => setDashMenu((m) => (m === "add" ? null : "add"))}
                data-haptic
              >
                <span className="ed-dash-top-header__add-glyph" aria-hidden>
                  +
                </span>
              </button>
              {dashMenu === "add" ? (
                <div
                  role="menu"
                  aria-label="Add stock options"
                  className="ed-dash-top-header__panel ed-dash-top-header__panel--right ed-dash-top-header__panel--add-stock"
                >
                  <div className="ed-dash-top-header__panel-heading">Add stock</div>
                  <Link
                    href="/dashboard?newLine=1"
                    role="menuitem"
                    className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked"
                    onClick={() => setDashMenu(null)}
                  >
                    <span className="ed-dash-top-header__panel-item-title">Add line</span>
                    <span className="ed-dash-top-header__panel-item-sub">One product typed in by hand</span>
                  </Link>
                  <button
                    type="button"
                    role="menuitem"
                    className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked"
                    onClick={() => {
                      setDashMenu(null);
                      onAddSheet();
                    }}
                  >
                    <span className="ed-dash-top-header__panel-item-title">Handwritten sheet / POS file</span>
                    <span className="ed-dash-top-header__panel-item-sub">Upload Excel, PDF, CSV, Word, or photo</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked"
                    onClick={() => {
                      setDashMenu(null);
                      onAddBatchScan();
                    }}
                  >
                    <span className="ed-dash-top-header__panel-item-title">Batch scan</span>
                    <span className="ed-dash-top-header__panel-item-sub">Camera-first walk the shelf</span>
                  </button>
                </div>
              ) : null}
            </div>
            <div className={dashDropdownClass(dashMenu === "notifications")} {...dashDropdownHoverProps}>
              <button
                type="button"
                className="ed-dash-top-header__icon-btn ed-dash-top-header__icon-btn--insight-centre"
                aria-expanded={dashMenu === "notifications"}
                aria-haspopup="dialog"
                aria-label={
                  deskNotificationCount > 0
                    ? (() => {
                        const n = deskNotificationCount;
                        const lineWord = n === 1 ? "line" : "lines";
                        const base = `Notifications — ${n} ${lineWord} need action on the desk (write-off, moved, or fix details)`;
                        if (urgentAttentionCount <= 0) return base;
                        const u = urgentAttentionCount;
                        const uw = u === 1 ? "is" : "are";
                        return `${base}. ${u} ${uw} overdue or in the urgent window.`;
                      })()
                    : "Notifications and dashboard insight centre — open panel"
                }
                title="Notifications and dashboard insight centre"
                onClick={() => setDashMenu((m) => (m === "notifications" ? null : "notifications"))}
              >
                <span className="ed-dash-top-header__bell" aria-hidden>
                  🔔
                </span>
                {deskNotificationCount > 0 ? (
                  <span className="ed-dash-top-header__badge">
                    {deskNotificationCount > 99 ? "99+" : deskNotificationCount}
                  </span>
                ) : null}
              </button>
              {dashMenu === "notifications" ? (
                <div
                  role="region"
                  aria-label="Desk notifications and actions"
                  className="ed-dash-top-header__panel ed-dash-top-header__panel--wide ed-dash-top-header__panel--right ed-dash-top-header__panel--insight-centre"
                >
                  <div className="ed-dash-top-header__panel-heading">Action queue</div>
                  <Link href="/notifications" className="ed-dash-top-header__panel-item" onClick={() => setDashMenu(null)}>
                    Open full page
                  </Link>
                  {deskNotificationQueue.length > 0 ? (
                    <div className="ed-dash-notif-queue">
                      {deskNotificationQueue.slice(0, 10).map(({ med, days, needsDate }) => {
                        const label = (med.name || "Line").trim() || "Line";
                        const dayLabel =
                          needsDate || days == null
                            ? "Set expiry date"
                            : days < 0
                              ? `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`
                              : days === 0
                                ? "Expires today"
                                : `${days} day${days === 1 ? "" : "s"} left`;
                        const dayTone = !needsDate && days != null && days < 0 ? "critical" : !needsDate && days != null && days <= 7 ? "soon" : "";
                        return (
                          <div key={String(med.id)} className="ed-dash-notif-row">
                            <div className="ed-dash-notif-row__meta">
                              <span className="ed-dash-notif-row__name">{label}</span>
                              <span
                                className={
                                  dayTone === "critical"
                                    ? "ed-dash-notif-row__days ed-dash-notif-row__days--critical"
                                    : dayTone === "soon"
                                      ? "ed-dash-notif-row__days ed-dash-notif-row__days--soon"
                                      : "ed-dash-notif-row__days"
                                }
                              >
                                {dayLabel}
                              </span>
                            </div>
                            <div className="ed-dash-notif-row__actions">
                              {!needsDate && days != null ? (
                                <>
                                  <button
                                    type="button"
                                    className="ed-dash-notif-row__btn ed-dash-notif-row__btn--moved"
                                    onClick={() => {
                                      void updateMed({ ...med, status: "item_moved" });
                                    }}
                                  >
                                    Moved / dispensed
                                  </button>
                                  <button
                                    type="button"
                                    className="ed-dash-notif-row__btn ed-dash-notif-row__btn--writeoff"
                                    onClick={() => {
                                      void updateMed({ ...med, status: "item_expired" });
                                    }}
                                  >
                                    Write-off
                                  </button>
                                </>
                              ) : null}
                              <button
                                type="button"
                                className="ed-dash-notif-row__btn ed-dash-notif-row__btn--edit"
                                onClick={() => {
                                  setEditingMed(med);
                                  setDashMenu(null);
                                }}
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  {deskNotificationQueue.length > 10 ? (
                    <p className="ed-dash-notif-cap">
                      Showing first 10 of {deskNotificationQueue.length}. Open the{" "}
                      <Link href="/notifications" onClick={() => setDashMenu(null)}>
                        notifications page
                      </Link>{" "}
                      for the full queue. Priority Pulse and expanded insights are coming in a future release.
                    </p>
                  ) : null}
                  {deskNotificationQueue.length === 0 ? (
                    hasInventory ? (
                      <p className="ed-dash-notif-empty">Action queue is clear. The badge stays off until a line needs a date fix, sits inside 90 days, or is overdue.</p>
                    ) : (
                      <p className="ed-dash-notif-empty">Add lines to your desk to see expiry actions here.</p>
                    )
                  ) : null}
                  <div className="ed-dash-top-header__panel-divider" aria-hidden />
                  <button
                    type="button"
                    className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--more-disclosure"
                    aria-expanded={notifMenuMoreExpanded}
                    onClick={() => setNotifMenuMoreExpanded((v) => !v)}
                  >
                    <span className="ed-dash-top-header__panel-item-title">
                      More <span aria-hidden>{notifMenuMoreExpanded ? "▴" : "▾"}</span>
                    </span>
                    <span className="ed-dash-top-header__panel-item-sub">
                      {notifMenuMoreExpanded
                        ? "Hide Priority Pulse & dashboard insights"
                        : "Show Priority Pulse & dashboard insights (coming soon)"}
                    </span>
                  </button>
                  {notifMenuMoreExpanded ? (
                    <>
                      <div
                        className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--coming-soon"
                        aria-disabled="true"
                        style={{ marginTop: "0.45rem" }}
                      >
                        <span className="ed-dash-top-header__panel-item-title">Priority Pulse</span>
                        <span className="ed-dash-top-header__panel-item-sub">Coming soon — stay tuned</span>
                      </div>
                      <div className="ed-dash-top-header__panel-heading">Dashboard insights</div>
                      <div
                        className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--coming-soon"
                        aria-disabled="true"
                      >
                        <span className="ed-dash-top-header__panel-item-title">Expiry risk overview</span>
                        <span className="ed-dash-top-header__panel-item-sub">Coming soon — stay tuned</span>
                      </div>
                      <div
                        className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--coming-soon"
                        aria-disabled="true"
                      >
                        <span className="ed-dash-top-header__panel-item-title">Inventory health &amp; intelligence</span>
                        <span className="ed-dash-top-header__panel-item-sub">Coming soon — stay tuned</span>
                      </div>
                      <div
                        className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--coming-soon"
                        aria-disabled="true"
                      >
                        <span className="ed-dash-top-header__panel-item-title">Inventory workspace</span>
                        <span className="ed-dash-top-header__panel-item-sub">Coming soon — stay tuned</span>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>

            {supabaseReady ? (
              <div className={dashDropdownClass(dashMenu === "profile")} {...dashDropdownHoverProps}>
                <button
                  type="button"
                  className={userId ? "ed-dash-top-header__avatar" : "ed-dash-top-header__nav-btn"}
                  data-tour="profile-menu-btn"
                  aria-expanded={dashMenu === "profile"}
                  aria-haspopup="true"
                  aria-label={userId ? "Account menu" : "Account and help menu"}
                  onClick={() => setDashMenu((m) => (m === "profile" ? null : "profile"))}
                >
                  {userId ? (
                    <>
                      <span className="ed-dash-top-header__avatar-circle" aria-hidden>
                        {(userEmail || "?").charAt(0).toUpperCase()}
                      </span>
                      <span aria-hidden>▾</span>
                    </>
                  ) : (
                    <>
                      Account <span aria-hidden>▾</span>
                    </>
                  )}
                </button>
                {dashMenu === "profile" ? (
                  <DashboardAccountMenuPanel
                    userEmail={userEmail}
                    hasSession={Boolean(userId)}
                    showManageSubscription={Boolean(supabaseReady && userId)}
                    onClose={() => setDashMenu(null)}
                    onLogout={() => onLogout()}
                  />
                ) : null}
              </div>
            ) : null}

            {mainTab === "dashboard" ? (
              <ThemeToggleIos
                dataTour="dashboard-theme-toggle"
                isDarkMode={isDarkMode}
                onToggle={() => setIsDarkMode((previous) => !previous)}
              />
            ) : null}
          </nav>
        </div>

        {isMobile ? (
          <div className="ed-dash-top-header__search ed-dash-top-header__search--mobile">
            <AppErrorBoundary context="dashboard-search">
              <DashboardSearchCombobox
                inputId="ed-dash-header-search-m"
                query={query}
                onQueryChange={setQuery}
                results={dashboardSearchCandidates}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                onSelectResult={(result) => {
                  executeDashboardSearchCandidate(result);
                }}
                onKeyDown={handleDashboardSearchInputKeyDown}
                onOpen={() => setIsSearchOpen(true)}
                onClose={closeDashboardSearch}
                isOpen={isSearchOpen}
                isSearching={isSearching}
                isDarkMode={isDarkMode}
                isMobile
                placeholder={SEARCH_PLACEHOLDER}
              />
            </AppErrorBoundary>
          </div>
        ) : null}
      </header>

      </div>

      {mainTab === "help" ? (
        <DashboardHowToUse isMobile={isMobile} onShowTourAgain={() => void showTourAgain()} />
      ) : mainTab === "settings" ? (
        <div className="ed-card-glass" style={{ padding: "1rem 1.15rem", borderRadius: ED.r16, marginBottom: "1rem" }}>
          {canUse("auditLogEnabled") ? (
            <>
              <h2 className="ed-dashboard-section-title">Compliance activity log</h2>
              <p style={{ margin: "0 0 0.75rem", fontSize: "0.8125rem", color: ED.textSecondary, lineHeight: 1.5 }}>
                Trace recent account events (uploads, edits, status updates, deletes, billing) for accountability and audit readiness. Requires the
                audit table — see{" "}
                <strong style={{ color: ED.text }}>scale_trust_extensions.sql</strong> in Supabase.
              </p>
              {auditLoading ? (
                <p style={{ color: ED.textMuted, fontSize: "0.875rem" }}>Loading…</p>
              ) : (
                <AuditLogPanel rows={auditRows || []} limit={100} />
              )}
            </>
          ) : null}

          <h2 className="ed-dashboard-section-title" style={{ marginTop: canUse("auditLogEnabled") ? "1.35rem" : 0 }}>
            Ops digest alerts
          </h2>
          <p style={{ margin: "0 0 0.65rem", fontSize: "0.8125rem", lineHeight: 1.5, color: ED.textSecondary }}>
            Operational summary only (not marketing). Sent Monday &amp; Thursday UTC when lines are within the 90-day expiry window.
          </p>
          <div style={{ display: "flex", gap: "0.55rem", marginBottom: "0.85rem", flexWrap: "wrap", alignItems: "center" }}>
            <input
              type="email"
              placeholder={userEmail || "your@email.com"}
              value={emailAddr}
              onChange={(e) => setEmailAddr(e.target.value)}
              style={{
                ...loginInputStyle,
                flex: 1,
                minWidth: "200px",
                padding: "0.65rem 1rem",
                minHeight: isMobile ? "44px" : undefined,
                fontSize: "0.875rem",
              }}
            />
            <button
              type="button"
              onClick={() => void subscribeEmailReminders()}
              disabled={emailSubLoading || !supabaseReady || !userId}
              className="ed-btn-primary"
              style={{
                padding: "0.6rem 1.15rem",
                minHeight: isMobile ? "44px" : undefined,
                fontFamily: ED.font,
                cursor: emailSubLoading || !supabaseReady || !userId ? "not-allowed" : "pointer",
                opacity: emailSubLoading || !supabaseReady || !userId ? 0.65 : 1,
              }}
            >
              {emailSubLoading ? "Saving…" : emailSubscribed ? "Update" : "Subscribe"}
            </button>
            <button
              type="button"
              onClick={() => setEmailPreview(!emailPreview)}
              className="ed-btn-ghost"
              style={{ padding: "0.6rem 1rem", minHeight: isMobile ? "44px" : undefined, fontFamily: ED.font, fontSize: "0.8125rem", fontWeight: 600 }}
            >
              {emailPreview ? "Hide sample" : "Preview sample"}
            </button>
          </div>
          {emailSubscribed && (
            <div style={{ color: "#047857", fontSize: "0.875rem", marginBottom: "0.75rem", lineHeight: 1.45, fontWeight: 500 }}>
              You’re subscribed — Monday and Thursday digests when you have near-expiry stock.
            </div>
          )}
          {emailSubError && (
            <div style={{ color: "#b91c1c", fontSize: "0.875rem", marginBottom: "0.75rem", lineHeight: 1.45 }}>
              {emailSubError}
            </div>
          )}
          {emailPreview && (
            <div
              style={{
                background: "rgba(240, 249, 255, 0.75)",
                border: "1px solid rgba(14, 165, 233, 0.18)",
                borderRadius: ED.r12,
                padding: "1rem 1rem",
                fontSize: "0.875rem",
                color: ED.textSecondary,
                lineHeight: 1.75,
                fontFamily: ED.font,
                marginBottom: "1rem",
              }}
              className="ed-fade-in"
            >
              <div style={{ color: ED.text, marginBottom: "0.3rem", fontSize: "0.9375rem", fontWeight: 700, letterSpacing: "-0.02em" }}>Digest preview</div>
              <div style={{ color: ED.textMuted, marginBottom: "1rem", fontSize: "0.8125rem" }}>Sample · {new Date().toLocaleDateString("en-ZA", { dateStyle: "medium" })}</div>
              <div style={{ color: isDarkMode ? "var(--ed-dark-warning)" : "#c2410c", marginBottom: "0.65rem", fontWeight: 600, fontSize: "0.8125rem" }}>Needs attention</div>
              {criticalItems.map((m, i) => {
                const daysToSellBy = getDaysUntilSellBy(m.expiryMonth, m.expiryYear);
                const val = m.quantity * (m.pricePerBox || 0);
                return (
                  <div
                    key={m.id}
                    style={{
                      marginBottom: "0.65rem",
                      paddingLeft: "0.85rem",
                      borderLeft: `3px solid ${
                        daysToSellBy <= CRITICAL_WINDOW_DAYS
                          ? (isDarkMode ? "var(--ed-dark-critical)" : "#f87171")
                          : (isDarkMode ? "var(--ed-dark-warning)" : "#fb923c")
                      }`,
                    }}
                  >
                    <span style={{ color: ED.text, fontWeight: 600 }}>
                      {i + 1}. {m.name} {m.dosage}
                    </span>
                    <span style={{ color: ED.textSecondary }}> — {m.quantity} {m.unit}</span>
                    <br />
                    <span
                      style={{
                        color: daysToSellBy <= CRITICAL_WINDOW_DAYS
                          ? (isDarkMode ? "var(--ed-dark-critical)" : "#dc2626")
                          : (isDarkMode ? "var(--ed-dark-warning)" : "#ea580c"),
                        fontSize: "0.8125rem",
                      }}
                    >
                      {daysToSellBy < 0 ? `${Math.abs(daysToSellBy)} days past sell-by` : `${daysToSellBy} days to sell-by`}
                    </span>
                    {val > 0 && <span style={{ color: ED.textMuted, fontSize: "0.8125rem" }}> · At risk {formatCurrency(val)}</span>}
                  </div>
                );
              })}
              <div style={{ marginTop: "1rem", color: ED.textMuted, fontSize: "0.8125rem" }}>
                Write-offs: <span style={{ color: isDarkMode ? "var(--ed-dark-critical)" : "#e11d48", fontWeight: 600 }}>{formatCurrency(totalWaste)}</span>
                {" · "}
                At risk: <span style={{ color: isDarkMode ? "var(--ed-dark-warning)" : "#ea580c", fontWeight: 600 }}>{formatCurrency(atRisk)}</span>
              </div>
            </div>
          )}

          <h2 className="ed-dashboard-section-title" style={{ marginTop: "1.35rem" }}>
            Safety controls
          </h2>
          <p style={{ margin: "0 0 0.65rem", fontSize: "0.8125rem", color: ED.textSecondary, lineHeight: 1.5 }}>
            Bulk clear moves all lines to <strong style={{ color: ED.text }}>Recently deleted</strong> for 30 days before final purge.
          </p>
          <button
            type="button"
            onClick={() => void onClearAllInventory()}
            className="ed-btn-ghost"
            style={{
              padding: "0.55rem 1rem",
              fontFamily: ED.font,
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: isDarkMode ? "var(--ed-dark-critical)" : "#b91c1c",
              border: isDarkMode ? "1px solid rgba(255, 77, 77, 0.35)" : "1px solid rgba(220, 38, 38, 0.35)",
              borderRadius: ED.r12,
              marginBottom: "1rem",
            }}
          >
            Move all inventory to Recently deleted…
          </button>

          <h2 id="ed-settings-trash-bin" className="ed-dashboard-section-title" style={{ marginTop: "0.5rem" }}>
            Recently deleted
          </h2>
          <p style={{ margin: "0 0 0.65rem", fontSize: "0.8125rem", color: ED.textSecondary, lineHeight: 1.5 }}>
            Lines stay recoverable for 30 days, then are removed permanently (optional purge cron).
          </p>
          {(deletedMeds || []).length === 0 ? (
            <p style={{ color: ED.textMuted, fontSize: "0.875rem" }}>Nothing in the bin.</p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {deletedMeds.map((dm) => (
                <li
                  key={String(dm.id)}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "0.65rem",
                    padding: "0.5rem 0",
                    borderBottom: "1px solid rgba(148, 163, 184, 0.18)",
                    fontSize: "0.8125rem",
                  }}
                >
                  <span style={{ color: ED.text, fontWeight: 600 }}>
                    {(dm.name || "Line").trim()}{" "}
                    <span style={{ color: ED.textSecondary, fontWeight: 500 }}>{dm.dosage}</span>
                  </span>
                  <button
                    type="button"
                    className="ed-btn-secondary"
                    style={{ padding: "0.35rem 0.75rem", fontFamily: ED.font, fontSize: "0.75rem", flexShrink: 0 }}
                    onClick={() => void restoreDeletedMed(dm)}
                  >
                    Restore
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
      {mainTab === "dashboard" ? <ConversionDebugPanel /> : null}
      {showWelcomeBanner && mainTab === "dashboard" && (
        <div
          role="status"
          style={{
            margin: "0.75rem 1rem 0",
            padding: "0.75rem 1rem",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
            border: "1px solid #6ee7b7",
            display: "flex",
            alignItems: "flex-start",
            gap: "0.65rem",
            fontSize: "0.875rem",
            lineHeight: 1.5,
            color: "#065f46",
          }}
        >
          <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: "0.05rem" }} aria-hidden>&#x2705;</span>
          <span style={{ flex: 1 }}>
            <strong>Account confirmed!</strong> You&rsquo;re all set. If your browser offers to save your password, tap <strong>Save</strong> &mdash; it makes logging in much faster next time.
          </span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setShowWelcomeBanner(false)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#059669",
              fontSize: "1.1rem",
              lineHeight: 1,
              padding: "0",
              flexShrink: 0,
            }}
          >
            &times;
          </button>
        </div>
      )}
      {!hasInventory ? (
        <div
          id={DASHBOARD_ATTENTION_FALLBACK_ID}
          className="ed-dashboard-empty ed-dashboard-empty--fill ed-dashboard-empty--surface ed-card-glass ed-fade-in"
          style={{
            width: "100%",
            minHeight: "calc(100dvh - 10.5rem)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <EmptyState
            illustration={EmptyStateIllustrations.EmptyInventory}
            title="No medications added yet"
            description="Add your first stock item to start tracking expiry dates, preventing waste, and optimizing your inventory management."
            actionText="+ Add inventory"
            actionOnClick={onAddSheet}
            style={{ maxWidth: "500px" }}
          />
          <div style={{ marginTop: "1.5rem", display: "flex", gap: "1rem", justifyContent: "center" }}>
            <button 
              type="button" 
              onClick={onAddBatchScan} 
              className="ed-dashboard-empty__cta-secondary" 
              style={{ 
                fontFamily: ED.font,
                padding: "0.625rem 1.25rem",
                borderRadius: ED.r12,
                border: "1.5px solid #cbd5e1",
                background: "transparent",
                color: "#475569",
                cursor: "pointer",
                transition: "all 150ms cubic-bezier(0.0, 0.0, 0.2, 1)"
              }}
              onMouseOver={(e) => {
                e.target.style.background = "#f8fafc";
                e.target.style.borderColor = "#2563eb";
                e.target.style.color = "#2563eb";
              }}
              onMouseOut={(e) => {
                e.target.style.background = "transparent";
                e.target.style.borderColor = "#cbd5e1";
                e.target.style.color = "#475569";
              }}
            >
              Walk the floor — batch scan
            </button>
          </div>
          <p
            style={{
              margin: "2rem 0 0",
              maxWidth: isMobile ? "100%" : "min(36rem, 94%)",
              color: "var(--ed-text-secondary)",
              fontSize: "0.8125rem",
              lineHeight: 1.55,
              fontWeight: 500,
              textAlign: "center",
              padding: "0 2rem"
            }}
          >
            Already have a stock export from your POS or pharmacy programme? On the same upload screen you can drop Excel, CSV, PDF, Word
            (.docx), or a clear JPEG/PNG — we use it for initial quantities, names, strengths, and schedule or shelf class when those
            columns exist.
          </p>
        </div>
      ) : (
        <>
      {hasInventory && mainTab === "dashboard" ? (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "0.8rem",
            flexWrap: isMobile ? "wrap" : "nowrap",
            marginBottom: "0.2rem",
          }}
        >
          <InventoryHealthDock meds={meds} profileKey={userId || null} />
          <div style={{ position: "relative", marginBottom: "0.45rem", marginLeft: "auto", width: isMobile ? "100%" : "auto" }}>
      <div data-tour="dashboard-view-mode" className="ed-segmented" style={{ marginBottom: 0, marginLeft: "auto" }}>
        <button
          type="button"
          onClick={() => withViewTransition(() => setDashboardView("finance"))}
          className={`ed-segmented__btn${dashboardView === "finance" ? " ed-segmented__btn--active" : ""}`}
        >
          <span className="ed-mode-badge" aria-hidden>
            R
          </span>
          Rands
        </button>
        <button
          type="button"
          onClick={() => withViewTransition(() => setDashboardView("stock"))}
          className={`ed-segmented__btn${dashboardView === "stock" ? " ed-segmented__btn--active" : ""}`}
        >
          <span className="ed-mode-badge" aria-hidden>
            #
          </span>
          No.&apos;s
        </button>
      </div>
      {viewModeTipOpen ? (
        <div className="ed-view-mode-tip" role="status" aria-live="polite">
          <div className="ed-view-mode-tip__title">View modes</div>
          <div>
            <strong style={{ color: ED.text }}>R · Rands</strong> shows value totals.{" "}
            <strong style={{ color: ED.text }}># · No.&apos;s</strong> focuses on bottles and boxes.
          </div>
          <button type="button" className="ed-view-mode-tip__close" onClick={closeViewModeTip}>
            Got it
          </button>
        </div>
      ) : null}
      </div>
        </div>
      ) : null}

      {showStatTiles ? (
        <div id="ed-dash-summary-anchor" className="ed-dash-summary-wrap ed-fade-in">
          <div data-tour="stats" className="ed-dash-summary-grid">
            {statTiles.map((k, idx) => {
              const variant = k.dashVariant || "atrisk";
              const cardClass = `ed-dash-summary-card ed-stat-tile ed-dash-summary-card--${variant} ed-card-glass`;
              const body = (
                <>
                  <span className="ed-stat-tile-kicker">{k.label}</span>
                  <div className="ed-stat-value" style={{ color: k.color }}>
                    <DashStatAnimatedValue
                      raw={k.statNumber}
                      mode={k.statFormat}
                      statsReveal={statsReveal}
                      delayMs={idx * 120}
                      durationMs={1200}
                      replayKey={deskLaunchReplay}
                    />
                  </div>
                  <div className="ed-stat-sub">{k.sub}</div>
                  <div
                    style={{
                      marginTop: "0.22rem",
                      minHeight: "0.95rem",
                      opacity: statHoverHint.key === k.label ? 0.92 : 0,
                      color: ED.textMuted,
                      fontSize: "0.66rem",
                      fontWeight: 600,
                      letterSpacing: "0.01em",
                      transition: `opacity 0.16s ${ED.ease}`,
                    }}
                  >
                    {statHoverHint.key === k.label ? statHoverHint.text : ""}
                  </div>
                  {k.historyLink ? <span className="ed-dash-summary-card__history">View history</span> : null}
                </>
              );
              if (k.statAction === "writeoffs") {
                return (
                  <button
                    type="button"
                    key={k.label}
                    className={cardClass}
                    onClick={() => setWriteOffsOpen(true)}
                    onMouseEnter={() =>
                      setStatHoverHint({
                        key: k.label,
                        text: pickStableMicrocopy("hero-writeoff", ["Remove immediately", "Non-compliant stock", "Action required now"], k.label),
                      })
                    }
                    onMouseLeave={() => setStatHoverHint({ key: "", text: "" })}
                    style={{
                      padding: "0.9rem 1rem",
                      textAlign: "left",
                      cursor: "pointer",
                      fontFamily: ED.font,
                      color: "inherit",
                    }}
                    data-haptic
                  >
                    {body}
                  </button>
                );
              }
              if (k.statAction === "recovered") {
                return (
                  <button
                    type="button"
                    key={k.label}
                    className={cardClass}
                    onClick={() => setRecoveredOpen(true)}
                    onMouseEnter={() =>
                      setStatHoverHint({
                        key: k.label,
                        text: pickStableMicrocopy("hero-recovered", ["Loss avoided", "Keep momentum", "Right on track"], k.label),
                      })
                    }
                    onMouseLeave={() => setStatHoverHint({ key: "", text: "" })}
                    style={{
                      padding: "0.9rem 1rem",
                      textAlign: "left",
                      cursor: "pointer",
                      fontFamily: ED.font,
                      color: "inherit",
                    }}
                    data-haptic
                  >
                    {body}
                  </button>
                );
              }
              if (k.statAction === "atrisk") {
                return (
                  <button
                    type="button"
                    key={k.label}
                    className={cardClass}
                    onClick={() => setAtRiskOpen(true)}
                    onMouseEnter={() =>
                      setStatHoverHint({
                        key: k.label,
                        text: pickStableMicrocopy("hero-atrisk", ["Sell soon", "Prioritize today", "Check occasionally"], k.label),
                      })
                    }
                    onMouseLeave={() => setStatHoverHint({ key: "", text: "" })}
                    style={{
                      padding: "0.9rem 1rem",
                      textAlign: "left",
                      cursor: "pointer",
                      fontFamily: ED.font,
                      color: "inherit",
                    }}
                    data-haptic
                  >
                    {body}
                  </button>
                );
              }
              return (
                <div
                  key={k.label}
                  className={cardClass}
                  onMouseEnter={() =>
                    setStatHoverHint({
                      key: k.label,
                      text: pickStableMicrocopy("hero-atrisk", ["Sell soon", "Prioritize today", "Check occasionally"], k.label),
                    })
                  }
                  onMouseLeave={() => setStatHoverHint({ key: "", text: "" })}
                  style={{ padding: "0.9rem 1rem" }}
                >
                  {body}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <>
      <div
        data-tour="filters"
        style={{
          display: "flex",
          gap: "0.9rem",
          marginTop: isMobile ? "1.2rem" : "1.9rem",
          marginBottom: "0.75rem",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem", minWidth: isMobile ? "100%" : "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", flexWrap: "wrap" }}>
            <h2 className="ed-dash-hero-title" style={{ marginBottom: 0 }}>Inventory lines</h2>
            {inventoryRefreshing ? (
              <span
                className="ed-card-glass"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.45rem",
                  borderRadius: ED.r999,
                  padding: "0.3rem 0.6rem",
                  border: "1px solid rgba(14, 165, 233, 0.22)",
                  color: ED.textSecondary,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                }}
                aria-live="polite"
              >
                <HourglassLoader size={18} label="Refreshing lines" />
                Syncing...
              </span>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", width: isMobile ? "100%" : "auto" }}>
            {filterOptions.map((f) => (
              <button type="button" key={f.value} onClick={() => setFilterStatus(f.value)} style={{
                padding: "0.45rem 0.85rem",
                background: filterStatus === f.value ? "rgba(14, 165, 233, 0.12)" : "rgba(255,255,255,0.5)",
                border: `1px solid ${filterStatus === f.value ? "rgba(14, 165, 233, 0.35)" : "rgba(148, 163, 184, 0.25)"}`,
                borderRadius: ED.r12,
                color: filterStatus === f.value ? ED.text : ED.textSecondary,
                minHeight: isMobile ? "44px" : undefined,
                cursor: "pointer",
                fontSize: "0.8125rem",
                fontWeight: 600,
                fontFamily: ED.font,
                transition: `all 0.2s ${ED.ease}`,
              }}>{f.label}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem", alignItems: "flex-end", marginLeft: "auto" }}>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{
            background: "rgba(255,255,255,0.9)",
            border: "1px solid rgba(14, 165, 233, 0.2)",
            borderRadius: ED.r12,
            color: ED.text,
            padding: "0.5rem 0.9rem",
            fontSize: "0.8125rem",
            fontWeight: 600,
            minHeight: isMobile ? "44px" : undefined,
            fontFamily: ED.font,
            cursor: "pointer",
            width: isMobile ? "100%" : "auto",
          }}>
            <option value="urgency">Sort: Urgency</option>
            {dashboardView === "finance" ? <option value="value">Sort: Line Value</option> : null}
            <option value="quantity">Sort: Quantity</option>
            <option value="name">Sort: Name</option>
          </select>
          {sortedFiltered.length > 0 ? (
            <div
              className="ed-ios-filter-bar ed-ios-filter-bar--risk-bands"
              role="group"
              aria-labelledby="ed-risk-band-label"
              aria-describedby="ed-risk-band-hint"
              style={{ marginLeft: "auto", alignItems: "flex-end", textAlign: "right" }}
            >
              <span id="ed-risk-band-label" className="ed-ios-filter-bar__label" />
              <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", width: "100%", alignItems: "flex-end" }}>
                <span id="ed-risk-band-hint" className="ed-ios-filter-bar__hint">
                  Tap a tag to hide or show a colour band.
                </span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.45rem", alignItems: "center", justifyContent: "flex-end" }}>
                  {[
                    { key: "red", label: "Critical", color: isDarkMode ? "var(--ed-dark-critical)" : "#dc2626" },
                    { key: "orange", label: "At Risk", color: isDarkMode ? "var(--ed-dark-warning)" : "#d97706" },
                    { key: "green", label: "Monitor / Safe", color: isDarkMode ? "var(--ed-dark-safe)" : "#059669" },
                  ].map((band) => {
                    const isOn = cardBandVisibility[band.key];
                    const n = colorBandStats[band.key];
                    const aria = isOn
                      ? `Hide ${band.label} lines from the board (${n} ${n === 1 ? "line" : "lines"}). Press again to show.`
                      : `Show ${band.label} lines on the board (${n} ${n === 1 ? "line" : "lines"}).`;
                    return (
                      <button
                        key={band.key}
                        type="button"
                        className={`ed-ios-filter-pill${isOn ? " ed-ios-filter-pill--on" : " ed-ios-filter-pill--band-off"}`}
                        onClick={() =>
                          setCardBandVisibility((prev) => ({
                            ...prev,
                            [band.key]: !prev[band.key],
                          }))
                        }
                        style={{
                          minHeight: isMobile ? "44px" : undefined,
                          borderColor: isOn ? `${band.color}66` : "rgba(148,163,184,0.28)",
                          color: isOn ? band.color : (isDarkMode ? "var(--ed-dark-text-muted)" : "#64748b"),
                          background: isOn ? `${band.color}14` : (isDarkMode ? "var(--ed-dark-bg-surface)" : "rgba(255,255,255,0.55)"),
                        }}
                        aria-pressed={isOn}
                        aria-label={aria}
                        title={isOn ? `${band.label}: visible — click to hide` : `${band.label}: hidden — click to show`}
                      >
                        <span className="ed-ios-filter-pill__label">{band.label}</span>
                        <span className="ed-ios-filter-pill__count" aria-hidden="true">
                          {n}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {hiddenBands.length > 0 ? (
                <button
                  type="button"
                  className="ed-ios-filter-reset"
                  onClick={() => setCardBandVisibility({ red: true, orange: true, green: true })}
                >
                  Hidden: {hiddenBands.join(", ")} · Show All
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ display: "flex", gap: "0.55rem", marginBottom: sortedFiltered.length > 0 ? "0.4rem" : "0.7rem", flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="search"
          placeholder="Search name or dose…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Filter lines by name"
          style={{
            ...loginInputStyle,
            flex: "1 1 220px",
            minWidth: "180px",
            padding: "0.55rem 0.85rem",
            fontSize: "0.8125rem",
            minHeight: isMobile ? "44px" : undefined,
          }}
        />
      </div>

      <div className="ed-dashboard-med-grid">
        {visibleSlice.map((med) => (
          <MedCard
            key={med.id}
            med={med}
            onUpdate={updateMed}
            onDelete={deleteMed}
            onEdit={setEditingMed}
            onInteractionHint={showInteractionHint}
            viewMode={dashboardView}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
          />
        ))}
      </div>

      {hasMoreLines && (
        <div
          ref={loadMoreSentinelRef}
          style={{ display: "flex", justifyContent: "center", padding: loadingMore ? "1rem" : "0.35rem", marginBottom: "1rem", minHeight: loadingMore ? "3rem" : "1px" }}
        >
          {loadingMore ? <HourglassLoader size={32} label="Loading more" /> : null}
        </div>
      )}

      {colorFiltered.length === 0 && (
        <div className="ed-card-glass ed-fade-in" style={{ textAlign: "center", padding: "1.65rem 1.25rem", marginBottom: "1.15rem" }}>
          {debouncedNameSearch.trim() && sorted.length > 0 ? (
            <p style={{ margin: 0, color: ED.textSecondary, fontSize: "0.9375rem", lineHeight: 1.55, fontWeight: 500, maxWidth: "28rem", marginLeft: "auto", marginRight: "auto" }}>
              No lines match your search. Clear the search box or adjust filters.
            </p>
          ) : sortedFiltered.length > 0 ? (
            <p style={{ margin: 0, color: ED.textSecondary, fontSize: "0.9375rem", lineHeight: 1.55, fontWeight: 500 }}>
              No lines visible in the selected risk groups. Click the dashed tags above to show Critical, At Risk, or Monitor / Safe lines again.
            </p>
          ) : allInactiveOnAllView ? (
            <p style={{ margin: 0, color: ED.textSecondary, fontSize: "0.9375rem", lineHeight: 1.55, fontWeight: 500, maxWidth: "28rem", marginLeft: "auto", marginRight: "auto" }}>
              Active stock is clear — written-off lines roll up under <strong style={{ color: ED.text }}>Write-offs</strong>, moved lines under <strong style={{ color: ED.text }}>Recovered</strong>. Use the <strong style={{ color: ED.text }}>Write-offs</strong> or <strong style={{ color: ED.text }}>Recovered</strong> filters to review those lines.
            </p>
          ) : (
            <p style={{ margin: 0, color: ED.textSecondary, fontSize: "0.9375rem", lineHeight: 1.55, fontWeight: 500 }}>
              No lines match this filter. Try <strong style={{ color: ED.text }}>All</strong> or add a line.
            </p>
          )}
        </div>
      )}
      </>
    </>
  )}
    </>
  )}
    </div>
      {!deskLaunchDone ? (
        <DeskLaunchSequence
          key={deskLaunchReplay}
          replayKey={deskLaunchReplay}
          isDarkMode={isDarkMode}
          navHourglassAnchorRef={navLaunchAnchorRef}
          navLogoMountRef={deskNavLogoMountRef}
          dataReady={inventoryLoaded}
          onComplete={() => setDeskLaunchDone(true)}
        />
      ) : null}
    </>
  );
});

Dashboard.displayName = "Dashboard";

function LoginView({ linkError = "", onDismissLinkError = () => {} }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordEditable, setPasswordEditable] = useState(false);
  const [awaitingVerify, setAwaitingVerify] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState(/** @type {"signIn" | "forgot"} */ ("signIn"));

  function formatResetRequestError(err) {
    const status = typeof err === "object" && err !== null && "status" in err ? Number(err.status) : NaN;
    const raw = err instanceof Error ? err.message : String(err || "");
    const text = raw.toLowerCase();
    if (status === 429 || text.includes("email rate limit exceeded") || text.includes("rate limit")) {
      return "Too many attempts — please wait 60 minutes before trying again.";
    }
    return humanizeNetworkOrUnknown(err) || "Could not send reset email.";
  }

  async function resendConfirm() {
    setError("");
    if (linkError) onDismissLinkError();
    if (!supabase || !email.trim()) return;
    setLoading(true);
    try {
      const { error: e } = await supabase.auth.resend({
        type: "signup",
        email: email.trim(),
        options: { emailRedirectTo: getAuthRedirectTo() },
      });
      if (e) throw e;
    } catch (err) {
      setError(humanizeNetworkOrUnknown(err));
    } finally {
      setLoading(false);
    }
  }

  async function submitSignIn(e) {
    e.preventDefault();
    setError("");
    if (linkError) onDismissLinkError();
    if (!supabase) {
      setError("Sign-in isn't available right now. Please try again in a moment.");
      return;
    }
    if (!email.trim() || !password) {
      setError("Enter email and password.");
      return;
    }
    setLoading(true);
    try {
      const { error: signErr } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (signErr) throw signErr;
      setPassword("");
    } catch (err) {
      let msg = humanizeNetworkOrUnknown(err);
      if (/email not confirmed|verify/i.test(msg)) {
        msg = "Confirm your email — we sent a link when you signed up.";
        setAwaitingVerify(true);
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function sendReset() {
    setError("");
    if (linkError) onDismissLinkError();
    setResetSent(false);
    if (!supabase || !email.trim()) {
      setError("Enter the email for your account.");
      return;
    }
    setLoading(true);
    try {
      const { error: e } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: getPasswordResetRedirectTo(),
      });
      if (e) throw e;
      setResetSent(true);
    } catch (err) {
      setError(formatResetRequestError(err));
    } finally {
      setLoading(false);
    }
  }

  const authFieldGap = "0.65rem";
  const combinedError = linkError || error;
  const unlockPasswordField = () => {
    if (!passwordEditable) setPasswordEditable(true);
  };

  return (
    <main
      id="main"
      tabIndex={-1}
      className="ed-auth-screen"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: ED.font,
        background: "linear-gradient(165deg, #ffffff 0%, #f0f7ff 45%, #e8f4fc 100%)",
        backgroundAttachment: "fixed",
        outline: "none",
      }}
    >
      <div className="ed-card-glass ed-auth-card ed-fade-in" style={{ width: "100%", maxWidth: "400px", padding: "2rem 1.75rem", borderRadius: ED.r16 }}>
        <div style={{ marginBottom: "0.85rem" }}>
          <DeskBackButton fallbackHref="/" />
        </div>
        <header style={{ textAlign: "center", marginBottom: "1.1rem" }}>
          <AuthBrandHeader>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <ExpiryDeskWordmark variant="auth" showTagline={false} href="/" />
            </div>
          </AuthBrandHeader>
          <p style={{ margin: "0.35rem 0 0", fontSize: "0.875rem", fontWeight: 500, color: ED.textSecondary, lineHeight: 1.45 }}>See what’s at risk before it becomes a write-off.</p>
        </header>

        {!supabase && (
          <div
            role="alert"
            style={{
              marginBottom: "1rem",
              padding: "0.75rem 0.9rem",
              borderRadius: ED.r12,
              background: "rgba(254, 242, 242, 0.95)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              color: "#b91c1c",
              fontSize: "0.8125rem",
              lineHeight: 1.45,
            }}
          >
            Sign-in isn’t configured for this deployment — check your Supabase URL and anon key.
          </div>
        )}

        <h1 style={{ fontSize: "1.125rem", fontWeight: 700, margin: "0 0 1rem", color: ED.text, textAlign: "center", letterSpacing: "-0.02em" }}>
          {authMode === "forgot" ? "Reset password" : "Sign in"}
        </h1>

        {authMode === "signIn" && (
          <>
            <AuthProviderButtons onError={setError} disabled={loading} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.65rem",
                margin: "1.1rem 0 1rem",
              }}
            >
              <div style={{ flex: 1, height: "1px", background: "rgba(100, 116, 139, 0.25)" }} />
              <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: ED.textMuted, whiteSpace: "nowrap" }}>or with email</span>
              <div style={{ flex: 1, height: "1px", background: "rgba(100, 116, 139, 0.25)" }} />
            </div>
          </>
        )}

        {authMode === "forgot" && (
          <p style={{ margin: "0 0 1rem", fontSize: "0.875rem", lineHeight: 1.5, color: ED.textSecondary, textAlign: "center" }}>
            Enter your work email — we’ll send a link to set a new password. The link opens ExpiryDesk; choose a password and you’re back in.
          </p>
        )}

        {awaitingVerify && authMode === "signIn" && (
          <div style={{ background: "rgba(240, 253, 244, 0.92)", border: "1px solid rgba(34, 197, 94, 0.3)", borderRadius: ED.r12, padding: "0.85rem 1rem", marginBottom: "1rem", color: "#166534", fontSize: "0.8125rem", lineHeight: 1.45 }}>
            Check your inbox for a confirmation link, then log in.
            <button
              type="button"
              onClick={() => void resendConfirm()}
              disabled={loading}
              style={{ display: "block", marginTop: "0.55rem", background: "none", border: "none", color: "#15803d", textDecoration: "underline", cursor: loading ? "not-allowed" : "pointer", fontSize: "0.8125rem", padding: 0, fontFamily: ED.font, fontWeight: 600 }}
            >
              Resend email
            </button>
          </div>
        )}

        {resetSent && (
          <div style={{ background: "rgba(239, 246, 255, 0.95)", border: "1px solid rgba(59, 130, 246, 0.3)", borderRadius: ED.r12, padding: "0.85rem 1rem", marginBottom: "1rem", color: "#1e40af", fontSize: "0.8125rem", lineHeight: 1.45 }}>
            If an account exists for that email, we sent reset instructions. Open the link on this device, then set your new password here.
          </div>
        )}

        {authMode === "signIn" ? (
        <form onSubmit={submitSignIn}>
          <input
            className="ed-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Work email"
            type="email"
            autoComplete="email"
            name="email"
            aria-label="Work email"
            style={{ marginBottom: authFieldGap }}
          />
          <PasswordVisibilityField
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={unlockPasswordField}
            onPointerDown={unlockPasswordField}
            onMouseDown={unlockPasswordField}
            onTouchStart={unlockPasswordField}
            onKeyDown={unlockPasswordField}
            autoComplete="current-password"
            name="password"
            placeholder="Password"
            ariaLabel="Password"
            readOnly={!passwordEditable}
            disabled={loading}
          />

          <button
            type="submit"
            disabled={loading || !supabase}
            className="ed-btn-auth-solid"
            style={{
              marginTop: "1.15rem",
              width: "100%",
              padding: "0.9rem 1rem",
              minHeight: "48px",
              fontFamily: ED.font,
              cursor: loading || !supabase ? "not-allowed" : "pointer",
              opacity: !supabase ? 0.55 : 1,
            }}
          >
            {loading ? (
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.45rem" }}>
                <HourglassLoader size={16} label="Signing in" />
                Please wait…
              </span>
            ) : (
              "Sign in"
            )}
          </button>
        </form>
        ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void sendReset();
          }}
        >
          <input
            className="ed-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Work email"
            type="email"
            autoComplete="email"
            aria-label="Work email for password reset"
            style={{ marginBottom: authFieldGap }}
          />
          <button
            type="submit"
            disabled={loading || !supabase}
            className="ed-btn-auth-solid"
            style={{
              marginTop: "0.25rem",
              width: "100%",
              padding: "0.9rem 1rem",
              minHeight: "48px",
              fontFamily: ED.font,
              cursor: loading || !supabase ? "not-allowed" : "pointer",
              opacity: !supabase ? 0.55 : 1,
            }}
          >
            {loading ? (
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.45rem" }}>
                <HourglassLoader size={16} label="Sending reset link" />
                Please wait…
              </span>
            ) : (
              "Send reset link"
            )}
          </button>
        </form>
        )}

        {authMode === "signIn" ? (
        <button
          type="button"
          onClick={() => {
            setAuthMode("forgot");
            setError("");
            setPassword("");
            setPasswordEditable(false);
            if (linkError) onDismissLinkError();
            setResetSent(false);
          }}
          disabled={loading || !supabase}
          style={{
            display: "block",
            margin: "0.85rem auto 0",
            background: "none",
            border: "none",
            color: ED.textSecondary,
            cursor: loading ? "not-allowed" : "pointer",
            fontFamily: ED.font,
            fontSize: "0.8125rem",
            fontWeight: 500,
            textDecoration: "underline",
            textUnderlineOffset: "2px",
          }}
        >
          Forgot password?
        </button>
        ) : (
        <button
          type="button"
          onClick={() => {
            setAuthMode("signIn");
            setError("");
            setResetSent(false);
            setPassword("");
            setPasswordEditable(false);
          }}
          disabled={loading}
          style={{
            display: "block",
            margin: "0.85rem auto 0",
            background: "none",
            border: "none",
            color: ED.textSecondary,
            cursor: loading ? "not-allowed" : "pointer",
            fontFamily: ED.font,
            fontSize: "0.8125rem",
            fontWeight: 500,
            textDecoration: "underline",
            textUnderlineOffset: "2px",
          }}
        >
          Back to sign in
        </button>
        )}

        {authMode === "signIn" && (
        <p style={{ margin: "1.15rem 0 0", textAlign: "center", fontSize: "0.875rem", color: ED.textSecondary }}>
          First time here?{" "}
          <Link href="/signup" style={{ color: "#0284c7", fontWeight: 600, textDecoration: "none" }}>
            Create an account
          </Link>
        </p>
        )}

        {combinedError && (
          <div style={{ marginTop: "1rem", color: "#b91c1c", fontSize: "0.875rem", lineHeight: 1.45, textAlign: "center" }} role="alert">
            {combinedError}
            {linkError ? (
              <button
                type="button"
                onClick={() => onDismissLinkError()}
                style={{
                  display: "block",
                  margin: "0.65rem auto 0",
                  background: "none",
                  border: "none",
                  color: "#0284c7",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: ED.font,
                  fontSize: "0.8125rem",
                  textDecoration: "underline",
                }}
              >
                Dismiss
              </button>
            ) : null}
          </div>
        )}
      </div>
    </main>
  );
}

function PasswordRecoveryView({ onCompleted }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const pwCheck = signupPasswordChecks(pw);
  const newPwPolicyInvalid = pw.length > 0 && validatePasswordRules(pw) !== null;

  async function submit(e) {
    e.preventDefault();
    setError("");
    const msg = validatePasswordRules(pw);
    if (msg) {
      setError(msg);
      return;
    }
    if (pw !== pw2) {
      setError("Passwords do not match.");
      return;
    }
    if (!supabase) {
      setError("Sign-in isn't available right now. Please try again in a moment.");
      return;
    }
    setLoading(true);
    try {
      const { error: u } = await supabase.auth.updateUser({ password: pw });
      if (u) throw u;
      setPw("");
      setPw2("");
      onCompleted();
    } catch (err) {
      setError(humanizeNetworkOrUnknown(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <main
      id="main"
      tabIndex={-1}
      className="ed-auth-screen"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: ED.font,
        background: "linear-gradient(165deg, #ffffff 0%, #f0f7ff 45%, #e8f4fc 100%)",
        backgroundAttachment: "fixed",
        outline: "none",
      }}
    >
      <div className="ed-card-glass ed-auth-card ed-fade-in" style={{ width: "100%", maxWidth: "400px", padding: "2rem 1.75rem", borderRadius: ED.r16 }}>
        <div style={{ marginBottom: "0.85rem" }}>
          <DeskBackButton fallbackHref="/" />
        </div>
        <header style={{ textAlign: "center", marginBottom: "1.1rem" }}>
          <AuthBrandHeader>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <ExpiryDeskWordmark variant="auth" showTagline={false} href="/" />
            </div>
          </AuthBrandHeader>
          <p style={{ margin: "0.35rem 0 0", fontSize: "0.875rem", fontWeight: 500, color: ED.textSecondary, lineHeight: 1.45 }}>See what’s at risk before it becomes a write-off.</p>
        </header>
        <h1 style={{ fontSize: "1.125rem", fontWeight: 700, margin: "0 0 0.5rem", color: ED.text, letterSpacing: "-0.02em", textAlign: "center" }}>Choose a new password</h1>
        <p style={{ color: ED.textMuted, fontSize: "0.8125rem", margin: "0 0 0.65rem", lineHeight: 1.45, textAlign: "center", fontWeight: 600 }}>
          Use at least 8 characters, 1 number, and 1 special character (!, @, #, …).
        </p>
        <ul
          style={{
            margin: "0 0 1.15rem",
            paddingLeft: "1.15rem",
            fontSize: "0.8125rem",
            color: ED.textMuted,
            lineHeight: 1.5,
            listStyle: "disc",
          }}
        >
          <li style={{ color: pwCheck.len ? "#15803d" : ED.textMuted }}>At least 8 characters</li>
          <li style={{ color: pwCheck.num ? "#15803d" : ED.textMuted }}>One number</li>
          <li style={{ color: pwCheck.sym ? "#15803d" : ED.textMuted }}>One symbol (!, @, #, …)</li>
        </ul>
        <form onSubmit={submit}>
          <PasswordVisibilityField
            value={pw}
            onChange={(e) => {
              setPw(e.target.value);
              setError("");
            }}
            placeholder="New password"
            autoComplete="new-password"
            ariaLabel="New password"
            disabled={loading}
            invalid={newPwPolicyInvalid}
            inputStyle={{ marginBottom: "0.65rem" }}
          />
          <PasswordVisibilityField
            value={pw2}
            onChange={(e) => {
              setPw2(e.target.value);
              setError("");
            }}
            placeholder="Confirm password"
            autoComplete="new-password"
            ariaLabel="Confirm password"
            disabled={loading}
            invalid={pw2.length > 0 && pw !== pw2}
          />
          <button
            type="submit"
            disabled={loading || !!validatePasswordRules(pw) || pw !== pw2}
            className="ed-btn-auth-solid"
            style={{
              marginTop: "1.15rem",
              width: "100%",
              padding: "0.9rem 1rem",
              minHeight: "48px",
              fontFamily: ED.font,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Updating…" : "Save & continue"}
          </button>
        </form>
        {error && (
          <div style={{ marginTop: "1rem", color: "#b91c1c", fontSize: "0.875rem", lineHeight: 1.45, textAlign: "center" }} role="alert">
            {error}
          </div>
        )}
      </div>
    </main>
  );
}

/**
 * @param {{ initialSession?: import("@supabase/supabase-js").Session | null; children?: import("react").ReactNode }} props
 * `initialSession` comes from the server (cookie-based SSR) so the desk shell avoids an auth spinner on first paint.
 */
export default function App({ initialSession = null, children = null }) {
  const pathname = usePathname();
  const router = useRouter();
  /** True on last commit while URL is `/dashboard` — read synchronously in `popstate` before React rerenders. */
  const deskRouteRef = useRef(false);
  useEffect(() => {
    deskRouteRef.current = pathname === "/dashboard" || pathname === "/notifications";
  }, [pathname]);
  const isUploadRoute = pathname === "/upload";
  const isManageSubscriptionRoute = pathname === "/manage-subscription";
  const isInsightsRoute = pathname === "/insights";
  const isPriorityPulseRoute = pathname === "/priority-pulse";
  const isNotificationsRoute = pathname === "/notifications";
  const isDeskAuxRoute = isManageSubscriptionRoute || isInsightsRoute || isPriorityPulseRoute;

  /** Exposes `Dashboard` line actions while `/notifications` is visible (dashboard stays mounted, hidden). */
  const dashboardHandleRef = useRef(/** @type {import("@/components/DeskNotificationsPage").DeskDashboardHandle | null} */ (null));

  const [meds, setMeds] = useState(null);
  const [deletedMeds, setDeletedMeds] = useState([]);
  const [batchScanOpen, setBatchScanOpen] = useState(false);
  /** True while batch has a draft or any lines confirmed this session (for tab-close warning). */
  const [batchScanRiskyExit, setBatchScanRiskyExit] = useState(false);
  /** Snapshot from device recovery store — show resume modal before mounting scanner. */
  const [batchRecoveryGate, setBatchRecoveryGate] = useState(/** @type {import("@/lib/batchScanRecovery").BatchScanRecoveryV1 | null} */ (null));
  /** Passed into BatchScanView once to hydrate UI (cleared after apply). */
  const [batchRecoveryPayload, setBatchRecoveryPayload] = useState(/** @type {import("@/lib/batchScanRecovery").BatchScanRecoveryV1 | null} */ (null));
  /** False until we have read recovery storage for resume (avoids a one-frame scanner flash). */
  const [batchRecoveryChecked, setBatchRecoveryChecked] = useState(false);
  const [inventoryLoaded, setInventoryLoaded] = useState(false);
  const [inventoryRefreshing, setInventoryRefreshing] = useState(false);
  /** Set when the active-inventory fetch fails; never masquerade as an empty shelf. */
  const [inventoryLoadError, setInventoryLoadError] = useState("");
  const [viewportLayout, setViewportLayout] = useState({ width: 1280, height: 800 });
  const [billingNotice, setBillingNotice] = useState("");
  /** Inline banner when sheet lines parsed but Supabase insert failed (replaces alert). */
  const [sheetCommitError, setSheetCommitError] = useState("");
  /** Batch lines queued after failed insert — background flush retries. */
  const [batchPendingSyncCount, setBatchPendingSyncCount] = useState(0);
  /** User-visible error when the offline queue could not accept a row (storage full or cap). */
  const [batchQueueNotice, setBatchQueueNotice] = useState("");
  const [authLinkError, setAuthLinkError] = useState("");

  const [session, setSession] = useState(initialSession);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [paidPlan, setPaidPlan] = useState("free");
  const [billingStatus, setBillingStatus] = useState(null);
  const marketingSyncRef = useRef({ userId: null, plan: null });
  /** Prevents duplicate `updateUser` when backfilling `terms_accepted_at` for OAuth signups. */
  const oauthTermsSyncedRef = useRef(/** @type {string | null} */ (null));
  const landingConversionSentRef = useRef(false);
  /** Monotonic id so overlapping `reloadInventory` runs cannot apply stale results after a newer run started. */
  const inventoryReloadGenRef = useRef(0);
  /** Coalesce rapid Realtime bursts into one fetch (avoids UI jank / main-thread pile-up). */
  const inventoryRealtimeDebounceRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  /** Track inventoryLoaded state without circular dependency */
  const inventoryLoadedRef = useRef(false);

  const supabaseReady = useMemo(() => Boolean(supabase), []);

  useEffect(() => {
    inventoryLoadedRef.current = inventoryLoaded;
  }, [inventoryLoaded]);

  useEffect(() => {
    function updateViewport() {
      if (typeof window === "undefined") return;
      const vv = window.visualViewport;
      const width = Math.round(vv?.width ?? window.innerWidth);
      const height = Math.round(vv?.height ?? window.innerHeight);
      setViewportLayout((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    }
    updateViewport();
    window.addEventListener("resize", updateViewport, { passive: true });
    window.addEventListener("orientationchange", updateViewport, { passive: true });
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    vv?.addEventListener("resize", updateViewport, { passive: true });
    vv?.addEventListener("scroll", updateViewport, { passive: true });
    return () => {
      window.removeEventListener("resize", updateViewport);
      window.removeEventListener("orientationchange", updateViewport);
      vv?.removeEventListener("resize", updateViewport);
      vv?.removeEventListener("scroll", updateViewport);
    };
  }, []);

  const prevPathnameRef = useRef(null);
  useEffect(() => {
    const prev = prevPathnameRef.current;
    if (prev === "/upload" && pathname !== "/upload") {
      setBatchScanOpen(false);
    }
    prevPathnameRef.current = pathname;
  }, [pathname]);

  const onBatchExitRiskChange = useCallback((risky) => {
    setBatchScanRiskyExit(Boolean(risky));
  }, []);

  const onBatchRecoveryConsumed = useCallback(() => {
    setBatchRecoveryPayload(null);
  }, []);

  useEffect(() => {
    if (!batchScanOpen) {
      setBatchScanRiskyExit(false);
      setBatchRecoveryPayload(null);
      setBatchRecoveryGate(null);
      setBatchRecoveryChecked(false);
      return;
    }
    const uid = session?.user?.id;
    if (!uid) {
      setBatchRecoveryGate(null);
      setBatchRecoveryChecked(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await loadBatchScanRecovery(uid);
        if (cancelled) return;
        if (
          r &&
          (r.confirmedCount > 0 ||
            batchScanRecoveryHasResumableDraft(r) ||
            (r.sessionLog?.length ?? 0) > 0 ||
            (r.outboundPending?.length ?? 0) > 0)
        ) {
          setBatchRecoveryGate(r);
          setBatchRecoveryPayload(null);
        } else {
          setBatchRecoveryGate(null);
        }
      } catch {
        if (!cancelled) setBatchRecoveryGate(null);
      } finally {
        if (!cancelled) setBatchRecoveryChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [batchScanOpen, session?.user?.id]);

  useEffect(() => {
    if (!batchScanOpen || !batchScanRiskyExit) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [batchScanOpen, batchScanRiskyExit]);

  useEffect(() => {
    if (!session?.user?.id) {
      setBatchPendingSyncCount(0);
      return;
    }
    return subscribeBatchPendingCount(setBatchPendingSyncCount);
  }, [session?.user?.id]);

  useEffect(() => {
    if (!supabaseReady || !session?.user?.id || !supabase) return;
    const client = supabase;
    const run = () => {
      void flushBatchScanPendingQueue(client, (data, clientMedId) => {
        const mapped = mapMedicationRow(data);
        setMeds((prev) => {
          const rest = (prev || []).filter((m) => String(m.id) !== String(clientMedId));
          if (rest.some((m) => sameMedId(m.id, mapped.id))) return rest;
          return [...rest, mapped];
        });
      });
    };
    void run();
    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") run();
    };
    window.addEventListener("online", run);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", run);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [supabaseReady, session?.user?.id]);

  /** Warm the upload route + RSC payload while on the dashboard so "Add sheet" feels instant on mobile. */
  useEffect(() => {
    if (!session?.user?.id || passwordRecovery) return;
    if (pathname !== "/dashboard" && pathname !== "/notifications") return;
    try {
      router.prefetch("/upload");
    } catch {
      /* ignore */
    }
  }, [session?.user?.id, pathname, passwordRecovery, router]);

  /**
   * Browser "Back" from `/dashboard`: confirm leaving (may sign out / open the previous page).
   * OK — stay on wherever history navigated (e.g. landing). Cancel — return to the desk and refresh.
   */
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!session?.user?.id || passwordRecovery) return;

    const onPopState = () => {
      const wasDashboard = deskRouteRef.current;
      if (typeof window !== "undefined" && window.location.pathname === "/dashboard") return;
      if (!wasDashboard) return;

      queueMicrotask(() => {
        const leave = window.confirm(
          "Leave the ExpiryDesk dashboard? Your browser is going back to the previous page — you may be signed out.\n\nClick OK to continue, or Cancel to stay on the desk."
        );
        if (leave) return;
        router.replace("/dashboard");
        window.location.reload();
      });
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [session?.user?.id, passwordRecovery, router]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const billing = (url.searchParams.get("billing") || "").toLowerCase();
    if (billing === "success") {
      setBillingNotice("Payment received. Premium is on the way — refresh if your badge hasn't caught up.");
    } else if (billing === "cancelled") {
      setBillingNotice("Checkout closed. You're still on your current plan — upgrade anytime.");
    }
    if (billing) {
      url.searchParams.delete("billing");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  /** Supabase puts errors in the hash or query when email links fail (expired OTP, redirect mismatch, etc.). */
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const href = window.location.href;
      const hashParams = new URLSearchParams(window.location.hash.slice(1) || "");
      const pageUrl = new URL(href);
      const qErr = pageUrl.searchParams.get("error");
      const qDesc = pageUrl.searchParams.get("error_description");
      const hErr = hashParams.get("error");
      const hDesc = hashParams.get("error_description");
      const err = qErr || hErr;
      const desc = qDesc || hDesc;
      if (!err && !desc) return;
      let msg = desc || err || "Something went wrong with this sign-in link.";
      try {
        msg = decodeURIComponent(String(msg).replace(/\+/g, " "));
      } catch {
        /* keep raw */
      }
      if (/expired|invalid|otp|already been used/i.test(msg)) {
        msg =
          "This link has expired or was already used. Sign in below, or request a new confirmation or reset email.";
      }
      if (err === "auth_callback" || err === "sign_in_failed") {
        msg =
          desc ||
          "Sign-in didn't complete. Try Google or email again — and confirm this site is listed in Supabase redirect URLs.";
      }
      setAuthLinkError(msg);
      const clean = new URL(href);
      clean.hash = "";
      clean.searchParams.delete("error");
      clean.searchParams.delete("error_description");
      clean.searchParams.delete("error_code");
      window.history.replaceState({}, "", `${clean.pathname}${clean.search}${clean.hash}`);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !session?.user?.id) return;
    const url = new URL(window.location.href);
    const hadHashAuth =
      window.location.hash &&
      (window.location.hash.includes("access_token") ||
        window.location.hash.includes("refresh_token") ||
        window.location.hash.includes("error"));
    const hadPkceCode = url.searchParams.has("code");
    if (!hadHashAuth && !hadPkceCode) return;
    url.hash = "";
    url.searchParams.delete("code");
    const q = url.searchParams.toString();
    window.history.replaceState({}, "", `${url.pathname}${q ? `?${q}` : ""}`);
  }, [session?.user?.id]);

  useEffect(() => {
    let mounted = true;

    if (!supabaseReady) {
      setSession(null);
      setMeds(null);
      return undefined;
    }

    const recoveryFromUrl =
      typeof window !== "undefined" &&
      /type=recovery/.test(`${window.location.hash || ""}${window.location.search || ""}`);

    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === "PASSWORD_RECOVERY") {
        setPasswordRecovery(true);
        setSession(newSession);
        setMeds(null);
        return;
      }
      setSession(newSession);
      if (newSession?.user?.id) {
        setBatchScanOpen(false);
        if (typeof window !== "undefined") {
          const path = window.location.pathname;
          const hash = window.location.hash;
          const isHashAuth = hash.includes("access_token") || hash.includes("type=signup");
          if (path === "/upload" || path === "/signup" || (path === "/" && isHashAuth)) {
            if (path === "/" && isHashAuth) setShowWelcomeBanner(true);
            router.replace("/dashboard");
          }
        }
      } else {
        setMeds(null);
        setBatchScanOpen(false);
        setInventoryLoaded(false);
        setInventoryRefreshing(false);
        setPasswordRecovery(false);
      }
    });

    void (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(data.session);
        if (recoveryFromUrl && data.session?.user?.id) {
          setPasswordRecovery(true);
          setMeds(null);
        } else if (!data.session?.user?.id) {
          setMeds(null);
        }
      } catch {
        if (mounted) {
          setSession(null);
          setMeds(null);
        }
      }
    })();

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [supabaseReady, router]);

  /** Refresh session when the user returns to the tab (avoids unnecessary daily re-login when JWT idles out). */
  useEffect(() => {
    if (!supabaseReady || !supabase || typeof window === "undefined") return;

    async function reviveSession() {
      if (document.visibilityState !== "visible") return;
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (!s?.expires_at) return;
        const expiresMs = s.expires_at * 1000;
        if (Date.now() > expiresMs - 120_000) await supabase.auth.refreshSession();
      } catch {
        /* ignore */
      }
    }

    window.addEventListener("focus", reviveSession);
    document.addEventListener("visibilitychange", reviveSession);
    return () => {
      window.removeEventListener("focus", reviveSession);
      document.removeEventListener("visibilitychange", reviveSession);
    };
  }, [supabaseReady]);

  const reloadInventory = useCallback(async (options = {}) => {
    if (!supabaseReady || !supabase || !session?.user?.id || passwordRecovery) return;
    
    // Get current inventoryLoaded state to avoid circular dependency
    const currentInventoryLoaded = inventoryLoadedRef.current;
    const backgroundRefresh = options.background === true && currentInventoryLoaded;
    const gen = ++inventoryReloadGenRef.current;
    
    if (backgroundRefresh) {
      setInventoryRefreshing(true);
    } else {
      setInventoryLoaded(false);
      setInventoryRefreshing(false);
    }
    
    const uid = session.user.id;
    const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const medSelect =
      "id, name, dosage, quantity, unit, expiry_month, expiry_year, price_per_box, status, scan_date, deleted_at, product_category, expiry_day_convention, ai_confidence_score, image_fingerprint, supplier_name";
    const PAGE = 1000;

    try {
      const allActive = [];
      let activeFetchError = null;
      for (let from = 0; ; from += PAGE) {
        if (gen !== inventoryReloadGenRef.current) return;
        const { data, error } = await supabase
          .from("medications")
          .select(medSelect)
          .eq("user_id", uid)
          .is("deleted_at", null)
          .order("expiry_year", { ascending: true })
          .order("expiry_month", { ascending: true })
          .order("name", { ascending: true })
          .range(from, from + PAGE - 1);
        if (error) {
          activeFetchError = error;
          break;
        }
        const chunk = data || [];
        allActive.push(...chunk);
        if (chunk.length < PAGE) break;
      }

      if (gen !== inventoryReloadGenRef.current) return;

      const delRes = await supabase
        .from("medications")
        .select(medSelect)
        .eq("user_id", uid)
        .not("deleted_at", "is", null)
        .gte("deleted_at", cutoff)
        .order("deleted_at", { ascending: false })
        .limit(100);

      if (gen !== inventoryReloadGenRef.current) return;

      if (activeFetchError) {
        logger.error("inventory.active_meds.failed", activeFetchError);
        const msg =
          activeFetchError && typeof activeFetchError === "object" && "message" in activeFetchError
            ? String(activeFetchError.message)
            : "Could not load inventory.";
        setInventoryLoadError(humanizeSupabaseSaveError(msg));
        setMeds((prev) => (Array.isArray(prev) ? prev : []));
      } else {
        setInventoryLoadError("");
        const rows = allActive;
        setMeds(rows.map(mapMedicationRow));
        // Do not derive trial/sheet caps from inventory rows — each batch line has a unique scan_date ISO
        // and would falsely exhaust the trial after a few label photos.
      }

      if (gen !== inventoryReloadGenRef.current) return;

      if (!delRes.error) {
        setDeletedMeds((delRes.data || []).map(mapMedicationRow));
      } else {
        logger.error("inventory.deleted_meds.failed", delRes.error);
        setDeletedMeds((prev) => (Array.isArray(prev) ? prev : []));
      }
    } catch (error) {
      logger.error("inventory.reload.failed", error);
      if (gen === inventoryReloadGenRef.current) {
        setInventoryLoadError(humanizeNetworkOrUnknown(error));
        setMeds((prev) => (Array.isArray(prev) ? prev : []));
      }
    } finally {
      if (gen === inventoryReloadGenRef.current) {
        setInventoryLoaded(true);
        setInventoryRefreshing(false);
      }
    }
  }, [supabaseReady, supabase, session?.user?.id, passwordRecovery]);

  /** Live inventory when another tab/device updates `medications` (requires Supabase Realtime on that table). */
  useEffect(() => {
    if (!supabaseReady || !supabase || !session?.user?.id || passwordRecovery) return;
    const uid = session.user.id;
    const debounceMs = 420;
    const channel = supabase
      .channel(`app-medications-${uid}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "medications", filter: `user_id=eq.${uid}` },
        () => {
          if (inventoryRealtimeDebounceRef.current) {
            clearTimeout(inventoryRealtimeDebounceRef.current);
          }
          inventoryRealtimeDebounceRef.current = setTimeout(() => {
            inventoryRealtimeDebounceRef.current = null;
            void reloadInventory({ background: true });
          }, debounceMs);
        },
      )
      .subscribe();
    return () => {
      if (inventoryRealtimeDebounceRef.current) {
        clearTimeout(inventoryRealtimeDebounceRef.current);
        inventoryRealtimeDebounceRef.current = null;
      }
      void supabase.removeChannel(channel);
    };
  }, [supabaseReady, session?.user?.id, passwordRecovery, reloadInventory]);

  useEffect(() => {
    if (!supabaseReady) return;

    if (!session?.user?.id) {
      setInventoryLoaded(false);
      setInventoryRefreshing(false);
      setInventoryLoadError("");
      setDeletedMeds([]);
      return;
    }

    if (passwordRecovery) {
      setInventoryLoaded(true);
      return;
    }

    let cancelled = false;
    let timeoutId;
    
    void (async () => {
      try {
        // Add timeout to prevent infinite loading
        timeoutId = setTimeout(() => {
          if (!cancelled) {
            logger.warn('inventory.loading.timeout', { timeout: 10000 });
            setInventoryLoaded(true);
            setInventoryRefreshing(false);
          }
        }, 10000); // 10 second timeout
        
        await reloadInventory();
        if (cancelled) return;
        
        // Clear timeout on successful load
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      } catch (error) {
        logger.error('inventory.loading.failed', error);
        if (!cancelled) {
          setInventoryLoaded(true); // Allow UI to render even on error
          setInventoryRefreshing(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [supabaseReady, session?.user?.id, passwordRecovery, reloadInventory]);

  useEffect(() => {
    if (!supabaseReady || !supabase || !session?.user?.id || passwordRecovery) return;
    if (landingConversionSentRef.current) return;
    const v = typeof document !== "undefined" ? getPricingVariantFromDocumentCookie() : null;
    if (!v) return;
    void (async () => {
      const { error } = await supabase.from("landing_conversions").insert({
        user_id: session.user.id,
        variant: v,
      });
      if (!error || error.code === "23505") {
        landingConversionSentRef.current = true;
      }
    })();
  }, [supabaseReady, session?.user?.id, passwordRecovery]);

  useEffect(() => {
    async function loadBillingStatus() {
      if (!supabaseReady || !session?.access_token) {
        setPaidPlan("free");
        setBillingStatus(null);
        return;
      }
      try {
        const response = await fetch("/api/billing/status", {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        const data = await response.json();
        if (!response.ok) {
          setPaidPlan("free");
          setBillingStatus(null);
          return;
        }
        const raw = String(data?.plan || "free").toLowerCase();
        setPaidPlan(raw === "premium" ? "premium" : raw === "pro" ? "pro" : "free");
        setBillingStatus(data || null);
      } catch {
        setPaidPlan("free");
        setBillingStatus(null);
      }
    }
    loadBillingStatus();
  }, [supabaseReady, session?.access_token]);

  useEffect(() => {
    if (!supabaseReady || !supabase || !session?.user?.id || passwordRecovery) return;
    const u = session.user;
    if (!u.email_confirmed_at || u.user_metadata?.welcome_email_sent) return;

    let cancelled = false;
    (async () => {
      const { data: s } = await supabase.auth.getSession();
      const token = s.session?.access_token;
      if (!token || cancelled) return;
      try {
        const res = await fetch("/api/onboarding/send-welcome", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) await supabase.auth.refreshSession();
      } catch {
        /* non-blocking */
      }
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- narrow session fields only; full session.user is unstable
  }, [supabaseReady, session?.user?.id, session?.user?.email_confirmed_at, session?.user?.user_metadata?.welcome_email_sent, passwordRecovery]);

  /**
   * Email/password signups set `terms_accepted_at` in `signUp` options. OAuth users agree via the signup banner —
   * backfill the same metadata on first session so audits and downstream jobs stay consistent.
   */
  useEffect(() => {
    if (!supabaseReady || !supabase || !session?.user?.id || passwordRecovery) return;
    const u = session.user;
    if (u.user_metadata?.terms_accepted_at) return;

    const identities = u.identities ?? [];
    const oauthIdentity = identities.find(
      (i) => i.provider === "google" || i.provider === "apple",
    );
    if (!oauthIdentity) return;

    if (oauthTermsSyncedRef.current === u.id) return;

    const provider = oauthIdentity.provider === "apple" ? "apple" : "google";
    let cancelled = false;

    void (async () => {
      try {
        const { error } = await supabase.auth.updateUser({
          data: {
            terms_accepted_at: new Date().toISOString(),
            terms_accepted_via: "oauth_signup",
          },
        });
        if (cancelled || error) return;
        oauthTermsSyncedRef.current = u.id;
        trackOAuthTermsRecorded(provider);
        await supabase.auth.refreshSession();
      } catch {
        /* non-blocking */
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- backfill when user id / identities / terms flag change; `session.user` identity is unstable across refreshes
  }, [
    supabaseReady,
    session?.user?.id,
    session?.user?.identities,
    session?.user?.user_metadata?.terms_accepted_at,
    passwordRecovery,
  ]);

  /** Set `marketing_cadence` from subscription + `newsletter_opt_in` (covers email-confirm signups with no session at registration). Re-runs when `paidPlan` updates after billing loads. */
  useEffect(() => {
    if (!supabaseReady || !session?.access_token || !session?.user?.id || passwordRecovery) {
      if (!session?.user?.id) marketingSyncRef.current = { userId: null, plan: null };
      return;
    }
    const uid = session.user.id;
    const prev = marketingSyncRef.current;
    if (prev.userId === uid && prev.plan === paidPlan) return;
    marketingSyncRef.current = { userId: uid, plan: paidPlan };
    void (async () => {
      try {
        await fetch("/api/marketing/register-opt-in", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: "{}",
        });
      } catch {
        /* non-blocking */
      }
    })();
  }, [supabaseReady, session?.user?.id, session?.access_token, passwordRecovery, paidPlan]);

  /** @returns {Promise<boolean>} whether inventory was saved (batch UI can stay open on false) */
  async function handleParsed(parsedMeds) {
    setSheetCommitError("");
    if (session?.user?.id && Array.isArray(parsedMeds) && parsedMeds.length > 0) {
      void mergePosReferenceFromParsed(session.user.id, parsedMeds);
    }
    if (!supabaseReady || !session?.user?.id) {
      withViewTransition(() => {
        const annotated = parsedMeds.map((m) => ({
          ...m,
          incompleteCoreFields: computeIncompleteCoreFields(m),
        }));
        setMeds(mergeDuplicates(annotated));
        setBatchScanOpen(false);
        router.replace("/dashboard");
      });
      return true;
    }

    const scanDate = new Date().toISOString();
    const rows = parsedMeds.map((m) => {
      const t = sanitizeMedForSave({
        ...m,
        status: null,
      });
      const inc = computeIncompleteCoreFields(m);
      return {
        user_id: session.user.id,
        name: t.name,
        dosage: t.dosage || "",
        quantity: m.quantity,
        unit: t.unit,
        expiry_month: m.expiryMonth,
        expiry_year: m.expiryYear,
        price_per_box: null,
        status: null,
        product_category:
          m.productCategory != null && String(m.productCategory).trim()
            ? String(m.productCategory).trim()
            : null,
        supplier_name: t.supplierName ? String(t.supplierName).trim().slice(0, 120) : null,
        scan_date: scanDate,
        expiry_day_convention: m.expiryDayConvention || "last",
        image_fingerprint: m.imageFingerprint ?? null,
        ai_confidence_score: m.aiConfidence ?? null,
        ai_raw_payload: m.aiRawPayload ?? null,
      };
    });

    try {
      const { data, error } = await insertMedicationRowsWithSchemaFallback(supabase, rows);
      if (error || !data) {
        setSheetCommitError("We couldn't save that sheet. Please try again.");
        return false;
      }

      const inserted = (data || []).map((row, i) => {
        const mapped = mapMedicationRow(row);
        const meta = parsedMeds[i] || {};
        return {
          ...mapped,
          originalName: meta.originalName || null,
          suggestedName: meta.suggestedName || null,
          matchScore: typeof meta.matchScore === "number" ? meta.matchScore : null,
          matchConfidence: meta.matchConfidence || null,
          matchStatus: meta.matchStatus || "not_checked",
        };
      });

      withViewTransition(() => {
        setMeds((prev) => [...(prev || []), ...inserted]);
        setBatchScanOpen(false);
        router.replace("/dashboard");
      });
      void insertAuditLog(supabase, session.user.id, {
        action: "scan_completed",
        entityType: "scan",
        entityId: null,
        meta: { linesAdded: inserted.length, source: "sheet" },
      });
      return true;
    } catch (e) {
      setSheetCommitError(humanizeNetworkOrUnknown(e));
      return false;
    }
  }

  /**
   * Batch scan persists each confirmed label immediately (one Supabase insert per item).
   * That streams work, keeps the UI responsive, and avoids losing hundreds of lines if something fails mid-run.
   * AI parse is also one `/api/parse-batch-scan` call per capture (see `BatchScanView`).
   */
  async function handleBatchScanSaveItem(item) {
    if (!item) return false;
    const itemClean = sanitizeMedForSave(item);
    const scanIso = new Date().toISOString();
    const inc = computeIncompleteCoreFields(item);
    const row = {
      user_id: session?.user?.id ?? "",
      name: itemClean.name,
      dosage: itemClean.dosage || "",
      quantity: item.quantity,
      unit: itemClean.unit,
      expiry_month: item.expiryMonth,
      expiry_year: item.expiryYear,
      price_per_box: item.pricePerBox ?? null,
      status: itemClean.status,
      product_category: itemClean.productCategory,
      supplier_name: itemClean.supplierName ? String(itemClean.supplierName).trim().slice(0, 120) : null,
      scan_date: scanIso,
      expiry_day_convention: item.expiryDayConvention || "last",
    };

    if (!supabaseReady || !session?.user?.id) {
      const local = {
        id: `local-${Date.now()}`,
        name: row.name,
        dosage: row.dosage,
        quantity: row.quantity,
        unit: row.unit,
        expiryMonth: item.expiryMonth,
        expiryYear: item.expiryYear,
        pricePerBox: item.pricePerBox ?? null,
        productCategory: itemClean.productCategory,
        supplierName: itemClean.supplierName || "",
        status: row.status,
        originalName: item.originalName || null,
        suggestedName: item.suggestedName || null,
        matchScore: typeof item.matchScore === "number" ? item.matchScore : null,
        matchConfidence: item.matchConfidence || null,
        matchStatus: item.matchStatus || "not_checked",
        scanDate: scanIso,
        deletedAt: null,
        incompleteCoreFields: inc,
      };
      setMeds((prev) => [...(prev || []), local]);
      track('medication.added', { 
        method: 'batch_scan',
        hasExpiry: true,
        daysUntilExpiry: row.expiry_year && row.expiry_month 
          ? Math.floor((new Date(row.expiry_year, row.expiry_month, 1).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
          : null
      });
      return local;
    }

    row.user_id = session.user.id;
    const insertRes = await insertMedicationRowsWithSchemaFallback(supabase, [row]);
    const data = insertRes.data?.[0];
    const error = insertRes.error;
    if (error || !data) {
      const clientMedId =
        item?.id != null && String(item.id) !== "" ? `bs-${item.id}` : `pending-${Date.now()}`;
      const enq = enqueueBatchScanPending(row, clientMedId);
      if (!enq.ok) {
        const msg =
          enq.reason === "quota"
            ? "Could not queue this line offline — browser storage is full. Free some space, then try again when you have a stable connection."
            : `Too many lines are waiting to sync (${MAX_BATCH_SCAN_PENDING_QUEUE} max). Try again after earlier lines finish syncing.`;
        setBatchQueueNotice(msg);
        throw new Error(msg);
      }
      setBatchQueueNotice("");
      const optimistic = {
        id: clientMedId,
        name: row.name,
        dosage: row.dosage,
        quantity: row.quantity,
        unit: row.unit,
        expiryMonth: item.expiryMonth,
        expiryYear: item.expiryYear,
        pricePerBox: item.pricePerBox ?? null,
        productCategory: itemClean.productCategory,
        supplierName: itemClean.supplierName || "",
        status: row.status,
        originalName: item.originalName || null,
        suggestedName: item.suggestedName || null,
        matchScore: typeof item.matchScore === "number" ? item.matchScore : null,
        matchConfidence: item.matchConfidence || null,
        matchStatus: item.matchStatus || "not_checked",
        scanDate: scanIso,
        deletedAt: null,
        incompleteCoreFields: inc,
      };
      setMeds((prev) => [...(prev || []), optimistic]);
      return { ...optimistic, _batchSaveDeferred: true };
    }
    const mapped = mapMedicationRow(data);
    const saved = {
      ...mapped,
      originalName: item.originalName || null,
      suggestedName: item.suggestedName || null,
      matchScore: typeof item.matchScore === "number" ? item.matchScore : null,
      matchConfidence: item.matchConfidence || null,
      matchStatus: item.matchStatus || "not_checked",
    };
    setMeds((prev) => [...(prev || []), saved]);
    return saved;
  }

  async function handleBatchScanUndoItem(itemId) {
    if (itemId == null) return false;
    if (!supabaseReady || !session?.user?.id) {
      setMeds((prev) => (prev || []).filter((m) => m.id !== itemId));
      return true;
    }
    const ts = new Date().toISOString();
    const removedRow = (meds || []).find((m) => m.id === itemId);
    const { error } = await supabase
      .from("medications")
      .update({ deleted_at: ts })
      .eq("id", itemId)
      .eq("user_id", session.user.id);
    if (error) return false;
    setMeds((prev) => (prev || []).filter((m) => m.id !== itemId));
    if (removedRow) setDeletedMeds((d) => [{ ...removedRow, deletedAt: ts }, ...d]);
    void insertAuditLog(supabase, session.user.id, { action: "item_deleted", entityId: String(itemId), meta: { source: "batch_undo" } });
    return true;
  }

  async function handleLogout() {
    if (supabaseReady && supabase) {
      try {
        await supabase.auth.signOut();
      } catch {
        /* still send user to login */
      }
    }
    clearSupabaseAuthStorage();
    if (typeof window !== "undefined") window.location.assign("/");
  }

  async function handleClearAllInventory() {
    if (!window.confirm("Clear your entire list? Lines move to Recently deleted (recoverable for 30 days).")) return;
    if (!supabaseReady || !session?.user?.id) {
      setMeds([]);
      setBatchScanOpen(false);
      router.push("/upload");
      return;
    }
    const ts = new Date().toISOString();
    await supabase.from("medications").update({ deleted_at: ts }).eq("user_id", session.user.id).is("deleted_at", null);
    void insertAuditLog(supabase, session.user.id, { action: "inventory_cleared", entityType: "inventory", entityId: null });
    await reloadInventory();
    setBatchScanOpen(false);
    router.push("/upload");
  }

  const vw = viewportLayout.width;
  const vh = viewportLayout.height;
  /** Width-based breakpoints only — using min(vw,vh) wrongly treated "wide but short" laptop windows as phones. */
  const isMobile = vw < 768;
  /** Phone-style landscape: only when already narrow, not desktop with limited vertical space. */
  const isLandscapeCompact = isMobile && vw >= vh && vh < 520;

  return (
    <AppErrorBoundary
      onError={(error, errorInfo) => {
        logger.error('app.error_boundary.triggered', error, { 
          component: 'ExpiryDeskApp',
          componentStack: errorInfo.componentStack 
        });
        // Force inventory loaded state to prevent infinite loading on errors
        setInventoryLoaded(true);
        setInventoryRefreshing(false);
      }}
      fallback={
        <div style={{ 
          minHeight: "100vh", 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center", 
          justifyContent: "center", 
          gap: "1rem",
          fontFamily: ED.font,
          color: ED.textSecondary,
          padding: "1rem"
        }}>
          <div style={{ textAlign: "center", maxWidth: "400px" }}>
            <h3 style={{ color: ED.text, marginBottom: "1rem" }}>Something went wrong</h3>
            <p style={{ marginBottom: "1.5rem" }}>
              The app encountered an error. Please refresh the page to try again.
            </p>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: "0.75rem 1.5rem",
                background: "#0ea5e9",
                color: "white",
                border: "none",
                borderRadius: ED.r12,
                cursor: "pointer",
                fontFamily: ED.font
              }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      }
    >
      <div className="ed-desk-shell" style={{ minHeight: "100vh", color: ED.text, fontFamily: ED.font }}>
      {billingNotice && (
        <div className="ed-shell-billing-banner" style={{ paddingTop: "0.85rem", paddingBottom: 0 }}>
          <div className="ed-card-glass" style={{ borderRadius: ED.r12, padding: "0.85rem 1rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.85rem", background: "rgba(239, 246, 255, 0.92)", border: "1px solid rgba(59, 130, 246, 0.28)" }}>
            <span style={{ fontSize: "0.9rem", color: "#1d4ed8", fontWeight: 500 }}>{billingNotice}</span>
            <button type="button" onClick={() => setBillingNotice("")} className="ed-btn-ghost" style={{ padding: "0.4rem 0.75rem", fontFamily: ED.font, fontSize: "0.8125rem", fontWeight: 600, flexShrink: 0 }}>
              Dismiss
            </button>
          </div>
        </div>
      )}
      {batchPendingSyncCount > 0 && (
        <div className="ed-shell-billing-banner" style={{ paddingTop: "0.5rem", paddingBottom: 0 }}>
          <div
            className="ed-card-glass"
            style={{
              borderRadius: ED.r12,
              padding: "0.75rem 1rem",
              background: "rgba(254, 243, 199, 0.92)",
              border: "1px solid rgba(217, 119, 6, 0.35)",
            }}
            role="status"
          >
            <span style={{ fontSize: "0.9rem", color: "#92400e", fontWeight: 500, lineHeight: 1.45 }}>
              Still syncing {batchPendingSyncCount === 1 ? "1 line" : `${batchPendingSyncCount} lines`} to your list. We retry automatically when you&apos;re online or when you return to this tab.
            </span>
          </div>
        </div>
      )}
      {batchQueueNotice && (
        <div className="ed-shell-billing-banner" style={{ paddingTop: "0.5rem", paddingBottom: 0 }}>
          <div
            className="ed-card-glass"
            style={{
              borderRadius: ED.r12,
              padding: "0.85rem 1rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "0.85rem",
              background: isDarkMode ? "rgba(255, 77, 77, 0.1)" : "rgba(254, 242, 242, 0.95)",
              border: isDarkMode ? "1px solid rgba(255, 77, 77, 0.28)" : "1px solid rgba(220, 38, 38, 0.28)",
            }}
            role="alert"
          >
            <span style={{ fontSize: "0.9rem", color: isDarkMode ? "var(--ed-dark-critical)" : "#991b1b", fontWeight: 500, lineHeight: 1.45 }}>{batchQueueNotice}</span>
            <button
              type="button"
              onClick={() => setBatchQueueNotice("")}
              className="ed-btn-ghost"
              style={{ padding: "0.4rem 0.75rem", fontFamily: ED.font, fontSize: "0.8125rem", fontWeight: 600, flexShrink: 0 }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      {passwordRecovery && session && supabaseReady ? (
        <PasswordRecoveryView
          onCompleted={() => {
            setMeds([]);
            setPasswordRecovery(false);
          }}
        />
      ) : !session ? (
        <LoginView linkError={authLinkError} onDismissLinkError={() => setAuthLinkError("")} />
      ) : isDeskAuxRoute ? (
        <div id="main" tabIndex={-1} style={{ minHeight: "100vh", width: "100%" }}>
          {children}
        </div>
      ) : isUploadRoute ? (
        <div id="main" tabIndex={-1} style={{ minHeight: "100vh", width: "100%" }}>
          <Suspense fallback={null}>
            <UploadBatchScanParamsBridge setBatchScanOpen={setBatchScanOpen} router={router} />
          </Suspense>
          {batchScanOpen ? (
          !batchRecoveryChecked ? (
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 55,
                background: "rgba(248, 250, 252, 0.92)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: ED.font,
              }}
            >
              <HourglassLoader size={36} label="Opening batch scan" />
            </div>
          ) : batchRecoveryGate && !batchRecoveryPayload ? (
            <div
              className="batch-scan-ui"
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 60,
                background: "rgba(6, 10, 18, 0.94)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "max(1rem, env(safe-area-inset-top)) max(1rem, env(safe-area-inset-right)) max(1rem, env(safe-area-inset-bottom)) max(1rem, env(safe-area-inset-left))",
                fontFamily: ED.font,
              }}
            >
              <div
                className="ed-card-glass"
                style={{
                  maxWidth: 420,
                  width: "100%",
                  padding: "1.5rem 1.35rem",
                  borderRadius: ED.r16,
                  border: "1px solid rgba(14, 165, 233, 0.2)",
                  boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
                }}
              >
                <h2 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 800, letterSpacing: "-0.02em", color: ED.text, lineHeight: 1.2 }}>
                  Resume batch scan?
                </h2>
                <p style={{ margin: "0.75rem 0 0", color: ED.textSecondary, fontSize: "0.9375rem", lineHeight: 1.55 }}>
                  We found a saved session on this device
                  {batchRecoveryGate.confirmedCount > 0 ? (
                    <>
                      : <strong style={{ color: ED.text }}>{batchRecoveryGate.confirmedCount}</strong> line(s) already confirmed
                    </>
                  ) : null}
                  {batchScanRecoveryHasResumableDraft(batchRecoveryGate) ? (
                    <>
                      {(batchRecoveryGate.confirmedCount > 0 || (batchRecoveryGate.sessionLog?.length ?? 0) > 0)
                        ? " and "
                        : ": "}
                      <strong style={{ color: ED.text }}>an unfinished scan</strong> still in progress
                    </>
                  ) : null}
                  {(batchRecoveryGate.outboundPending?.length ?? 0) > 0 ? (
                    <>
                      {(batchRecoveryGate.confirmedCount > 0 ||
                        batchScanRecoveryHasResumableDraft(batchRecoveryGate) ||
                        (batchRecoveryGate.sessionLog?.length ?? 0) > 0) &&
                        " "}
                      <strong style={{ color: ED.text }}>{batchRecoveryGate.outboundPending.length}</strong> line
                      {batchRecoveryGate.outboundPending.length === 1 ? "" : "s"} may still be syncing.
                    </>
                  ) : null}
                  . Confirmed lines are saved as you go (or queued offline); you can continue or discard this draft.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem", marginTop: "1.35rem" }}>
                  <button
                    type="button"
                    className="ed-btn-primary"
                    style={{ padding: "0.75rem 1rem", fontFamily: ED.font, fontWeight: 700, fontSize: "0.9375rem" }}
                    onClick={() => {
                      setBatchRecoveryPayload(batchRecoveryGate);
                      setBatchRecoveryGate(null);
                    }}
                  >
                    Resume where I left off
                  </button>
                  <button
                    type="button"
                    className="ed-btn-secondary"
                    style={{ padding: "0.65rem 1rem", fontFamily: ED.font, fontWeight: 600, fontSize: "0.875rem" }}
                    onClick={() => {
                      if (session?.user?.id) void clearBatchScanRecovery(session.user.id);
                      setBatchRecoveryGate(null);
                    }}
                  >
                    Start a fresh batch
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <BatchScanView
              userId={session?.user?.id ?? null}
              recoveryInitial={batchRecoveryPayload}
              onRecoveryConsumed={onBatchRecoveryConsumed}
              onExitRiskChange={onBatchExitRiskChange}
              onSaveItem={(item) => handleBatchScanSaveItem(item)}
              onUndoItem={(id) => handleBatchScanUndoItem(id)}
              onDone={() => setBatchScanOpen(false)}
              onCancel={() => setBatchScanOpen(false)}
              parseAccessToken={session?.access_token ?? null}
            />
          )
        ) : (
          <UploadView
            commitError={sheetCommitError}
            onClearCommitError={() => setSheetCommitError("")}
            onParsed={(m) => handleParsed(m)}
            onUseDemoData={() => {
              withViewTransition(() => {
                setMeds(mergeDuplicates(DEMO_MEDS));
                setBatchScanOpen(false);
                router.replace("/dashboard");
              });
            }}
            isMobile={isMobile}
            onCancel={() => {
              withViewTransition(() => {
                setBatchScanOpen(false);
                router.push("/dashboard");
              });
            }}
            parseAccessToken={session?.access_token ?? null}
          />
          )}
        </div>
      ) : (
        <>
          {inventoryLoadError ? (
            <div className="ed-shell-billing-banner" style={{ paddingTop: "0.5rem", paddingBottom: 0 }}>
              <div
                className="ed-card-glass"
                style={{
                  borderRadius: ED.r12,
                  padding: "0.85rem 1rem",
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "0.75rem",
                  background: "rgba(254, 242, 242, 0.95)",
                  border: "1px solid rgba(220, 38, 38, 0.28)",
                }}
                role="alert"
              >
                <span style={{ fontSize: "0.9rem", color: "#991b1b", fontWeight: 600, lineHeight: 1.45, flex: "1 1 12rem" }}>
                  {inventoryLoadError}
                </span>
                <div style={{ display: "flex", gap: "0.45rem", flexShrink: 0 }}>
                  <button
                    type="button"
                    className="ed-btn-primary"
                    style={{ padding: "0.45rem 0.85rem", fontFamily: ED.font, fontSize: "0.8125rem", fontWeight: 700 }}
                    onClick={() => {
                      setInventoryLoadError("");
                      void reloadInventory();
                    }}
                  >
                    Retry
                  </button>
                  <button
                    type="button"
                    className="ed-btn-ghost"
                    style={{ padding: "0.45rem 0.75rem", fontFamily: ED.font, fontSize: "0.8125rem", fontWeight: 600 }}
                    onClick={() => setInventoryLoadError("")}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          {(pathname === "/dashboard" || isNotificationsRoute) && (
            <div style={isNotificationsRoute ? { display: "none" } : undefined} aria-hidden={isNotificationsRoute ? true : undefined}>
              <Dashboard
                ref={dashboardHandleRef}
                meds={meds || []}
                setMeds={setMeds}
                deletedMeds={deletedMeds}
                setDeletedMeds={setDeletedMeds}
                onAddSheet={() => {
                  setBatchScanOpen(false);
                  router.push("/upload");
                }}
                onAddBatchScan={() => {
                  setBatchScanOpen(true);
                  router.push("/upload?batchScan=1");
                }}
                onClearAllInventory={() => void handleClearAllInventory()}
                isMobile={isMobile}
                isLandscapeCompact={isLandscapeCompact}
                supabaseReady={supabaseReady}
                supabaseClient={supabase}
                userId={session?.user?.id || null}
                userEmail={session?.user?.email || ""}
                userMetadata={session?.user?.user_metadata || null}
                onLogout={handleLogout}
                paidPlan={paidPlan}
                billingStatus={billingStatus}
                userCreatedAt={session?.user?.created_at ?? null}
                parseAccessToken={session?.access_token ?? null}
                inventoryLoaded={inventoryLoaded}
                inventoryRefreshing={inventoryRefreshing}
              />
            </div>
          )}
          {isNotificationsRoute ? <DeskNotificationsPage meds={meds || []} dashboardRef={dashboardHandleRef} /> : null}
        </>
      )}
      </div>
    </AppErrorBoundary>
  );
}
